import React, {
  useState, useEffect, useContext, createContext, useRef, useMemo, useCallback,
} from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates, arrayMove,
} from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import DOMPurify from 'dompurify';
import {
  Home, Briefcase, Calendar, Users, Settings, LogOut, Menu, X, Plus, Edit2,
  Trash2, ChevronLeft, ChevronRight, Search, Phone, Mail,
  MapPin, Palette, Save, Building, User, CheckCircle, Circle, Clock,
  ChevronDown, ChevronUp, Grid3X3, List, AlignLeft, Check, Wrench, FileText,
  Lock, ArrowRight, StickyNote, Share2, Eye, EyeOff, Hash, Shield, UserPlus,
  Moon, Sun, Printer, Download, Copy, CalendarDays, Star,
  HelpCircle, ThumbsUp, ThumbsDown, RotateCcw, AlertTriangle,
  MessageCircle, Undo2, Paperclip, UserSquare, Bell, UserCheck, UserX, Camera, Gift, Key,
  LayoutGrid, ExternalLink, Link, Archive, RotateCw, GripVertical, Image as ImageIcon, Folder,
  CheckSquare, Square,
} from 'lucide-react';
import Cropper from 'react-easy-crop';
import './App.css';
import './dashboard.css';
import { DashboardPreview } from './components/DashboardPreview';
import { MobileWorkspaceBrand } from './components/MobileWorkspaceBrand';
import { GuestSessionBanner } from './components/GuestSessionBanner';
import { ProjectScheduleDates } from './components/ProjectScheduleDates';
import { useClientDirectory } from './hooks/useClientDirectory';
import { LinkPickerField } from './components/LinkPickerField';
import { ClientShareDialog, PublicClientPage } from './components/ClientSharing';
import { getCroppedImageBlob } from './components/cropImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// CLICK-OUTSIDE HOOK — closes any dropdown/popup when user
// clicks outside the ref'd container
// ============================================================
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

// ============================================================
// LOCALSTORAGE-BACKED STATE — persists per-page UX preferences
// (search filters, view mode, active template) across nav
// ============================================================
function usePersistentState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return initial;
      return JSON.parse(raw);
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
  }, [key, value]);
  return [value, setValue];
}

// ============================================================
// THEMES
// ============================================================
const THEMES = [
  {
    id: 'cobalt', name: 'Cobalt', preview: '#1B4FBF',
    primary: '218 79% 40%', primaryFg: '0 0% 100%', accent: '218 55% 88%', radius: '0.5rem',
    background: '218 20% 95%', foreground: '222 47% 11%', card: '218 15% 98%',
    muted: '218 12% 90%', mutedFg: '218 12% 38%', border: '218 10% 85%',
    sidebar: '218 79% 25%', sidebarFg: '0 0% 98%',
    darkBg: '222 30% 9%', darkFg: '210 15% 92%', darkCard: '222 25% 13%',
    darkMuted: '222 20% 18%', darkMutedFg: '210 12% 74%', darkBorder: '222 15% 26%',
  },
  {
    id: 'teal', name: 'Classic Teal', preview: '#1F2937',
    primary: '215 28% 17%', primaryFg: '0 0% 100%', accent: '215 20% 85%', radius: '0.75rem',
    background: '45 18% 93%', foreground: '217 19% 27%', card: '45 12% 96%',
    muted: '44 10% 89%', mutedFg: '218 11% 42%', border: '44 6% 83%',
    sidebar: '215 28% 17%', sidebarFg: '0 0% 95%',
    darkBg: '215 28% 8%', darkFg: '45 10% 92%', darkCard: '215 24% 12%',
    darkMuted: '215 20% 16%', darkMutedFg: '45 12% 74%', darkBorder: '215 16% 24%',
  },
  {
    id: 'emerald', name: 'Classic Emerald', preview: '#10B981',
    primary: '160 84% 39%', primaryFg: '0 0% 100%', accent: '152 60% 92%', radius: '0.5rem',
    background: '0 0% 100%', foreground: '222 47% 11%', card: '0 0% 100%',
    muted: '160 14% 96%', mutedFg: '160 8% 42%', border: '160 13% 90%',
    sidebar: '160 30% 97%', sidebarFg: '160 20% 25%',
    darkBg: '160 20% 8%', darkFg: '152 10% 92%', darkCard: '160 18% 12%',
    darkMuted: '160 15% 17%', darkMutedFg: '160 10% 74%', darkBorder: '160 12% 26%',
  },
  {
    id: 'blue', name: 'Corporate Blue', preview: '#3B82F6',
    primary: '221 83% 53%', primaryFg: '0 0% 100%', accent: '221 60% 92%', radius: '0.3rem',
    background: '220 20% 99%', foreground: '222 47% 11%', card: '0 0% 100%',
    muted: '221 14% 96%', mutedFg: '221 8% 46%', border: '221 13% 91%',
    sidebar: '221 30% 97%', sidebarFg: '221 20% 25%',
    darkBg: '222 25% 8%', darkFg: '220 12% 92%', darkCard: '222 22% 12%',
    darkMuted: '222 18% 17%', darkMutedFg: '220 10% 74%', darkBorder: '222 14% 26%',
  },
  {
    id: 'orange', name: 'Industrial Orange', preview: '#F97316',
    primary: '24 95% 53%', primaryFg: '0 0% 100%', accent: '24 80% 92%', radius: '0rem',
    background: '30 20% 99%', foreground: '20 14% 10%', card: '30 20% 100%',
    muted: '24 14% 96%', mutedFg: '24 8% 42%', border: '24 13% 90%',
    sidebar: '24 25% 97%', sidebarFg: '24 20% 22%',
    darkBg: '20 20% 8%', darkFg: '30 12% 92%', darkCard: '20 18% 12%',
    darkMuted: '20 15% 17%', darkMutedFg: '24 10% 74%', darkBorder: '20 12% 26%',
  },
  {
    id: 'violet', name: 'Deep Violet', preview: '#8B5CF6',
    primary: '262 83% 58%', primaryFg: '0 0% 100%', accent: '262 60% 93%', radius: '0.5rem',
    background: '270 20% 99%', foreground: '260 20% 12%', card: '270 15% 100%',
    muted: '262 14% 96%', mutedFg: '262 8% 46%', border: '262 13% 91%',
    sidebar: '262 25% 97%', sidebarFg: '262 20% 25%',
    darkBg: '262 25% 8%', darkFg: '270 12% 92%', darkCard: '262 22% 12%',
    darkMuted: '262 18% 17%', darkMutedFg: '262 10% 74%', darkBorder: '262 14% 26%',
  },
  {
    id: 'rose', name: 'Crimson Rose', preview: '#E11D48',
    primary: '347 77% 50%', primaryFg: '0 0% 100%', accent: '347 60% 93%', radius: '0.75rem',
    background: '350 20% 99%', foreground: '345 15% 11%', card: '350 15% 100%',
    muted: '347 14% 96%', mutedFg: '347 8% 46%', border: '347 13% 91%',
    sidebar: '347 25% 97%', sidebarFg: '347 18% 25%',
    darkBg: '347 22% 8%', darkFg: '350 12% 92%', darkCard: '347 18% 12%',
    darkMuted: '347 14% 17%', darkMutedFg: '347 10% 74%', darkBorder: '347 12% 26%',
  },
  {
    id: 'sky', name: 'Sky Blue', preview: '#0284C7',
    primary: '198 93% 45%', primaryFg: '0 0% 100%', accent: '198 80% 90%', radius: '0.5rem',
    background: '200 20% 99%', foreground: '200 20% 10%', card: '200 15% 100%',
    muted: '198 14% 96%', mutedFg: '198 8% 42%', border: '198 13% 90%',
    sidebar: '198 30% 97%', sidebarFg: '198 20% 25%',
    darkBg: '200 22% 8%', darkFg: '198 12% 92%', darkCard: '200 18% 12%',
    darkMuted: '198 14% 17%', darkMutedFg: '198 10% 74%', darkBorder: '198 12% 26%',
  },
  {
    id: 'amber', name: 'Golden Amber', preview: '#D97706',
    primary: '43 96% 50%', primaryFg: '222 47% 11%', accent: '43 80% 90%', radius: '0.25rem',
    background: '45 25% 99%', foreground: '40 15% 10%', card: '45 20% 100%',
    muted: '43 14% 96%', mutedFg: '43 8% 42%', border: '43 13% 90%',
    sidebar: '43 25% 97%', sidebarFg: '43 20% 22%',
    darkBg: '40 20% 8%', darkFg: '45 12% 92%', darkCard: '40 18% 12%',
    darkMuted: '40 14% 17%', darkMutedFg: '43 12% 74%', darkBorder: '40 12% 26%',
  },
  {
    id: 'slate', name: 'Midnight Slate', preview: '#334155',
    primary: '215 28% 27%', primaryFg: '0 0% 100%', accent: '215 20% 88%', radius: '0.5rem',
    background: '215 14% 96%', foreground: '215 28% 11%', card: '215 10% 99%',
    muted: '215 12% 91%', mutedFg: '215 12% 40%', border: '215 10% 85%',
    sidebar: '215 28% 18%', sidebarFg: '215 10% 95%',
    darkBg: '215 22% 8%', darkFg: '215 12% 92%', darkCard: '215 18% 12%',
    darkMuted: '215 14% 17%', darkMutedFg: '215 10% 74%', darkBorder: '215 12% 26%',
  },
];

const DEFAULT_THEME = THEMES.find(t => t.id === 'teal') || THEMES[0];

function applyTheme(theme) {
  const r = document.documentElement;
  r.classList.add('theme-transitioning');
  r.style.setProperty('--primary', theme.primary);
  r.style.setProperty('--primary-foreground', theme.primaryFg);
  r.style.setProperty('--accent', theme.accent);
  r.style.setProperty('--radius', theme.radius);
  r.style.setProperty('--ring', theme.primary);
  if (theme.sidebar) r.style.setProperty('--sidebar-bg', theme.sidebar);
  if (theme.sidebarFg) r.style.setProperty('--sidebar-fg', theme.sidebarFg);
  // Respect current dark mode — don't override light/dark bg/fg
  const isDark = r.classList.contains('dark-mode');
  if (isDark) {
    if (theme.darkBg) r.style.setProperty('--background', theme.darkBg);
    if (theme.darkFg) r.style.setProperty('--foreground', theme.darkFg);
    if (theme.darkCard) r.style.setProperty('--card', theme.darkCard);
    if (theme.darkMuted) r.style.setProperty('--muted', theme.darkMuted);
    if (theme.darkMutedFg) r.style.setProperty('--muted-foreground', theme.darkMutedFg);
    if (theme.darkBorder) r.style.setProperty('--border', theme.darkBorder);
  } else {
    if (theme.background) r.style.setProperty('--background', theme.background);
    if (theme.foreground) r.style.setProperty('--foreground', theme.foreground);
    if (theme.card) r.style.setProperty('--card', theme.card);
    if (theme.muted) r.style.setProperty('--muted', theme.muted);
    if (theme.mutedFg) r.style.setProperty('--muted-foreground', theme.mutedFg);
    if (theme.border) r.style.setProperty('--border', theme.border);
  }
  setTimeout(() => r.classList.remove('theme-transitioning'), 350);
}

// Swaps the browser tab icon + PWA home-screen icon for a custom workspace icon
// (or resets to the default Projek icon when url is falsy).
function applyFavicon(url) {
  const sized = [
    { rel: 'icon', sizes: '32x32', fallback: '/favicon.png' },
    { rel: 'icon', sizes: '64x64', fallback: '/favicon64.png' },
    { rel: 'icon', sizes: '192x192', fallback: '/logo192.png' },
    { rel: 'apple-touch-icon', sizes: '180x180', fallback: '/apple-touch-icon.png' },
  ];
  sized.forEach(({ rel, sizes, fallback }) => {
    let link = document.querySelector(`link[rel="${rel}"][sizes="${sizes}"]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      link.sizes = sizes;
      document.head.appendChild(link);
    }
    link.href = url ? `${BACKEND_URL}${url}` : fallback;
  });
}

function applyDarkMode(theme, isDark) {
  const r = document.documentElement;
  r.classList.add('theme-transitioning');
  r.classList.toggle('dark-mode', isDark);
  if (isDark && theme) {
    r.style.setProperty('--background', theme.darkBg || '222 22% 9%');
    r.style.setProperty('--foreground', theme.darkFg || '210 15% 92%');
    r.style.setProperty('--card', theme.darkCard || '222 18% 13%');
    r.style.setProperty('--muted', theme.darkMuted || '222 15% 18%');
    r.style.setProperty('--muted-foreground', theme.darkMutedFg || '210 12% 74%');
    r.style.setProperty('--border', theme.darkBorder || '222 15% 22%');
  } else if (!isDark && theme) {
    r.style.setProperty('--background', theme.background || '0 0% 100%');
    r.style.setProperty('--foreground', theme.foreground || '222 47% 11%');
    r.style.setProperty('--card', theme.card || '0 0% 100%');
    r.style.setProperty('--muted', theme.muted || '220 14% 96%');
    r.style.setProperty('--muted-foreground', theme.mutedFg || '220 8% 46%');
    r.style.setProperty('--border', theme.border || '220 13% 91%');
  }
  setTimeout(() => r.classList.remove('theme-transitioning'), 350);
}

// ============================================================
// API
// ============================================================
async function apiFetch(path, opts = {}, token = null) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BACKEND_URL}/api${path}`, { ...opts, headers });
  let data;
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : {};
  } catch { data = {}; }
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

// ============================================================
// CONTEXTS
// ============================================================
const AuthCtx = createContext(null);
const ThemeCtx = createContext({ theme: DEFAULT_THEME, setTheme: () => {}, darkMode: false, toggleDarkMode: () => {} });
const DataCtx = createContext(null);

function useAuth() { return useContext(AuthCtx) || {}; }
function useTheme() { return useContext(ThemeCtx); }
function useData() { return useContext(DataCtx); }
function useDarkMode() { const { darkMode, toggleDarkMode } = useContext(ThemeCtx); return { darkMode, toggleDarkMode }; }

// ============================================================
// DATE HELPERS
// ============================================================
function todayStr() { return new Date().toISOString().split('T')[0]; }
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}
function fmtDate(dateStr) {
  if (!dateStr) return '';
  const hasTime = dateStr.includes('T') && dateStr.split('T')[1] && dateStr.split('T')[1] !== '00:00';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (hasTime) {
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart} at ${timePart}`;
  }
  return datePart;
}
// Formats a custom-field value; date/datetime values get human-friendly formatting
function fmtFieldValue(cf) {
  const v = cf?.value;
  if (Array.isArray(v)) return v.map(id => LINK_NAMES[id]).filter(Boolean).join(', ');
  if (!v || typeof v !== 'string') return v || '';
  const isDateLike = cf.type === 'date' || cf.type === 'datetime' || /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(v);
  return isDateLike ? fmtDate(v) : v;
}
function fmtShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function getWeekStart(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d.toISOString().split('T')[0];
}
function getMonthDays(year, month) {
  const days = [];
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Pad start
  for (let i = 0; i < firstDay; i++) {
    const d = new Date(year, month, 1 - (firstDay - i));
    days.push({ dateStr: d.toISOString().split('T')[0], isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(year, month, d);
    days.push({ dateStr: dt.toISOString().split('T')[0], isCurrentMonth: true });
  }
  // Pad end to 42
  while (days.length < 42) {
    const last = new Date(days[days.length - 1].dateStr + 'T00:00:00');
    last.setDate(last.getDate() + 1);
    days.push({ dateStr: last.toISOString().split('T')[0], isCurrentMonth: false });
  }
  return days;
}
// Returns the EARLIEST date across all date fields (start of timeline)
function getJobBestDate(job) {
  const dates = [
    job.install_start, job.delivery_date, job.install_end,
    ...(job.custom_fields || [])
      .filter(cf => (cf.type === 'datetime' || cf.type === 'date') && cf.value)
      .map(cf => cf.value)
  ].filter(Boolean);
  if (!dates.length) return null;
  return dates.sort()[0]; // ISO strings sort lexicographically = chronologically
}

// Returns the LATEST date across all date fields (end of timeline)
function getJobEndDate(job) {
  const dates = [
    job.install_start, job.delivery_date, job.install_end,
    ...(job.custom_fields || [])
      .filter(cf => (cf.type === 'datetime' || cf.type === 'date') && cf.value)
      .map(cf => cf.value)
  ].filter(Boolean);
  if (!dates.length) return null;
  return dates.sort().pop(); // latest date
}

function jobOnDay(job, day) {
  const dp = s => (s ? s.split('T')[0] : '');
  const start = dp(getJobBestDate(job));
  const end = dp(getJobEndDate(job));
  if (!start) return false;
  if (start && end && start !== end) return start <= day && day <= end;
  return start === day;
}
function groupByPeriod(jobs) {
  const today = todayStr();
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  // Unscheduled last so dated jobs always surface first
  const groups = { Today: [], Tomorrow: [], 'This Week': [], Upcoming: [], Unscheduled: [] };
  jobs.forEach(j => {
    // Normalize to date-only string (handles both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:MM:SSZ')
    const raw = getJobBestDate(j);
    const date = raw ? raw.split('T')[0] : null;
    if (!date) { groups.Unscheduled.push(j); return; }
    if (date === today) { groups.Today.push(j); return; }
    if (date === tomorrow) { groups.Tomorrow.push(j); return; }
    if (date > today && date <= weekEnd) { groups['This Week'].push(j); return; }
    groups.Upcoming.push(j);
  });
  // Soonest to farthest away within each dated group
  const byDate = (a, b) => (getJobBestDate(a) || '').localeCompare(getJobBestDate(b) || '');
  groups.Today.sort(byDate);
  groups.Tomorrow.sort(byDate);
  groups['This Week'].sort(byDate);
  groups.Upcoming.sort(byDate);
  return groups;
}
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// ============================================================
// UI COMPONENTS
// ============================================================

function Modal({ open, onClose, title, children, width = 'max-w-lg' }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
      <div className={`modal-box ${width}`} onClick={e => e.stopPropagation()} data-testid="modal-box">
        <div className="modal-header">
          <h3 className="modal-title font-manrope">{title}</h3>
          <button className="modal-close" onClick={onClose} data-testid="modal-close-btn"><X size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function Badge({ variant = 'neutral', children }) {
  const cls = {
    success: 'badge-success', warning: 'badge-warning', danger: 'badge-danger',
    info: 'badge-info', neutral: 'badge-neutral', primary: 'badge-primary',
  }[variant] || 'badge-neutral';
  return <span className={`badge ${cls}`}>{children}</span>;
}

function StatusBadge({ status }) {
  const map = {
    new: ['info', 'New'], active: ['success', 'Active'],
    completed: ['success', 'Completed'], cancelled: ['danger', 'Cancelled'],
  };
  const [v, label] = map[status] || ['neutral', status];
  return <Badge variant={v}>{label}</Badge>;
}

function PriorityBadge({ priority }) {
  const map = { high: ['danger', 'High'], medium: ['warning', 'Medium'], low: ['neutral', 'Low'] };
  const [v, label] = map[priority] || ['neutral', priority];
  return <Badge variant={v}>{label}</Badge>;
}

// Curated palette for auto-assigned member/avatar colors (visually distinct on white and dark)
const AVATAR_PALETTE = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
  '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6',
  '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#78716C', '#0891B2',
];
const VIBRANT_COLORS = [
  '#EF4444','#F97316','#EAB308','#22C55E','#14B8A6','#06B6D4',
  '#3B82F6','#6366F1','#8B5CF6','#D946EF','#EC4899','#0EA5E9',
  '#F43F5E','#84CC16','#A855F7','#0891B2',
];
function randomVibrantColor() { return VIBRANT_COLORS[Math.floor(Math.random() * VIBRANT_COLORS.length)]; }
function hashColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h) + str.charCodeAt(i) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function Avatar({ name = '', photo, color = '#10B981', size = 32 }) {
  const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  // Default green means "auto" — assign a hash-based color based on name for visual differentiation
  const displayColor = (!color || color === '#10B981') ? hashColor(name || '?') : color;
  if (photo) return (
    <div className="avatar" style={{ width: size, height: size }}>
      <img src={photo} alt={name} />
    </div>
  );
  return (
    <div className="avatar" style={{ width: size, height: size, background: displayColor, color: '#fff', fontSize: size * 0.35 }}>
      {initials || '?'}
    </div>
  );
}

function Btn({ variant = 'primary', size = 'md', children, className = '', ...props }) {
  const base = 'btn';
  const v = { primary: 'btn-primary', outline: 'btn-outline', ghost: 'btn-ghost', danger: 'btn-danger' }[variant] || 'btn-primary';
  const s = { sm: 'btn-sm', md: '', lg: 'btn-lg' }[size] || '';
  return <button className={`${base} ${v} ${s} ${className}`} {...props}>{children}</button>;
}

// ============================================================
// MULTI-SELECT (bulk archive / bulk delete) — shared helpers
// ============================================================
function useMultiSelect() {
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = (ids) => setSelected(new Set(ids));
  const clear = () => setSelected(new Set());
  const exit = () => { setSelectMode(false); setSelected(new Set()); setBusy(false); };
  return { selectMode, setSelectMode, selected, toggle, selectAll, clear, exit, busy, setBusy };
}

function SelectCheck({ id, selected, onToggle, testid }) {
  return (
    <button type="button" className={`multi-select-check ${selected ? 'checked' : ''}`}
      onClick={(e) => { e.stopPropagation(); onToggle(id); }}
      data-testid={testid} title={selected ? 'Deselect' : 'Select'}>
      {selected ? <CheckSquare size={16} /> : <Square size={16} />}
    </button>
  );
}

function MultiSelectBar({ count, total, onSelectAll, onClear, onCancel, onAction, actionLabel, ActionIcon, busy, danger, testid }) {
  return (
    <div className="multi-select-bar" data-testid={testid}>
      <span className="multi-select-count" data-testid={`${testid}-count`}>{count} selected</span>
      <button className="multi-select-link" type="button" onClick={onSelectAll} data-testid={`${testid}-select-all`}>Select all ({total})</button>
      {count > 0 && <button className="multi-select-link" type="button" onClick={onClear}>Clear</button>}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</button>
        <Btn size="sm" variant={danger ? 'danger' : 'primary'} disabled={!count || busy} onClick={onAction} data-testid={`${testid}-action`}>
          <ActionIcon size={14} /> {busy ? 'Working…' : actionLabel}
        </Btn>
      </div>
    </div>
  );
}

function PasswordInput({ label, error, className = '', value, onChange, placeholder, required, 'data-testid': testId, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="form-field">
      {label && <label className="form-label">{label}</label>}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          className={`form-input ${className}`}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          style={{ paddingRight: 36 }}
          {...props}
        />
        <button type="button" onClick={() => setShow(s => !s)}
          style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))' }}>
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

function Input({ label, labelAction, error, className = '', ...props }) {
  return (
    <div className="form-field">
      {label && <label className="form-label">{label}{labelAction}</label>}
      <input className={`form-input ${error ? 'input-error' : ''} ${className}`} {...props} />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

function Textarea({ label, labelAction, error, className = '', ...props }) {
  return (
    <div className="form-field">
      {label && <label className="form-label">{label}{labelAction}</label>}
      <textarea className={`form-input form-textarea ${error ? 'input-error' : ''} ${className}`} {...props} />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

function Select({ label, labelAction, error, options = [], className = '', ...props }) {
  return (
    <div className="form-field">
      {label && <label className="form-label">{label}{labelAction}</label>}
      <div className="select-wrap">
        <select className={`form-input ${error ? 'input-error' : ''} ${className}`} {...props}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown size={13} className="select-caret" />
      </div>
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, sub, action }) {
  return (
    <div className="empty-state">
      {Icon && <div className="empty-icon"><Icon size={32} /></div>}
      <h4 className="font-manrope">{title}</h4>
      <p>{desc || sub}</p>
      {action}
    </div>
  );
}

function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Delete' }) {
  return (
    <Modal open={open} onClose={onClose} title={title} width="max-w-sm">
      <p className="text-muted mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <Btn variant="outline" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" onClick={onConfirm} data-testid="confirm-delete-btn">{confirmLabel}</Btn>
      </div>
    </Modal>
  );
}

// ============================================================
// JOB FORM MODAL
// ============================================================
// ============================================================
// DEFAULT FIELD DEFINITIONS
// ============================================================
const FIELD_TYPES = {
  text:        { label: 'Text',          icon: FileText },
  number:      { label: 'Number',        icon: Hash },
  email:       { label: 'Email',         icon: Mail },
  tel:         { label: 'Phone',         icon: Phone },
  date:        { label: 'Date Only',     icon: CalendarDays },
  time:        { label: 'Time Only',     icon: Clock },
  datetime:    { label: 'Date & Time',   icon: CalendarDays },
  url:         { label: 'URL / Link',    icon: Link },
  attachment:  { label: 'Attachment',    icon: Paperclip },
  multioption: { label: 'Multi Option',  icon: List },
  location:    { label: 'Location',      icon: MapPin },
  color:       { label: 'Color',         icon: Palette },
  team_member: { label: 'Team Member',   icon: UserSquare },
  project_link: { label: 'Assign Project', icon: Briefcase },
  client_link:  { label: 'Assign Client',  icon: UserCheck },
};

const FIELD_TYPE_TO_INPUT_TYPE = {
  number: 'number', datetime: 'datetime-local', time: 'time',
  date: 'date', email: 'email', tel: 'tel', url: 'url',
};

const FIELD_ID_PLACEHOLDERS = { email: 'jane@company.com', phone: '+1 (555) 000-0000' };

function getMemberFieldPlaceholder(fd, isName, isClient) {
  if (isName) return 'Jane Smith';
  if (fd.id === 'role') return isClient ? 'e.g. Property Owner' : 'e.g. Lead Technician';
  return FIELD_ID_PLACEHOLDERS[fd.id] || `Enter ${fd.label.toLowerCase()}`;
}

// Link fields hold ids; cards resolve them to names through this registry.
const LINK_NAMES = {};
function registerLinkNames(items = []) {
  items.forEach(i => { if (i?.id) LINK_NAMES[i.id] = i.name; });
}
function isLinkField(type) { return type === 'project_link' || type === 'client_link' || type === 'team_member'; }
function hasFieldValue(cf) { return Array.isArray(cf.value) ? cf.value.length > 0 : !!cf.value; }

// Builds the ordered list of fields to display on a project card/panel: follows the
// CURRENT field template's order + label + showOnCard for system fields (which don't
// snapshot their own label), interleaved with the job's own custom fields (which carry
// their save-time label), filtered by the job's saved field_visibility snapshot.
const JOB_CARD_SKIP_IDS = new Set(['name', 'color', 'member_ids', 'client_ids']);
function getOrderedJobFields(job, fieldSchema) {
  const schema = (fieldSchema && fieldSchema.length) ? fieldSchema : DEFAULT_FIELDS;
  // Per-job snapshot wins when present; otherwise fall back to the template's own showOnCard
  // default (so a field hidden-by-default in Configure Fields stays hidden on older jobs
  // that predate it, e.g. Priority/Status added after the job was first saved).
  const isFieldVisible = (fd) => {
    if (job.field_visibility && fd.id in job.field_visibility) return job.field_visibility[fd.id] !== false;
    return fd.showOnCard !== false;
  };
  const seenCustomIds = new Set();
  const items = [];
  [...schema]
    .filter(fd => fd.enabled !== false && !JOB_CARD_SKIP_IDS.has(fd.id))
    .sort((a, b) => a.order - b.order)
    .forEach(fd => {
      if (fd.system) {
        if (!job[fd.id] || !isFieldVisible(fd)) return;
        const isDate = fd.type === 'date' || fd.type === 'datetime';
        items.push({ id: fd.id, label: fd.label, isAddress: fd.id === 'address', value: isDate ? fmtDate(job[fd.id]) : job[fd.id] });
      } else {
        seenCustomIds.add(fd.id);
        const cf = (job.custom_fields || []).find(c => c.id === fd.id);
        if (!cf || !hasFieldValue(cf) || !isFieldVisible(fd)) return;
        items.push({ id: fd.id, label: cf.label || fd.label, isAddress: false, value: fmtFieldValue(cf) });
      }
    });
  // Orphaned custom fields (saved on the job but no longer in the current template) — append at the end.
  (job.custom_fields || []).forEach(cf => {
    if (seenCustomIds.has(cf.id) || !hasFieldValue(cf)) return;
    const fallbackVisible = !job.field_visibility || job.field_visibility[cf.id] !== false;
    if (!fallbackVisible) return;
    items.push({ id: cf.id, label: cf.label, isAddress: false, value: fmtFieldValue(cf) });
  });
  return items;
}

const DEFAULT_FIELDS = [
  { id: 'name',              label: 'Project Name',       type: 'text',     required: true,  system: true, enabled: true, showOnCard: true,  order: 0 },
  { id: 'client',            label: 'Client Name',        type: 'text',     required: false, system: true, enabled: true, showOnCard: true,  order: 1 },
  { id: 'status',            label: 'Status',             type: 'multioption', required: false, system: true, enabled: true, showOnCard: true, order: 2, options: ['New','Active','Completed','Cancelled'] },
  { id: 'priority',          label: 'Priority',           type: 'multioption', required: false, system: true, enabled: true, showOnCard: true, order: 3, options: ['High','Medium','Low'] },
  { id: 'install_start',     label: 'Install Start',      type: 'datetime', required: false, system: true, enabled: true, showOnCard: true,  order: 4 },
  { id: 'install_end',       label: 'Install End',        type: 'datetime', required: false, system: true, enabled: true, showOnCard: true,  order: 5 },
  { id: 'delivery_date',     label: 'Delivery Date',      type: 'datetime', required: false, system: true, enabled: true, showOnCard: true,  order: 6 },
  { id: 'address',           label: 'Address',            type: 'location', required: false, system: true, enabled: true, showOnCard: true,  order: 7 },
  { id: 'site_instructions', label: 'Site Instructions',  type: 'text',     required: false, system: true, enabled: true, showOnCard: true,  order: 8 },
  { id: 'description',       label: 'Description',        type: 'text',     required: false, system: true, enabled: true, showOnCard: true,  order: 9 },
  { id: 'color',             label: 'Project Color',      type: 'text',     required: false, system: true, enabled: true, showOnCard: false, order: 10, isColor: true },
  { id: 'member_ids',        label: 'Assign Members',     type: 'text',     required: false, system: true, enabled: true, showOnCard: true,  order: 11, isMembers: true },
  { id: 'client_ids',        label: 'Assign Clients',     type: 'text',     required: false, system: true, enabled: true, showOnCard: true,  order: 12, isClients: true },
];

const CLIENT_FIELD = DEFAULT_FIELDS.find(f => f.id === 'client_ids');
// Saved templates predate the Assign Clients field, so append it when missing.
function withClientField(fields) {
  if (fields.some(f => f.id === 'client_ids')) return fields;
  return [...fields, { ...CLIENT_FIELD, order: fields.length }];
}

// ============================================================
// JOB FORM MODAL (with customizable fields)
// ============================================================
// Drag-and-drop sortable field item for config mode
function SortableFieldItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children(listeners)}
    </div>
  );
}

function getProjectFormValues(fields, job) {
  const values = {};
  fields.forEach(fd => {
    if (fd.system) {
      if (fd.id === 'status') values[fd.id] = job?.status || 'new';
      else if (fd.id === 'priority') values[fd.id] = job?.priority || 'medium';
      else if (fd.id === 'member_ids') values[fd.id] = job?.member_ids || [];
      else if (fd.id === 'color') values[fd.id] = job?.color || randomVibrantColor();
      else if (fd.type === 'datetime') {
        const value = job?.[fd.id] || '';
        values[fd.id] = value ? (value.includes('T') ? value.slice(0, 16) : value + 'T00:00') : '';
      } else values[fd.id] = job?.[fd.id] ?? '';
    } else {
      const saved = job?.custom_fields?.find(field => field.id === fd.id)?.value;
      values[fd.id] = saved ?? (isLinkField(fd.type) ? [] : '');
    }
  });
  return values;
}

function JobModal({ open, onClose, onSave, job, members }) {
  const { token } = useAuth();
  const { addTask, jobs: allJobs = [] } = useData();
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({});
  const [loadingFields, setLoadingFields] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [configMode, setConfigMode] = useState(false);
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [adminTemplates, setAdminTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [defaultsFolderOpen, setDefaultsFolderOpen] = useState(false);
  const [defaultTemplateId, setDefaultTemplateId] = useState(null);
  // initialTasks feature removed
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Clients live on the client record (one project each), so the project form
  // tracks its selection separately and syncs it after the project is saved.
  const [clients, setClients] = useState([]);
  const [clientIds, setClientIds] = useState([]);

  // Click-outside: close template dropdown
  const templateRef = useRef(null);
  const closeTemplateDropdown = useCallback(() => setTemplateOpen(false), []);
  useOnClickOutside(templateRef, closeTemplateDropdown);

  // Drag-and-drop sensors for field reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIdx = fields.findIndex(f => f.id === active.id);
      const newIdx = fields.findIndex(f => f.id === over.id);
      const updated = arrayMove(fields, oldIdx, newIdx);
      updated.forEach((f, i) => { f.order = i; });
      setFields(updated);
      saveTemplate(updated);
    }
  };

  // Load field template: new job = last used > admin default; edit = user template first
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingFields(true);
    setFields([]);
    setForm({});
    setErr('');
    setTemplateOpen(false);
    const loadTemplate = async () => {
      const applyFieldVisibility = (loadedFields) => {
        if (!job?.field_visibility) return loadedFields;
        return loadedFields.map(fd => {
          const vis = job.field_visibility[fd.id];
          return vis !== undefined ? { ...fd, showOnCard: vis } : fd;
        });
      };

      try {
        // Load all available templates
        const [adminTmpls, userTmpls, defaults] = await Promise.all([
          apiFetch('/admin/templates').catch(() => []),
          apiFetch('/user-templates', {}, token).catch(() => []),
          apiFetch('/profile/default-templates', {}, token).catch(() => ({})),
        ]);
        if (!active) return;
        setAdminTemplates(adminTmpls);
        setUserTemplates(userTmpls);
        setDefaultTemplateId(defaults?.project || null);

        if (!job) {
          // For new: account default template (if set) wins, else last-used
          const allTemplates = [...userTmpls, ...adminTmpls];
          const lastUsedId = localStorage.getItem('lastUsedTemplateId');
          const pickId = defaults?.project || lastUsedId;
          if (pickId) {
            const picked = allTemplates.find(t => t.id === pickId);
            if (picked?.fields?.length) {
              return picked.fields.map((f, i) => ({ ...f, order: i }));
            }
          }
          // Fall back to admin default template
          const defaultTmpl = adminTmpls.find(t => t.is_default);
          if (defaultTmpl?.fields?.length) {
            return [...defaultTmpl.fields].sort((a, b) => a.order - b.order);
          }
        }
        // User's saved field template (fallback for new, primary for edit)
        const tmpl = await apiFetch('/field-templates', {}, token);
        if (!active) return;
        if (tmpl?.fields?.length) {
          return applyFieldVisibility([...tmpl.fields].sort((a, b) => a.order - b.order));
        }
        // Final fallback: admin default (for edits with no user template)
        if (job) {
          const adminTmpl = await apiFetch('/admin/default-field-template');
          if (adminTmpl?.fields?.length) {
            return applyFieldVisibility([...adminTmpl.fields].sort((a, b) => a.order - b.order));
          }
        }
      } catch { /* fallback */ }
      return applyFieldVisibility([...DEFAULT_FIELDS]);
    };
    loadTemplate().then(rawFields => {
      // Initialize once per opening; late responses must not reset an active draft.
      if (!active) return;
      const loadedFields = withClientField(rawFields);
      setFields(loadedFields);
      setForm(getProjectFormValues(loadedFields, job));
      setLoadingFields(false);
    });
    return () => { active = false; };
  }, [token, open, job]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Clients list is manager-only; a 403 simply hides the Assign Clients field.
  useEffect(() => {
    if (!open) return;
    let active = true;
    apiFetch('/clients', {}, token)
      .then(list => {
        if (!active) return;
        registerLinkNames(list);
        setClients(list);
        setClientIds(job?.id ? list.filter(c => (c.project_ids || []).includes(job.id)).map(c => c.id) : []);
      })
      .catch(() => { if (active) { setClients([]); setClientIds([]); } });
    return () => { active = false; };
  }, [open, token, job]);

  const syncClients = async (jobId) => {
    const writes = clients.map(c => {
      const had = (c.project_ids || []).includes(jobId);
      const want = clientIds.includes(c.id);
      if (want && !had) return apiFetch(`/clients/${c.id}`, { method: 'PATCH', body: JSON.stringify({ project_ids: [jobId] }) }, token);
      if (!want && had) return apiFetch(`/clients/${c.id}`, { method: 'PATCH', body: JSON.stringify({ project_ids: (c.project_ids || []).filter(id => id !== jobId) }) }, token);
      return null;
    }).filter(Boolean);
    if (writes.length) await Promise.all(writes);
  };

  const applyTemplate = (tmpl) => {
    localStorage.setItem('lastUsedTemplateId', tmpl.id);
    const newFields = tmpl.fields.map((f, i) => ({ ...f, order: i }));
    setFields(newFields);
    setForm(prev => ({ ...getProjectFormValues(newFields, job), ...prev }));
    setTemplateOpen(false);
    setDefaultsFolderOpen(false);
  };

  const handleToggleDefault = async (tmplId) => {
    const next = defaultTemplateId === tmplId ? null : tmplId;
    try {
      const res = await apiFetch('/profile/default-template', { method: 'PUT', body: JSON.stringify({ kind: 'project', template_id: next }) }, token);
      setDefaultTemplateId(res?.project || null);
    } catch { /* silent */ }
  };

  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim()) return;
    setSavingTemplate(true);
    try {
      const created = await apiFetch('/user-templates', {
        method: 'POST',
        body: JSON.stringify({ name: saveTemplateName.trim(), fields }),
      }, token);
      setUserTemplates(prev => [...prev, created]);
      setSaveTemplateName('');
    } catch (e) { alert(e.message); }
    finally { setSavingTemplate(false); }
  };

  const handleDeleteUserTemplate = async (tmplId) => {
    try {
      await apiFetch(`/user-templates/${tmplId}`, { method: 'DELETE' }, token);
      setUserTemplates(prev => prev.filter(t => t.id !== tmplId));
    } catch (e) { alert(e.message); }
  };

  const handleSave = async () => {
    if (loadingFields || saving) return;
    if (!form.name?.trim()) { setErr('Project name is required'); return; }
    setSaving(true); setErr('');
    try {
      const systemData = {};
      const customFields = [];
      const field_visibility = {};
      fields.forEach(fd => {
        field_visibility[fd.id] = fd.showOnCard !== false;
      });
      fields.filter(fd => fd.enabled).forEach(fd => {
        if (fd.id === 'client_ids') return;
        if (fd.system) {
          systemData[fd.id] = form[fd.id] ?? '';
        } else {
          customFields.push({ id: fd.id, label: fd.label, type: fd.type, value: form[fd.id] || '', options: fd.options || [] });
        }
      });
      const date_fields = fields.filter(field => ['date', 'datetime'].includes(field.type))
        .map(({ id, label, type }) => ({ id, label, type }));
      const saved = await onSave({ ...systemData, custom_fields: customFields, field_visibility, date_fields });
      if (saved?.id) await syncClients(saved.id);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const saveTemplate = async (updatedFields) => {
    try {
      await apiFetch('/field-templates', { method: 'PUT', body: JSON.stringify({ fields: updatedFields }) }, token);
    } catch { /* silent */ }
  };

  const toggleField = (idx) => {
    const updated = [...fields];
    if (updated[idx].id === 'name') return; // can't disable name
    updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
    setFields(updated);
    saveTemplate(updated);
  };

  const addCustomField = () => {
    if (!newFieldLabel.trim()) return;
    const id = `custom_${Date.now()}`;
    const opts = newFieldType === 'multioption' ? newFieldOptions.split(',').map(o => o.trim()).filter(Boolean) : undefined;
    const newField = { id, label: newFieldLabel.trim(), type: newFieldType, required: false, system: false, enabled: true, order: fields.length, options: opts };
    const updated = [...fields, newField];
    setFields(updated);
    setNewFieldLabel('');
    setNewFieldOptions('');
    saveTemplate(updated);
  };

  const removeCustomField = (idx) => {
    if (fields[idx].id === 'name') return;
    const updated = fields.filter((_, i) => i !== idx);
    updated.forEach((f, i) => f.order = i);
    setFields(updated);
    saveTemplate(updated);
  };

  const startRename = (fd) => { setRenamingId(fd.id); setRenameValue(fd.label); };
  const applyRename = (idx) => {
    if (renameValue.trim()) {
      const updated = [...fields];
      updated[idx] = { ...updated[idx], label: renameValue.trim() };
      setFields(updated);
      saveTemplate(updated);
    }
    setRenamingId(null);
  };
  const cancelRename = () => setRenamingId(null);

  // Move field up/down
  const moveField = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const updated = [...fields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((f, i) => f.order = i);
    setFields(updated);
    saveTemplate(updated);
  };

  // Exit config mode when modal is closed
  useEffect(() => {
    if (!open) setConfigMode(false);
  }, [open]);

  const toggleCardVisibility = (fieldId) => {
    const updated = [...fields];
    const idx = updated.findIndex(f => f.id === fieldId);
    if (idx === -1) return;
    updated[idx] = { ...updated[idx], showOnCard: updated[idx].showOnCard === false ? true : false };
    setFields(updated);
    saveTemplate(updated);
  };

  const mapsUrl = form.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.address)}` : null;
  const enabledFields = fields;

  // Inline per-field config controls (shown next to each field's label when Configure Fields is on)
  const renderFieldControls = (fd, idx) => {
    if (renamingId === fd.id) {
      return (
        <span className="field-inline-controls">
          <input className="field-rename-input" autoFocus value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onBlur={() => applyRename(idx)}
            onKeyDown={e => { if (e.key === 'Enter') applyRename(idx); if (e.key === 'Escape') cancelRename(); }}
            data-testid={`rename-input-${fd.id}`} />
        </span>
      );
    }
    return (
      <span className="field-inline-controls" data-testid={`field-controls-${fd.id}`}>
        <span className="field-config-type">{fd.type}</span>
        {fd.id !== 'name' && (
          <button type="button"
            className={`field-vis-switch field-vis-switch-sm ${fd.showOnCard === false ? 'field-vis-off' : 'field-vis-on'}`}
            onClick={(e) => { e.preventDefault(); toggleCardVisibility(fd.id); }}
            title={fd.showOnCard !== false ? 'Shown on card — click to hide' : 'Hidden from card — click to show'}
            data-testid={`vis-toggle-${fd.id}`}>
            <span className="field-vis-knob" />
          </button>
        )}
        {(fd.type === 'datetime' || fd.type === 'date') && (
          <button type="button" className="field-arrow-btn"
            title={fd.type === 'datetime' ? 'Switch to Date Only' : 'Switch to Date & Time'}
            data-testid={`date-type-toggle-${fd.id}`}
            onClick={() => { const u = [...fields]; u[idx] = { ...fd, type: fd.type === 'datetime' ? 'date' : 'datetime' }; setFields(u); saveTemplate(u); }}>
            <span style={{ fontSize: 9, fontWeight: 700, lineHeight: 1 }}>{fd.type === 'datetime' ? 'D+T' : 'D'}</span>
          </button>
        )}
        <button type="button" className="field-arrow-btn" onClick={() => startRename(fd)}
          title="Rename field" data-testid={`rename-field-${fd.id}`}>
          <Edit2 size={12} />
        </button>
        {fd.id !== 'name' && (
          <button type="button" className="icon-btn icon-btn-danger" style={{ padding: 2 }}
            onClick={() => removeCustomField(idx)} data-testid={`remove-field-${fd.id}`} title="Delete field">
            <X size={12} />
          </button>
        )}
        <span className="field-move-arrows">
          <button type="button" className="field-arrow-btn" disabled={idx === 0}
            onClick={() => moveField(idx, -1)} title="Move up" data-testid={`move-up-${fd.id}`}>
            <ChevronUp size={13} />
          </button>
          <button type="button" className="field-arrow-btn" disabled={idx === fields.length - 1}
            onClick={() => moveField(idx, 1)} title="Move down" data-testid={`move-down-${fd.id}`}>
            <ChevronDown size={13} />
          </button>
        </span>
      </span>
    );
  };

  return (
    <Modal open={open} onClose={onClose} title={job?.id ? 'Edit Project' : 'New Project'} width="max-w-2xl">
      {/* Top Actions Row — Save | Configure Fields | My Templates */}
      <div className="modal-top-actions">
        <Btn onClick={handleSave} disabled={saving || loadingFields} data-testid="save-job-btn">
          {saving ? 'Saving...' : (job?.id ? 'Save Changes' : 'Create Project')}
        </Btn>
        <button className={`job-config-toggle ${configMode ? 'active' : ''}`}
          disabled={loadingFields || saving}
          onClick={() => setConfigMode(m => !m)} data-testid="toggle-field-config">
          <Settings size={14} /> {configMode ? 'Done' : 'Configure Fields'}
        </button>
        <div className="template-picker-wrapper" ref={templateRef} data-testid="template-picker-wrapper">
          <button className={`job-config-toggle ${templateOpen ? 'active' : ''}`} onClick={() => setTemplateOpen(o => !o)}
            disabled={loadingFields || saving}
            data-testid="template-picker-btn">
            <FileText size={14} /> My Templates <ChevronDown size={12} style={{ marginLeft: 2 }} />
          </button>
            {templateOpen && (
              <div className="template-dropdown" data-testid="template-dropdown">
                {userTemplates.length > 0 && (
                  <>
                    <div className="template-dropdown-section">My Templates</div>
                    {userTemplates.map(t => (
                      <div key={t.id} className="template-dropdown-item-row">
                        <button className="template-dropdown-item" onClick={() => applyTemplate(t)}
                          data-testid={`apply-template-${t.id}`}>
                          <FileText size={13} />
                          <span>{t.name}</span>
                          <span className="template-field-count">{t.fields?.length || 0} fields</span>
                        </button>
                        <button className={`template-star-btn ${defaultTemplateId === t.id ? 'is-default' : ''}`}
                          onClick={() => handleToggleDefault(t.id)}
                          title={defaultTemplateId === t.id ? 'Remove as default' : 'Set as default'}
                          data-testid={`set-default-template-${t.id}`}>
                          <Star size={12} fill={defaultTemplateId === t.id ? 'currentColor' : 'none'} />
                        </button>
                        <button className="template-delete-btn" onClick={() => handleDeleteUserTemplate(t.id)}
                          data-testid={`delete-template-${t.id}`} title="Delete template">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </>
                )}
                {adminTemplates.length > 0 && (
                  <>
                    <button type="button" className="template-folder-toggle"
                      onClick={() => setDefaultsFolderOpen(o => !o)} data-testid="default-templates-folder-toggle">
                      <Folder size={13} />
                      <span>Default Templates</span>
                      <span className="template-field-count">{adminTemplates.length}</span>
                      <ChevronDown size={12} className={defaultsFolderOpen ? 'rotate-180' : ''} style={{ marginLeft: 'auto' }} />
                    </button>
                    {defaultsFolderOpen && adminTemplates.map(t => (
                      <div key={t.id} className="template-dropdown-item-row template-dropdown-item-nested">
                        <button className="template-dropdown-item"
                          onClick={() => applyTemplate(t)} data-testid={`apply-template-${t.id}`}>
                          <FileText size={13} />
                          <span>{t.name}</span>
                          <span className="template-field-count">{t.fields?.length || 0} fields</span>
                        </button>
                        <button className={`template-star-btn ${defaultTemplateId === t.id ? 'is-default' : ''}`}
                          onClick={() => handleToggleDefault(t.id)}
                          title={defaultTemplateId === t.id ? 'Remove as default' : 'Set as default'}
                          data-testid={`set-default-template-${t.id}`}>
                          <Star size={12} fill={defaultTemplateId === t.id ? 'currentColor' : 'none'} />
                        </button>
                      </div>
                    ))}
                  </>
                )}
                {/* Save current as template */}
                <div className="template-dropdown-section">Save Current</div>
                <div className="template-save-row">
                  <input className="form-input" placeholder="Template name..." value={saveTemplateName}
                    onChange={e => setSaveTemplateName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveAsTemplate()}
                    data-testid="save-template-name" />
                  <Btn size="sm" onClick={handleSaveAsTemplate}
                    disabled={savingTemplate || !saveTemplateName.trim()} data-testid="save-template-btn">
                    <Save size={12} /> Save
                  </Btn>
                </div>
              </div>
            )}
          </div>
      </div>
      {err && <div className="alert-error mb-4" role="alert" data-testid="project-form-error">{err}</div>}

      {loadingFields ? (
        <div role="status" data-testid="project-form-loading">Loading fields…</div>
      ) : (
        /* ===== FORM MODE ===== */
        <div className="job-form-grid" data-testid="job-form-fields">
          {enabledFields.map((fd, idx) => (
            <FieldRenderer key={fd.id} field={fd}
              value={fd.id === 'client_ids' ? clientIds : form[fd.id]}
              onChange={fd.id === 'client_ids' ? setClientIds : (v => set(fd.id, v))}
              form={form} set={set} members={members} clients={clients}
              jobs={allJobs.filter(j => j.id !== job?.id)} mapsUrl={mapsUrl} token={token}
              labelControls={configMode ? renderFieldControls(fd, idx) : undefined} />
          ))}
          {configMode && (
            <div className="field-add-section job-field-full" data-testid="add-custom-field-section">
              <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add Custom Field</h4>
              <div className="field-add-row">
                <input className="form-input" placeholder="Field name..." value={newFieldLabel}
                  onChange={e => setNewFieldLabel(e.target.value)} data-testid="new-field-name" />
                <div className="select-wrap" style={{ flex: '0 0 150px' }}>
                  <select className="form-input" value={newFieldType}
                    onChange={e => setNewFieldType(e.target.value)} data-testid="new-field-type">
                    {Object.entries(FIELD_TYPES).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="select-caret" />
                </div>
                <Btn size="sm" onClick={addCustomField} disabled={!newFieldLabel.trim()} data-testid="add-field-btn">
                  <Plus size={14} /> Add
                </Btn>
              </div>
              {newFieldType === 'multioption' && (
                <input className="form-input mt-2" placeholder="Options (comma separated): Option A, Option B, ..."
                  value={newFieldOptions} onChange={e => setNewFieldOptions(e.target.value)}
                  data-testid="new-field-options" />
              )}
            </div>
          )}
          {/* Bottom Save (QoL — mirrors the top button so users don't scroll back up) */}
          <div className="modal-bottom-actions" data-testid="job-modal-bottom-actions">
            <Btn onClick={handleSave} disabled={saving || loadingFields} data-testid="save-job-btn-bottom">
              {saving ? 'Saving...' : (job?.id ? 'Save Changes' : 'Create Project')}
            </Btn>
          </div>
        </div>
      )}

      {/* Initial Tasks section removed */}

    </Modal>
  );
}

// ============================================================
// MEMBER PICKER FIELD (stateful: search + recent 3 members)
// ============================================================
function MemberPickerField({ fd, value, onChange, members, VisBtn }) {
  const [memberSearch, setMemberSearch] = useState('');
  const assigned = value || [];
  const assignedList = members.filter(m => assigned.includes(m.id));
  const unassigned = members.filter(m => !assigned.includes(m.id));

  const toggle = (mid) => {
    const nowAssigning = !assigned.includes(mid);
    onChange(nowAssigning ? [...assigned, mid] : assigned.filter(x => x !== mid));
    if (nowAssigning) {
      try {
        const recent = JSON.parse(localStorage.getItem('recentMemberIds') || '[]');
        const filtered = recent.filter(id => id !== mid);
        filtered.unshift(mid);
        localStorage.setItem('recentMemberIds', JSON.stringify(filtered.slice(0, 10)));
      } catch {}
    }
  };

  // Recent members — unassigned only
  const recentIds = (() => { try { return JSON.parse(localStorage.getItem('recentMemberIds') || '[]'); } catch { return []; } })();
  const recentMembers = recentIds.map(id => unassigned.find(m => m.id === id)).filter(Boolean).slice(0, 3);

  const filtered = memberSearch.trim()
    ? unassigned.filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
    : [];

  return (
    <div className="job-field job-field-full">
      <label className="form-label">{fd.label}{VisBtn && <VisBtn />}</label>
      <div className="assign-members-field">
        {/* Assigned chips */}
        {assignedList.length > 0 && (
          <div className="member-chips-row">
            {assignedList.map(m => (
              <div key={m.id} className="member-chip-assigned" data-testid={`assigned-chip-${m.id}`}>
                <Avatar name={m.name} photo={m.photo_url} color={m.color} size={20} />
                <span>{m.name}</span>
                <button type="button" onClick={() => toggle(m.id)} className="member-chip-remove"><X size={11} /></button>
              </div>
            ))}
          </div>
        )}
        {/* Search input */}
        <div className="member-search-row">
          <Search size={13} className="member-search-icon" />
          <input
            className="member-search-input"
            placeholder={assignedList.length === 0 ? 'Search members...' : 'Add more members...'}
            value={memberSearch}
            onChange={e => setMemberSearch(e.target.value)}
            data-testid="member-search-input"
          />
          {memberSearch && (
            <button type="button" className="member-chip-remove" onClick={() => setMemberSearch('')}>
              <X size={12} />
            </button>
          )}
        </div>
        {/* No search: show recent 3 when none assigned, or all unassigned when some assigned */}
        {!memberSearch && assignedList.length === 0 && recentMembers.length > 0 && (
          <>
            <p className="member-section-label">Recent</p>
            <div className="member-add-list">
              {recentMembers.map(m => (
                <button key={m.id} type="button" className="member-add-row"
                  onClick={() => toggle(m.id)} data-testid={`assign-member-${m.id}`}>
                  <Avatar name={m.name} photo={m.photo_url} color={m.color} size={24} />
                  <div className="member-add-info">
                    <span className="member-add-name">{m.name}</span>
                    {m.role && <span className="member-add-role">{m.role}</span>}
                  </div>
                  <Plus size={13} className="member-add-plus" />
                </button>
              ))}
            </div>
          </>
        )}
        {/* Search results */}
        {memberSearch && (
          <div className="member-add-list">
            {filtered.length > 0 ? filtered.map(m => (
              <button key={m.id} type="button" className="member-add-row"
                onClick={() => toggle(m.id)} data-testid={`assign-member-${m.id}`}>
                <Avatar name={m.name} photo={m.photo_url} color={m.color} size={24} />
                <div className="member-add-info">
                  <span className="member-add-name">{m.name}</span>
                  {m.role && <span className="member-add-role">{m.role}</span>}
                </div>
                <Plus size={13} className="member-add-plus" />
              </button>
            )) : <p className="member-chip-empty">No members found</p>}
          </div>
        )}
        {/* No search: show all unassigned when some are already assigned */}
        {!memberSearch && assignedList.length > 0 && unassigned.length > 0 && (
          <div className="member-add-list">
            {unassigned.map(m => (
              <button key={m.id} type="button" className="member-add-row"
                onClick={() => toggle(m.id)} data-testid={`assign-member-${m.id}`}>
                <Avatar name={m.name} photo={m.photo_url} color={m.color} size={24} />
                <div className="member-add-info">
                  <span className="member-add-name">{m.name}</span>
                  {m.role && <span className="member-add-role">{m.role}</span>}
                </div>
                <Plus size={13} className="member-add-plus" />
              </button>
            ))}
          </div>
        )}
        {/* No search, none assigned, no recents — show all members */}
        {!memberSearch && assignedList.length === 0 && recentMembers.length === 0 && unassigned.length > 0 && (
          <div className="member-add-list">
            {unassigned.map(m => (
              <button key={m.id} type="button" className="member-add-row"
                onClick={() => toggle(m.id)} data-testid={`assign-member-${m.id}`}>
                <Avatar name={m.name} photo={m.photo_url} color={m.color} size={24} />
                <div className="member-add-info">
                  <span className="member-add-name">{m.name}</span>
                  {m.role && <span className="member-add-role">{m.role}</span>}
                </div>
                <Plus size={13} className="member-add-plus" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// FIELD RENDERER (renders individual field by type)
// ============================================================
function FieldRenderer({ field, value, onChange, form, set, members, clients, jobs, mapsUrl, token, showOnCard, onToggleVis, labelControls }) {
  const fd = field;

  const VisBtn = () => {
    if (labelControls !== undefined && labelControls !== null) return labelControls;
    return onToggleVis ? (
    <button
      className={`field-vis-switch field-vis-switch-sm ${showOnCard === false ? 'field-vis-off' : 'field-vis-on'}`}
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleVis(fd.id); }}
      title={showOnCard !== false ? 'Shown on card — click to hide' : 'Hidden from card — click to show'}
      data-testid={`vis-toggle-${fd.id}`}
    >
      <span className="field-vis-knob" />
    </button>
  ) : null;
  };

  // System fields with special rendering
  if (fd.isColor || (fd.system && fd.id === 'color') || fd.type === 'color') {
    const PRESET_COLORS = ['#EF4444','#3B82F6','#10B981','#F97316','#8B5CF6','#EAB308'];
    const recentColors = (() => { try { return JSON.parse(localStorage.getItem('lastUsedJobColors') || '[]'); } catch { return []; } })();
    const isCustom = value && !PRESET_COLORS.includes(value) && !recentColors.includes(value);
    const handleWheelChange = (color) => {
      onChange(color);
      try {
        const recent = JSON.parse(localStorage.getItem('lastUsedJobColors') || '[]');
        const filtered = recent.filter(c => c !== color);
        filtered.unshift(color);
        localStorage.setItem('lastUsedJobColors', JSON.stringify(filtered.slice(0, 6)));
      } catch {}
    };
    return (
      <div className="job-field job-field-full">
        <label className="form-label">{fd.label}<VisBtn /></label>
        <div className="color-picker-row">
          {/* Left: 6 preset colors */}
          {PRESET_COLORS.map(c => (
            <button key={c} type="button"
              className={`color-swatch ${value === c ? 'active' : ''}`}
              style={{ '--swatch-color': c }}
              onClick={() => onChange(c)}
              data-testid={`color-swatch-${c.slice(1)}`} title={c}>
              {value === c && <Check size={11} strokeWidth={3} />}
            </button>
          ))}
          {/* Center: Color wheel — custom color */}
          <label className={`color-swatch color-swatch-wheel ${isCustom ? 'active' : ''}`}
            title="Custom color">
            <input type="color" value={value || '#10B981'}
              onChange={e => handleWheelChange(e.target.value)} className="color-wheel-input"
              data-testid="color-wheel-input" />
            {isCustom && <Check size={11} strokeWidth={3} style={{ color: '#fff', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))' }} />}
          </label>
          {/* Right: 6 recent-color slots (from color wheel usage) */}
          {Array.from({ length: 6 }).map((_, i) => {
            const rc = recentColors[i];
            return rc ? (
              <button key={`rc-${i}`} type="button"
                className={`color-swatch color-swatch-recent ${value === rc ? 'active' : ''}`}
                style={{ '--swatch-color': rc }}
                onClick={() => onChange(rc)}
                data-testid={`color-recent-${i}`} title={rc}>
                {value === rc && <Check size={11} strokeWidth={3} />}
              </button>
            ) : (
              <button key={`rc-empty-${i}`} type="button"
                className="color-swatch color-swatch-empty" disabled
                data-testid={`color-recent-empty-${i}`} title="No recent color" />
            );
          })}
        </div>
      </div>
    );
  }

  if (fd.isMembers || (fd.system && fd.id === 'member_ids') || fd.type === 'team_member') {
    if (!members?.length) return null;
    return <MemberPickerField fd={fd} value={value} onChange={onChange} members={members} VisBtn={VisBtn} />;
  }

  if (fd.isClients || (fd.system && fd.id === 'client_ids')) {
    if (!clients?.length) return null;
    return <LinkPickerField label={fd.label} labelExtra={<VisBtn />} items={clients.map(c => ({ ...c, subtitle: c.role }))}
      value={value} onChange={onChange} multiple noun="client" testid="client-picker" />;
  }

  // Custom link fields — single selection, same design as Assign Members
  if (fd.type === 'client_link') {
    return <LinkPickerField label={fd.label} labelExtra={<VisBtn />} items={(clients || []).map(c => ({ ...c, subtitle: c.role }))}
      value={value} onChange={onChange} noun="client" testid={`link-${fd.id}`} />;
  }

  if (fd.type === 'project_link') {
    return <LinkPickerField label={fd.label} labelExtra={<VisBtn />} items={(jobs || []).map(j => ({ ...j, subtitle: j.client }))}
      value={value} onChange={onChange} noun="project" shape="dot" testid={`link-${fd.id}`} />;
  }

  // System select fields (status/priority)
  if (fd.system && fd.type === 'multioption') {
    const opts = (fd.options || []).map(o => ({ value: o.toLowerCase(), label: o }));
    return (
      <div className="job-field job-field-half">
        <Select label={fd.label} labelAction={<VisBtn />} value={value} onChange={e => onChange(e.target.value)} options={opts}
          data-testid={`job-${fd.id}-select`} />
      </div>
    );
  }

  // Location
  if (fd.type === 'location') {
    const url = value ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}` : null;
    return (
      <div className="job-field job-field-full">
        <label className="form-label">{fd.label}<VisBtn /></label>
        <div className="address-row">
          <input className="form-input" value={value || ''} onChange={e => onChange(e.target.value)}
            placeholder="Street, City, State ZIP" data-testid={`field-${fd.id}`} />
          <a href={url || '#'} target="_blank" rel="noopener noreferrer"
            className={`maps-btn ${!url ? 'maps-btn-disabled' : ''}`}
            onClick={e => { if (!url) e.preventDefault(); }} title="Search on Google Maps">
            <MapPin size={15} /> Maps
          </a>
        </div>
      </div>
    );
  }

  // Date & Time
  if (fd.type === 'datetime') {
    return (
      <div className="job-field job-field-half">
        <Input label={fd.label} labelAction={<VisBtn />} type="datetime-local" value={value || ''}
          onChange={e => onChange(e.target.value)} data-testid={`field-${fd.id}`} />
      </div>
    );
  }

  // Number
  if (fd.type === 'number') {
    return (
      <div className="job-field job-field-half">
        <Input label={fd.label} labelAction={<VisBtn />} type="number" value={value || ''}
          onChange={e => onChange(e.target.value)} placeholder="0"
          data-testid={`field-${fd.id}`} />
      </div>
    );
  }

  // Date only
  if (fd.type === 'date') {
    return (
      <div className="job-field job-field-half">
        <Input label={fd.label} labelAction={<VisBtn />} type="date" value={value || ''}
          onChange={e => onChange(e.target.value)}
          data-testid={`field-${fd.id}`} />
      </div>
    );
  }

  // Time only
  if (fd.type === 'time') {
    return (
      <div className="job-field job-field-half">
        <Input label={fd.label} labelAction={<VisBtn />} type="time" value={value || ''}
          onChange={e => onChange(e.target.value)}
          data-testid={`field-${fd.id}`} />
      </div>
    );
  }

  // URL / link
  if (fd.type === 'url') {
    return (
      <div className="job-field job-field-half">
        <label className="form-label">{fd.label}<VisBtn /></label>
        <div className="address-row">
          <input className="form-input" type="url" value={value || ''}
            onChange={e => onChange(e.target.value)} placeholder="https://"
            data-testid={`field-${fd.id}`} />
          {value && (
            <a href={value} target="_blank" rel="noopener noreferrer" className="maps-btn">
              <ExternalLink size={13} /> Open
            </a>
          )}
        </div>
      </div>
    );
  }

  // Attachment
  if (fd.type === 'attachment') {
    return (
      <div className="job-field job-field-full">
        <label className="form-label">{fd.label}<VisBtn /></label>
        <AttachmentField field={fd} value={value} onChange={onChange} token={token} />
      </div>
    );
  }

  // Multi-option (custom)
  if (fd.type === 'multioption' && !fd.system) {
    const opts = (fd.options || []).map(o => ({ value: o, label: o }));
    return (
      <div className="job-field job-field-half">
        <Select label={fd.label} labelAction={<VisBtn />} value={value || ''} onChange={e => onChange(e.target.value)}
          options={[{ value: '', label: `Select ${fd.label}...` }, ...opts]}
          data-testid={`field-${fd.id}`} />
      </div>
    );
  }

  // Text — long text fields are full width, short are half
  const isLong = fd.id === 'description' || fd.id === 'site_instructions';
  if (isLong) {
    return (
      <div className="job-field job-field-full">
        <Textarea label={fd.label} labelAction={<VisBtn />} value={value || ''} onChange={e => onChange(e.target.value)}
          rows={fd.id === 'description' ? 3 : 2} placeholder={`Enter ${fd.label.toLowerCase()}...`}
          data-testid={`field-${fd.id}`} />
      </div>
    );
  }

  // Default text — name is full, others are half
  return (
    <div className={`job-field ${fd.id === 'name' ? 'job-field-full' : 'job-field-half'}`}>
      <Input label={`${fd.label}${fd.required ? ' *' : ''}`} labelAction={<VisBtn />} value={value || ''}
        onChange={e => onChange(e.target.value)} placeholder={`Enter ${fd.label.toLowerCase()}...`}
        data-testid={`field-${fd.id}`} />
    </div>
  );
}

function AttachmentField({ field, value, onChange, token }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload-attachment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Upload failed'); }
      const data = await res.json();
      onChange(JSON.stringify({ url: data.url, filename: data.filename }));
    } catch (err) { alert(err.message); }
    finally { setUploading(false); }
  };

  let parsed = null;
  try { parsed = value ? JSON.parse(value) : null; } catch {}

  return (
    <div className="form-field">
      {parsed ? (
        <div className="attachment-preview">
          <Paperclip size={14} />
          <a href={`${BACKEND_URL}${parsed.url}`} target="_blank" rel="noopener noreferrer"
            className="attachment-filename">{parsed.filename}</a>
          <button className="icon-btn-sm" onClick={() => { onChange(''); if (fileRef.current) fileRef.current.value = ''; }}>
            <X size={12} />
          </button>
        </div>
      ) : (
        <label className="attachment-upload-btn">
          <Paperclip size={14} />
          <span>{uploading ? 'Uploading...' : 'Attach File'}</span>
          <input type="file" ref={fileRef} onChange={handleUpload} className="sr-only"
            disabled={uploading} data-testid={`field-${field.id}`} />
        </label>
      )}
    </div>
  );
}

// Default system fields for team members — shown & editable in Configure Fields
const DEFAULT_MEMBER_FIELDS = [
  { id: 'name',  label: 'Name',         type: 'text',  system: true, showOnCard: true, required: true },
  { id: 'email', label: 'Email',        type: 'email', system: true, showOnCard: true },
  { id: 'phone', label: 'Phone',        type: 'tel',   system: true, showOnCard: true },
  { id: 'role',  label: 'Role / Title', type: 'text',  system: true, showOnCard: true },
];

// A client belongs to one project, so the client template ships with this field
const CLIENT_PROJECT_FIELD = { id: 'project_ids', label: 'Assign Project', type: 'project_link', system: true, showOnCard: true };
const DEFAULT_CLIENT_FIELDS = [...DEFAULT_MEMBER_FIELDS, CLIENT_PROJECT_FIELD];
function withProjectField(fields) {
  if (fields.some(f => f.id === 'project_ids')) return fields;
  return [...fields, { ...CLIENT_PROJECT_FIELD }];
}

// ============================================================
// MEMBER FORM MODAL (with Configure Fields)
// ============================================================
function MemberModal({ open, onClose, onSave, member, token, kind = 'member' }) {
  const isClient = kind === 'client';
  const noun = isClient ? 'Client' : 'Member';
  const { jobs = [], members: teamMembers = [] } = useData();
  const [clients, setClients] = useState([]);
  const defaultFields = isClient ? DEFAULT_CLIENT_FIELDS : DEFAULT_MEMBER_FIELDS;
  const seedFields = useCallback((f) => (isClient ? withProjectField(f) : f), [isClient]);
  const empty = { name: '', role: '', email: '', phone: '', color: randomVibrantColor() };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [memberTemplates, setMemberTemplates] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [defaultTemplateId, setDefaultTemplateId] = useState(null);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Configure fields
  const [configMode, setConfigMode] = useState(false);
  const [memberFields, setMemberFields] = useState([]); // custom field schema
  const [customValues, setCustomValues] = useState({}); // field id -> value
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [savingFields, setSavingFields] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const templateRef = useRef(null);
  const closeTemplateDropdown = useCallback(() => setTemplateOpen(false), []);
  useOnClickOutside(templateRef, closeTemplateDropdown);

  // DnD sensors for field reordering
  const memberDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleMemberDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIdx = memberFields.findIndex(f => f.id === active.id);
      const newIdx = memberFields.findIndex(f => f.id === over.id);
      setMemberFields(prev => arrayMove(prev, oldIdx, newIdx));
    }
  };

  useEffect(() => {
    if (!open) return;
    setForm(member ? {
      name: member.name || '', role: member.role || '',
      email: member.email || '', phone: member.phone || '', color: member.color || randomVibrantColor(),
      ...(isClient ? { project_ids: member.project_ids || [] } : {}),
    } : { name: '', role: '', email: '', phone: '', color: randomVibrantColor(), ...(isClient ? { project_ids: [] } : {}) });
    setErr(''); setConfigMode(false);
    // Load custom field values from member
    const vals = {};
    (member?.custom_fields || []).forEach(cf => { vals[cf.id] = cf.value ?? ''; });
    setCustomValues(vals);
  }, [member, open, isClient]);

  useEffect(() => {
    if (!open || !token) return;
    apiFetch('/clients', {}, token).then(list => { registerLinkNames(list); setClients(list); }).catch(() => setClients([]));
  }, [open, token]);

  useEffect(() => {
    if (!open || !token) return;
    apiFetch(`/${kind}-templates`, {}, token).then(list => {
      setMemberTemplates(list);
      apiFetch('/profile/default-templates', {}, token).then(defaults => {
        const defId = defaults?.[kind] || null;
        setDefaultTemplateId(defId);
        if (!member) {
          const lastUsedId = localStorage.getItem(`lastUsedTemplateId_${kind}`);
          const picked = list.find(t => t.id === (defId || lastUsedId));
          if (picked?.defaults) setForm(f => ({ ...f, ...picked.defaults }));
        }
      }).catch(() => {});
    }).catch(() => {});
    apiFetch(`/${kind}-field-templates`, {}, token)
      .then(async data => {
        const saved = data.fields || [];
        if (saved.length) {
          // Respect saved schema exactly (deleted system fields stay deleted)
          setMemberFields(seedFields(saved));
          return;
        }
        // Fresh workspace — seed from the admin default for this kind, else built-ins
        const adminDefault = await apiFetch(`/admin/default-field-template?kind=${kind}`).catch(() => null);
        const base = adminDefault?.fields?.length
          ? [...adminDefault.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          : defaultFields;
        setMemberFields(seedFields(base));
      })
      .catch(() => setMemberFields(seedFields(defaultFields)));
  }, [open, token, kind, defaultFields, seedFields, member]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyTemplate = (tmpl) => {
    setForm(f => ({ ...f, ...(tmpl.defaults || {}) }));
    setTemplateOpen(false);
    localStorage.setItem(`lastUsedTemplateId_${kind}`, tmpl.id);
  };

  const handleToggleDefault = async (tmplId) => {
    const next = defaultTemplateId === tmplId ? null : tmplId;
    try {
      const res = await apiFetch('/profile/default-template', { method: 'PUT', body: JSON.stringify({ kind, template_id: next }) }, token);
      setDefaultTemplateId(res?.[kind] || null);
    } catch { /* silent */ }
  };

  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim()) return;
    setSavingTemplate(true);
    try {
      const created = await apiFetch(`/${kind}-templates`, {
        method: 'POST',
        body: JSON.stringify({ name: saveTemplateName.trim(), defaults: { role: form.role, email: form.email, phone: form.phone } }),
      }, token);
      setMemberTemplates(prev => [...prev, created]);
      setSaveTemplateName('');
    } catch (e) { alert(e.message); }
    finally { setSavingTemplate(false); }
  };

  const handleDeleteTemplate = async (tmplId) => {
    try {
      await apiFetch(`/${kind}-templates/${tmplId}`, { method: 'DELETE' }, token);
      setMemberTemplates(prev => prev.filter(t => t.id !== tmplId));
    } catch (e) { alert(e.message); }
  };

  const addCustomField = () => {
    if (!newFieldLabel.trim()) return;
    const newField = { id: `cf_${Date.now()}`, label: newFieldLabel.trim(), type: newFieldType, showOnCard: true };
    setMemberFields(prev => [...prev, newField]);
    setNewFieldLabel(''); setNewFieldType('text');
  };

  const removeCustomField = (id) => {
    // Name is a hard requirement — cannot be deleted (blocks new-member creation otherwise)
    if (id === 'name') return;
    setMemberFields(prev => prev.filter(f => f.id !== id));
    setCustomValues(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const startMemberRename = (fd) => { setRenamingId(fd.id); setRenameValue(fd.label); };
  const applyMemberRename = (id) => {
    if (renameValue.trim()) {
      setMemberFields(prev => prev.map(f => f.id === id ? { ...f, label: renameValue.trim() } : f));
    }
    setRenamingId(null);
  };
  const toggleMemberCardVis = (id) => {
    setMemberFields(prev => prev.map(f => f.id === id ? { ...f, showOnCard: f.showOnCard === false ? true : false } : f));
  };

  const saveFieldSchema = async () => {
    setSavingFields(true);
    try {
      await apiFetch(`/${kind}-field-templates`, { method: 'PUT', body: JSON.stringify({ fields: memberFields }) }, token);
      setConfigMode(false);
    } catch (e) { alert(e.message); }
    finally { setSavingFields(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const custom_fields = memberFields.filter(f => !f.system).map(f => ({
        id: f.id, label: f.label, type: f.type,
        value: customValues[f.id] ?? (isLinkField(f.type) ? [] : ''),
      }));
      await onSave({ ...form, custom_fields });
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`${member ? 'Edit' : 'New'} ${noun}`} width="max-w-2xl">
      {/* Top Actions */}
      <div className="modal-top-actions">
        <Btn onClick={configMode ? saveFieldSchema : handleSave}
          disabled={saving || savingFields} data-testid={`save-${kind}-btn`}>
          {configMode
            ? (savingFields ? 'Saving…' : 'Save Fields')
            : (saving ? 'Saving…' : (member ? 'Save Changes' : `Add ${noun}`))}
        </Btn>
        <button className={`job-config-toggle ${configMode ? 'active' : ''}`}
          onClick={() => setConfigMode(c => !c)} data-testid={`${kind}-configure-fields-btn`}
          title={`Configure custom fields for all ${kind}s`}>
          <Settings size={14} /> Configure Fields
          <ChevronDown size={12} className={configMode ? 'rotate-180' : ''} style={{ marginLeft: 2 }} />
        </button>
        <div className="template-picker-wrapper" ref={templateRef}>
          <button className={`job-config-toggle ${templateOpen ? 'active' : ''}`}
            onClick={() => setTemplateOpen(o => !o)} data-testid={`${kind}-template-picker-btn`}>
            <FileText size={14} /> My Templates <ChevronDown size={12} style={{ marginLeft: 2 }} />
          </button>
          {templateOpen && (
            <div className="template-dropdown member-template-dropdown" data-testid={`${kind}-template-dropdown`}>
              {memberTemplates.length > 0 && (
                <>
                  <div className="template-dropdown-section">My Templates</div>
                  {memberTemplates.map(t => (
                    <div key={t.id} className="template-dropdown-item-row">
                      <button className="template-dropdown-item" onClick={() => applyTemplate(t)}
                        data-testid={`apply-${kind}-template-${t.id}`}>
                        <FileText size={13} /><span>{t.name}</span>
                      </button>
                      <button className={`template-star-btn ${defaultTemplateId === t.id ? 'is-default' : ''}`}
                        onClick={() => handleToggleDefault(t.id)}
                        title={defaultTemplateId === t.id ? 'Remove as default' : 'Set as default'}
                        data-testid={`set-default-${kind}-template-${t.id}`}>
                        <Star size={12} fill={defaultTemplateId === t.id ? 'currentColor' : 'none'} />
                      </button>
                      <button className="template-delete-btn" onClick={() => handleDeleteTemplate(t.id)}
                        data-testid={`delete-${kind}-template-${t.id}`} title="Delete template">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  <div style={{ height: 1, background: 'hsl(var(--border))', margin: '6px 0' }} />
                </>
              )}
              <div className="template-dropdown-section">Save Current as Template</div>
              <div className="template-save-row">
                <input className="form-input" placeholder="Template name..." value={saveTemplateName}
                  onChange={e => setSaveTemplateName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveAsTemplate()}
                  data-testid={`save-${kind}-template-name`} />
                <Btn size="sm" onClick={handleSaveAsTemplate}
                  disabled={savingTemplate || !saveTemplateName.trim()} data-testid={`save-${kind}-template-btn`}>
                  <Save size={12} /> Save
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
      {err && <div className="alert-error mb-4">{err}</div>}

      {configMode ? (
        /* ===== CONFIGURE FIELDS MODE ===== */
        <div className="field-config" data-testid={`${kind}-config-mode`}>
          <DndContext sensors={memberDndSensors} collisionDetection={closestCenter} onDragEnd={handleMemberDragEnd}>
            <SortableContext items={memberFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
              <div className="field-config-dnd-list">
                {/* Custom fields only — sortable */}
                {memberFields.map((fd) => (
                  <SortableFieldItem key={fd.id} id={fd.id}>
                    {(dragListeners) => (
                      <div className={`field-edit-wrapper-row${fd.system ? ' field-edit-system' : ''}`} data-testid={`${kind}-field-config-${fd.id}`}>
                        <button type="button" className="field-drag-handle" {...dragListeners}
                          title="Drag to reorder" data-testid={`${kind}-drag-field-${fd.id}`}>
                          <GripVertical size={14} />
                        </button>
                        <div className="field-edit-label">
                          <span className="field-config-type">{fd.type}</span>
                          <span className="field-config-name">{fd.label}{fd.required ? ' *' : ''}</span>
                        </div>
                        {fd.id !== 'name' && (
                          <div className="field-vis-row">
                            <span className="field-vis-label">Card</span>
                            <button
                              className={`field-vis-switch ${fd.showOnCard !== false ? 'field-vis-on' : 'field-vis-off'}`}
                              onClick={() => toggleMemberCardVis(fd.id)}
                              data-testid={`${kind}-card-vis-${fd.id}`}
                              title={fd.showOnCard !== false ? 'Shown on card' : 'Hidden from card'}
                              type="button"
                            >
                              <span className="field-vis-knob" />
                            </button>
                          </div>
                        )}
                        <div className="field-edit-actions">
                          <button className="field-arrow-btn" onClick={() => startMemberRename(fd)}
                            data-testid={`${kind}-rename-field-${fd.id}`} title="Rename" type="button">
                            <Edit2 size={12} />
                          </button>
                          {fd.id !== 'name' && (
                            <button className="icon-btn icon-btn-danger" style={{ padding: '2px' }}
                              onClick={() => removeCustomField(fd.id)}
                              data-testid={`${kind}-remove-field-${fd.id}`} type="button">
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        {renamingId === fd.id && (
                          <div className="field-rename-bar-inline">
                            <input className="field-rename-input" autoFocus value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onBlur={() => applyMemberRename(fd.id)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') applyMemberRename(fd.id);
                                if (e.key === 'Escape') setRenamingId(null);
                              }}
                              data-testid={`${kind}-rename-input-${fd.id}`} />
                          </div>
                        )}
                      </div>
                    )}
                  </SortableFieldItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <div className="field-add-section" data-testid={`${kind}-add-field-section`}>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add Custom Field</h4>
            <div className="field-add-row">
              <input className="form-input" placeholder="Field name..." value={newFieldLabel}
                onChange={e => setNewFieldLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustomField()}
                data-testid={`${kind}-new-field-label`} />
              <div className="select-wrap" style={{ flex: '0 0 150px' }}>
                <select className="form-input" value={newFieldType} onChange={e => setNewFieldType(e.target.value)}
                  data-testid={`${kind}-new-field-type`}>
                  {Object.entries(FIELD_TYPES).filter(([k]) => k !== 'color').map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <ChevronDown size={13} className="select-caret" />
              </div>
              <Btn size="sm" onClick={addCustomField} disabled={!newFieldLabel.trim()}
                data-testid={`${kind}-add-field-btn`}><Plus size={14} /> Add</Btn>
            </div>
          </div>
        </div>
      ) : (
        /* ===== EDIT MODE ===== */
        <div className="member-modal-body">
          <div className="job-form-grid" data-testid={`${kind}-form-fields`}>
            {memberFields.map(fd => {
              const isSystem = !!fd.system;
              const isName = fd.id === 'name';
              if (isLinkField(fd.type)) {
                const linkFieldMeta = {
                  project_link: { items: jobs, shape: 'dot', noun: 'project' },
                  client_link: { items: clients.filter(c => c.id !== member?.id), shape: 'avatar', noun: 'client' },
                }[fd.type] || { items: teamMembers, shape: 'avatar', noun: 'member' };
                const linkValue = isSystem ? (form[fd.id] || []) : (customValues[fd.id] || []);
                return (
                  <div key={fd.id} className="job-field-full" data-testid={`${kind}-form-field-${fd.id}`}>
                    <LinkPickerField label={fd.label} items={linkFieldMeta.items.map(i => ({ ...i, subtitle: i.role || i.client }))}
                      value={linkValue}
                      onChange={ids => isSystem ? set(fd.id, ids) : setCustomValues(prev => ({ ...prev, [fd.id]: ids }))}
                      shape={linkFieldMeta.shape}
                      noun={linkFieldMeta.noun}
                      testid={`${kind}-link-${fd.id}`} />
                  </div>
                );
              }
              const value = isSystem ? (form[fd.id] || '') : (customValues[fd.id] || '');
              const onChange = isSystem
                ? (e) => set(fd.id, e.target.value)
                : (e) => setCustomValues(prev => ({ ...prev, [fd.id]: e.target.value }));
              const inputType = FIELD_TYPE_TO_INPUT_TYPE[fd.type] || 'text';
              const placeholder = getMemberFieldPlaceholder(fd, isName, isClient);
              const testId = isSystem ? `${kind}-${fd.id}-input` : `${kind}-field-value-${fd.id}`;
              return (
                <div key={fd.id} className={`form-field${isName ? ' job-field-full' : ''}`}
                  data-testid={`${kind}-form-field-${fd.id}`}>
                  <label className="form-label">{fd.label}{fd.required ? ' *' : ''}</label>
                  <input type={inputType} className="form-input" value={value}
                    onChange={onChange} placeholder={placeholder} data-testid={testId} />
                </div>
              );
            })}
          </div>
          {/* Bottom Save (mirrors the top button for scroll-friendly QoL) */}
          <div className="modal-bottom-actions" data-testid={`${kind}-modal-bottom-actions`}>
            <Btn onClick={handleSave} disabled={saving} data-testid={`save-${kind}-btn-bottom`}>
              {saving ? 'Saving…' : (member ? 'Save Changes' : `Add ${noun}`)}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ============================================================
// SIDEBAR
// ============================================================
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', Icon: Home },
  { id: 'projects',    label: 'Projects',  Icon: Briefcase },
  { id: 'schedule',  label: 'Schedule',  Icon: Calendar },
  { id: 'team',      label: 'Team',      Icon: Users },
];

function Sidebar({ page, setPage, user, onLogout, collapsed, setCollapsed }) {
  const isAdmin = user?.is_admin;
  const { darkMode, toggleDarkMode } = useDarkMode();
  const logoUrl = user?.company_logo_url ? `${BACKEND_URL}${user.company_logo_url}` : null;
  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`} data-testid="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-brand">
          {!collapsed ? (
            <div className="brand-full">
              {logoUrl ? (
                <div className="brand-logo-wrap" data-testid="sidebar-workspace-logo">
                  <img src={logoUrl} alt={user?.company || 'Workspace'} className="brand-logo-img" />
                  {user?.company && <div className="brand-sub truncate">{user.company}</div>}
                </div>
              ) : (
                <div>
                  <div className="brand-name font-manrope">Projek<span className="brand-dot">.</span></div>
                  {user?.company && <div className="brand-sub truncate">{user.company}</div>}
                </div>
              )}
            </div>
          ) : null}
          <button className="sidebar-toggle" onClick={() => setCollapsed(c => !c)}
            data-testid="sidebar-toggle-btn">
            <Menu size={18} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ id, label, Icon }) => (
            <button key={id}
              className={`nav-item ${page === id ? 'nav-item-active' : ''}`}
              onClick={() => setPage(id)}
              data-testid={`nav-${id}`}>
              <Icon size={18} className="nav-icon" />
              {!collapsed && <span className="nav-label">{label}</span>}
            </button>
          ))}
          {isAdmin && (
            <button
              className={`nav-item nav-item-admin ${page === 'admin' ? 'nav-item-active' : ''}`}
              onClick={() => setPage('admin')}
              data-testid="nav-admin">
              <Shield size={18} className="nav-icon" />
              {!collapsed && <span className="nav-label">Admin</span>}
            </button>
          )}
          {isAdmin && (
            <button
              className={`nav-item nav-item-admin ${page === 'invite-codes' ? 'nav-item-active' : ''}`}
              onClick={() => setPage('invite-codes')}
              data-testid="nav-invite-codes">
              <Key size={18} className="nav-icon" />
              {!collapsed && <span className="nav-label">Invite Codes</span>}
            </button>
          )}
          {isAdmin && (
            <button
              className={`nav-item nav-item-admin ${page === 'team-leaders' ? 'nav-item-active' : ''}`}
              onClick={() => setPage('team-leaders')}
              data-testid="nav-team-leaders">
              <UserCheck size={18} className="nav-icon" />
              {!collapsed && <span className="nav-label">Team Leaders</span>}
            </button>
          )}
        </nav>
      </div>
      <div className="sidebar-bottom">
        <button className="sidebar-user" onClick={() => setPage('settings')} data-testid="sidebar-user" title="Go to Profile">
          <Avatar name={user?.display_name || user?.username} photo={user?.photo_url} size={32} color="hsl(var(--primary))" />
          {!collapsed && (
            <div className="sidebar-user-info truncate">
              <div className="user-name truncate">{user?.display_name || user?.username}</div>
              <div className="user-email truncate" style={{ fontSize: 11, color: 'hsl(var(--sidebar-fg) / 0.55)' }}>{user?.email}</div>
            </div>
          )}
        </button>
        {['owner', 'manager'].includes(user?.workspace_role) && <button
          className={`nav-item ${page === 'clients' ? 'nav-item-active' : ''}`}
          onClick={() => setPage('clients')} data-testid="nav-clients" title="Clients">
          <UserSquare size={18} className="nav-icon" />
          {!collapsed && <span className="nav-label">Clients</span>}
        </button>}
        <button
          className={`nav-item ${page === 'archive' ? 'nav-item-active' : ''}`}
          onClick={() => setPage('archive')}
          data-testid="nav-archive">
          <Archive size={18} className="nav-icon" />
          {!collapsed && <span className="nav-label">Archive</span>}
        </button>
        {/* dark mode toggle removed from sidebar — now in Settings > Themes */}
        <button
          className={`nav-item ${page === 'settings' ? 'nav-item-active' : ''}`}
          onClick={() => setPage('settings')}
          data-testid="nav-settings">
          <Settings size={18} className="nav-icon" />
          {!collapsed && <span className="nav-label">Settings</span>}
        </button>
        <button className={`nav-item nav-logout ${collapsed ? 'justify-center' : ''}`}
          onClick={onLogout} data-testid="logout-btn">
          <LogOut size={18} />
          {!collapsed && <span>Log out</span>}
        </button>
      </div>
    </aside>
  );
}

// ============================================================
// SHARE JOB MODAL
// ============================================================
function ShareJobModal({ job, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!job) return null;
  const link = `${window.location.origin}/?job=${job.id}`;
  const printLink = `${link}&print=1`;
  const copyLink = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handlePrint = () => {
    const win = window.open(printLink, '_blank');
    if (!win) window.open(link, '_blank');
  };
  return (
    <Modal open onClose={onClose} title="Share Project" width="max-w-md">
      <div className="share-modal-body" data-testid="share-job-modal">
        <p className="share-modal-desc">Share this job with anyone — no login required.</p>
        <div className="share-link-box">
          <div className="share-link-row">
            <span className="share-link-url" data-testid="share-job-link">{link}</span>
          </div>
        </div>
        <div className="share-modal-actions">
          <Btn onClick={copyLink} data-testid="share-job-copy-btn">
            {copied ? <Check size={15}/> : <Copy size={15}/>}
            {copied ? 'Copied!' : 'Copy Link'}
          </Btn>
          <Btn variant="outline" onClick={() => window.open(link, '_blank')}
            data-testid="share-job-open-btn">
            <ExternalLink size={15}/> Open
          </Btn>
          <Btn variant="outline" onClick={handlePrint}
            data-testid="share-job-print-btn">
            <Printer size={15}/> Print as PDF
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// SHARE MEMBER MODAL
// ============================================================
function ShareMemberModal({ member, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!member) return null;
  const link = `${window.location.origin}/?member=${member.id}`;
  const copyLink = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal open onClose={onClose} title="Share Profile" width="max-w-md">
      <div className="share-modal-body" data-testid="share-member-modal">
        <div className="share-member-preview">
          <Avatar name={member.name} photo={member.photo_url} color={member.color} size={48} />
          <div>
            <div className="share-member-name font-manrope">{member.name}</div>
            {member.role && <div className="share-member-role">{member.role}</div>}
          </div>
        </div>
        <div className="share-link-box">
          <div className="share-link-row">
            <span className="share-link-url" data-testid="share-member-link">{link}</span>
          </div>
        </div>
        <div className="share-modal-actions">
          <Btn onClick={copyLink} data-testid="share-member-copy-btn">
            {copied ? <Check size={15}/> : <Copy size={15}/>}
            {copied ? 'Copied!' : 'Copy Link'}
          </Btn>
          <Btn variant="outline" onClick={() => window.open(link, '_blank')}
            data-testid="share-member-open-btn">
            <ArrowRight size={15}/> View Profile
          </Btn>
        </div>
      </div>
    </Modal>
  );
}


function CompanyProfilePage({ username }) {
  const [stage, setStage] = useState('loading'); // loading | locked | unlocked | notfound
  const [publicInfo, setPublicInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [checking, setChecking] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const unlockProfile = useCallback(async (pw) => {
    try {
      const data = await apiFetch(`/public/company/${username}/access`, {
        method: 'POST', body: JSON.stringify({ password: pw }),
      });
      setProfile(data);
      setStage('unlocked');
    } catch (e) {
      setPwError(e.message || 'Wrong access code');
      setStage('locked');
    }
  }, [username]);

  useEffect(() => {
    apiFetch(`/public/company/${username}`)
      .then(info => {
        setPublicInfo(info);
        setStage(info.has_password ? 'locked' : 'loading_profile');
        if (!info.has_password) unlockProfile('');
      })
      .catch(() => setStage('notfound'));
  }, [username, unlockProfile]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setChecking(true); setPwError('');
    await unlockProfile(password);
    setChecking(false);
  };

  if (stage === 'loading' || stage === 'loading_profile') return (
    <div className="loading-screen"><div className="loading-spinner"/></div>
  );

  if (stage === 'notfound') return (
    <div className="cp-shell">
      <div className="cp-notfound">
        <Building size={40}/>
        <h2 className="font-manrope">Workspace not found</h2>
        <p>The workspace <strong>{username}</strong> doesn't exist on Projek<span className="brand-dot">.</span></p>
        <a href="/" className="btn btn-primary" style={{marginTop:16,textDecoration:'none'}}>Go to Projek<span className="brand-dot">.</span></a>
      </div>
    </div>
  );

  if (stage === 'locked') return (
    <div className="cp-shell" data-testid="company-locked">
      <div className="cp-lock-card">
        <div className="cp-lock-icon"><Lock size={28}/></div>
        <h2 className="font-manrope cp-lock-title">{publicInfo?.company || username}</h2>
        <p className="cp-lock-sub">This workspace profile is password protected.</p>
        {pwError && <div className="alert-error mb-4" data-testid="cp-pw-error">{pwError}</div>}
        <form onSubmit={handleUnlock} className="cp-lock-form">
          <div className="form-field">
            <label className="form-label">Access Code</label>
            <div className="address-row">
              <input type={showPw ? 'text' : 'password'} className="form-input"
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Enter access code…" autoFocus required data-testid="cp-password-input"/>
              <button type="button" className="icon-btn" onClick={() => setShowPw(s=>!s)}>
                {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>
          <Btn type="submit" disabled={checking || !password} className="w-full mt-2" data-testid="cp-unlock-btn">
            {checking ? 'Checking…' : 'View Profile'}
          </Btn>
        </form>
        <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginTop:16,textAlign:'center'}}>
          Powered by <a href="/" style={{color:'hsl(var(--primary))',fontWeight:700}}>Projek<span className="brand-dot">.</span></a>
        </p>
      </div>
    </div>
  );

  // Unlocked profile — now we have full data
  return (
    <GuestCompanyView
      data={profile}
      password={password}
      onBack={() => { window.location.href = '/'; }}
      isUrl
    />
  );
}

// ============================================================
// GUEST COMPANY VIEW (read-only full app for company code viewers)
// ============================================================
function GuestCompanyView({ data, password, onBack }) {
  const [activeTab, setActiveTab] = useState('projects');
  const [teamSearch, setTeamSearch] = useState('');
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [localTasks, setLocalTasks] = useState(data.tasks || []);
  const [guestCollapsed, setGuestCollapsed] = useState(false);

  // Swap browser tab icon to this workspace's custom app icon; restore default on unmount
  useEffect(() => {
    applyFavicon(data?.workspace_icon_url);
    return () => applyFavicon(null);
  }, [data?.workspace_icon_url]);

  const guestEditsAllowed = data.guest_edits_allowed || false;

  const editTask = async (id, updates) => {
    if (!guestEditsAllowed || !updates.status) return;
    try {
      const updated = await apiFetch(
        `/public/company/${data.username}/task/${id}/status`,
        { method: 'PATCH', body: JSON.stringify({ password: password || '', status: updates.status }) }
      );
      setLocalTasks(prev => prev.map(t => t.id === id ? updated : t));
    } catch (e) { console.error('Task status update failed:', e); }
  };

  const readOnlyData = {
    jobs: data.jobs || [],
    members: data.members || [],
    tasks: localTasks,
    isReadOnly: true,
    canChangeTaskStatus: guestEditsAllowed,
    addJob: async () => {}, editJob: async () => {}, archiveJob: async () => {},
    addMember: async () => {}, editMember: async () => {}, archiveMember: async () => {},
    addTask: async () => {}, editTask, removeTask: async () => {},
    goToTeamMember: (name) => { setTeamSearch(name); setActiveTab('team'); },
    teamSearchQuery: teamSearch,
    setTeamSearchQuery: setTeamSearch,
    goToJobPunchList: (id) => {
      setExpandedJobId(id);
      setActiveTab('projects');
      setTimeout(() => {
        const el = document.getElementById(`job-card-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    },
    expandedPunchListJobId: expandedJobId,
    setExpandedPunchListJobId: setExpandedJobId,
    goToPage: (pg) => setActiveTab(pg),
    fieldSchema: data.field_template_fields || [],
  };

  const jobs = data.jobs || [];
  const members = data.members || [];
  const total = jobs.length;
  const active = jobs.filter(j => j.status === 'active').length;

  const guestNavTabs = [
    { id: 'dashboard', label: 'Dashboard', Icon: Home },
    { id: 'projects', label: 'Projects', Icon: Briefcase },
    { id: 'schedule', label: 'Schedule', Icon: Calendar },
    { id: 'team', label: 'Team', Icon: Users },
  ];

  return (
    <div className="app-shell guest-view-shell" data-testid="guest-company-view">
      <div className="app-body">
        {/* Guest Sidebar — same style as manager sidebar, read-only */}
        <aside className={`sidebar ${guestCollapsed ? 'sidebar-collapsed' : ''}`} data-testid="guest-sidebar">
          <div className="sidebar-top">
            <div className="sidebar-brand">
              {!guestCollapsed && (
                <div className="brand-full">
                  {data.company_logo_url ? (
                    <div className="brand-logo-wrap" data-testid="guest-sidebar-logo">
                      <img src={`${BACKEND_URL}${data.company_logo_url}`} alt={data.company || 'Workspace'} className="brand-logo-img" />
                      <div className="brand-sub truncate" data-testid="guest-workspace-name" title={data.company || data.username}>{data.company || data.username}</div>
                    </div>
                  ) : (
                    <div>
                      <div className="brand-name font-manrope">{data.company}<span className="brand-dot">.</span></div>
                      <div className="brand-sub truncate" data-testid="guest-workspace-name" title={data.company || data.username}>{data.company || data.username}</div>
                    </div>
                  )}
                </div>
              )}
              <button className="sidebar-toggle" onClick={() => setGuestCollapsed(c => !c)}
                data-testid="guest-sidebar-toggle">
                <Menu size={18} />
              </button>
            </div>
            <nav className="sidebar-nav">
              {guestNavTabs.map(({ id, label, Icon }) => (
                <button key={id}
                  className={`nav-item ${activeTab === id ? 'nav-item-active' : ''}`}
                  onClick={() => setActiveTab(id)}
                  data-testid={`guest-tab-${id}`}>
                  <Icon size={18} className="nav-icon" />
                  {!guestCollapsed && <span className="nav-label">{label}</span>}
                </button>
              ))}
            </nav>
          </div>
          <div className="sidebar-bottom">
            <button className="nav-item nav-logout" onClick={onBack}
              data-testid="guest-view-exit-btn" title="Exit workspace" aria-label="Exit workspace">
              <LogOut size={18} />
              {!guestCollapsed && <span>Exit</span>}
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className={`main-area ${guestCollapsed ? 'main-collapsed' : ''}`}>
          <DataCtx.Provider value={readOnlyData}>
            {activeTab === 'dashboard' && <Dashboard workspace={data} />}
            {activeTab === 'projects' && <ProjectsPage />}
            {activeTab === 'schedule' && <SchedulePage />}
            {activeTab === 'team' && <TeamPage />}
          </DataCtx.Provider>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <div className="mobile-nav" data-testid="guest-mobile-nav">
        {guestNavTabs.map(({ id, label, Icon }) => (
          <button key={id}
            className={`mobile-nav-item ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
            data-testid={`guest-mobile-nav-${id}`}>
            <Icon size={20} /><span>{label}</span>
          </button>
        ))}
        <button className="mobile-nav-item" onClick={onBack} data-testid="guest-mobile-exit" aria-label="Exit workspace">
          <LogOut size={20} /><span>Exit</span>
        </button>
      </div>
    </div>
  );
}

// ============================================================
// GUEST SAVE MODAL — converts guest to real account
// ============================================================
function GuestSaveModal({ onSave, onClose }) {
  const [form, setForm] = useState({ username: '', email: '', password: '', inviteCode: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.email.trim() || form.password.length < 6 || !/\d/.test(form.password)) {
      setErr('All fields required. Password must be at least 6 characters with 1 number.');
      return;
    }
    if (!form.inviteCode.trim()) { setErr('Invite code required.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave(form);
    } catch (e) {
      setErr(e.message || 'Could not save workspace. Try again.');
      setSaving(false);
    }
  };

  return (
    <Modal open title="Save Your Workspace" onClose={onClose} data-testid="guest-save-modal">
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Create a free account to keep your workspace and all your data permanently.
        Your workspace will be linked to your new account automatically.
      </p>
      {err && <div className="alert-error mb-4" data-testid="guest-save-error">{err}</div>}
      <form onSubmit={handleSubmit} className="auth-form">
        <Input label="Username" value={form.username} onChange={e => set('username', e.target.value)}
          placeholder="johndoe" required data-testid="guest-save-username" />
        <Input label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)}
          placeholder="you@company.com" required data-testid="guest-save-email" />
        <PasswordInput label="Password" value={form.password} onChange={e => set('password', e.target.value)}
          placeholder="Min 8 characters" required data-testid="guest-save-password" />
        <Input label="Invite Code" value={form.inviteCode} onChange={e => set('inviteCode', e.target.value)}
          placeholder="Enter your invite code" required data-testid="guest-save-invite-code" />
        <div className="modal-footer" style={{ paddingTop: 8 }}>
          <Btn variant="outline" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving} data-testid="guest-save-submit">
            {saving ? 'Saving...' : 'Create Account & Save'}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================
// LANDING PAGE
// ============================================================

function LandingPage({ onAuth }) {
  const [view, setView] = useState('home');
  const [form, setForm] = useState({ email: '', password: '', username: '', company: '' });
  const [guestCompany, setGuestCompany] = useState('');
  const [companyPassword, setCompanyPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [err, setErr] = useState('');
  const [guestErr, setGuestErr] = useState('');
  const [accessErr, setAccessErr] = useState('');
  const [companyProfile, setCompanyProfile] = useState(null);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetErr, setResetErr] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Full-screen invite-code gate — blocks ANY account creation until a valid
  // code is entered. 'signup' or 'guest' tells us where to go once verified.
  const [inviteGateFor, setInviteGateFor] = useState(null);
  const [verifiedCode, setVerifiedCode] = useState('');
  const [gateCode, setGateCode] = useState('');
  const [gateErr, setGateErr] = useState('');
  const [gateLoading, setGateLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openInviteGate = (forWhat) => {
    if (verifiedCode) { if (forWhat === 'signup') setView('signup'); return; }
    setGateCode(''); setGateErr(''); setInviteGateFor(forWhat); setView('invite-gate');
  };

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    if (!gateCode.trim()) { setGateErr('Invalid code'); return; }
    setGateLoading(true); setGateErr('');
    try {
      await apiFetch('/invite-codes/check', { method: 'POST', body: JSON.stringify({ code: gateCode.trim() }) });
      const code = gateCode.trim();
      setVerifiedCode(code);
      if (inviteGateFor === 'google') {
        sessionStorage.setItem('hm_pending_invite_code', code);
        handleGoogleAuth();
      } else {
        setView('signup');
      }
    } catch (e) { setGateErr(e.message); }
    finally { setGateLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      let data;
      if (view === 'signin') {
        data = await apiFetch('/auth/signin', { method: 'POST', body: JSON.stringify({ email: form.email, password: form.password }) });
      } else {
        if (!form.username.trim() || !form.company.trim()) { setErr('All fields required'); setLoading(false); return; }
        if (!verifiedCode) { setErr('Invalid code'); setLoading(false); return; }
        if (form.password.length < 6 || !/\d/.test(form.password)) {
          setErr('Password must be at least 6 characters and contain at least 1 number.');
          setLoading(false); return;
        }
        data = await apiFetch('/auth/signup', { method: 'POST', body: JSON.stringify({ ...form, invite_code: verifiedCode }) });
      }
      onAuth(data.token, data.user);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  // Trial workspaces are free to create — no invite code needed to "try".
  // Saving/upgrading the trial into a permanent account later goes through
  // the normal Sign Up flow (and its invite-code gate) instead.
  const handleGuestCreate = async () => {
    if (!guestCompany.trim()) { setGuestErr('Enter a workspace name'); return; }
    setGuestLoading(true); setGuestErr('');
    try {
      const data = await apiFetch('/auth/guest', { method: 'POST', body: JSON.stringify({ company: guestCompany.trim() }) });
      onAuth(data.token, data.user);
    } catch (e) { setGuestErr(e.message); }
    finally { setGuestLoading(false); }
  };

  const handleCompanyAccess = async (e) => {
    e.preventDefault();
    if (!companyPassword.trim()) { setAccessErr('Enter a workspace code'); return; }
    setAccessLoading(true); setAccessErr('');
    try {
      const code = companyPassword.trim();
      // A Workspace Manager Access Code signs the person straight into the
      // workspace as a manager (real session, full edit, no account needed).
      try {
        const mgr = await apiFetch('/public/manager-access', { method: 'POST', body: JSON.stringify({ password: code }) });
        if (mgr?.token) { localStorage.removeItem('hm_company_code'); onAuth(mgr.token, mgr.user); return; }
      } catch (_) { /* not a manager code — fall back to read-only workspace view */ }
      const data = await apiFetch('/public/access-by-password', { method: 'POST', body: JSON.stringify({ password: code }) });
      setCompanyProfile(data);
      setView('company-profile');
      localStorage.setItem('hm_company_code', code);
    } catch (e) { setAccessErr(e.message); }
    finally { setAccessLoading(false); }
  };

  // Stay signed into a workspace-code view across browser/tab close — employees
  // shouldn't have to re-enter the code every time they reopen the app.
  useEffect(() => {
    const saved = localStorage.getItem('hm_company_code');
    if (!saved) return;
    setCompanyPassword(saved);
    apiFetch('/public/access-by-password', { method: 'POST', body: JSON.stringify({ password: saved }) })
      .then(data => { setCompanyProfile(data); setView('company-profile'); })
      .catch(() => localStorage.removeItem('hm_company_code'));
  }, []); // eslint-disable-line

  const handleForgotSend = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) { setResetErr('Enter your email'); return; }
    setResetLoading(true); setResetErr('');
    try {
      await apiFetch('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email: resetEmail.trim() }) });
      setView('reset');
    } catch (e) { setResetErr(e.message); }
    finally { setResetLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode.trim() || !newPassword.trim()) { setResetErr('Fill in all fields'); return; }
    setResetLoading(true); setResetErr('');
    try {
      await apiFetch('/auth/reset-password', { method: 'POST', body: JSON.stringify({ email: resetEmail.trim(), code: resetCode.trim(), new_password: newPassword }) });
      setResetSuccess('Password reset! You can now sign in.');
      setTimeout(() => { setView('signin'); setResetSuccess(''); setResetEmail(''); setResetCode(''); setNewPassword(''); }, 2000);
    } catch (e) { setResetErr(e.message); }
    finally { setResetLoading(false); }
  };

  const leftPanel = (
    <div className="landing-left">
      <h1 className="landing-title font-manrope" data-testid="landing-title" style={{ marginBottom: 20 }}>Projek<span className="brand-dot">.</span></h1>
      <p className="landing-tagline">Fewer calls. Fewer mix-ups. More time.</p>
      <p className="landing-desc">Projek gives small trades businesses instant access to project info, schedules, team contacts, and clients, so the day runs smoother.</p>
      <ul className="landing-features">
        <li>Answers to "what's the address?" in seconds</li>
        <li>A team that always know what to do and when to do it</li>
        <li>Contacts info that never get lost</li>
        <li>A Private client directory</li>
      </ul>
      <p className="landing-cta">Take your time back.</p>
    </div>
  );

  if (view === 'company-profile' && companyProfile) {
    return (
      <GuestCompanyView
        data={companyProfile}
        password={companyPassword}
        onBack={() => { setView('home'); setCompanyProfile(null); setCompanyPassword(''); localStorage.removeItem('hm_company_code'); }}
      />
    );
  }

  if (view === 'company-code') {
    return (
      <div className="landing landing--auth" data-testid="landing-page">
        {leftPanel}
        <div className="landing-right">
          <div className="landing-right-inner">
            <button className="landing-back" onClick={() => { setView('home'); setAccessErr(''); }} data-testid="code-back-btn"><ChevronLeft size={16} /> Back</button>
            <div className="auth-card" data-testid="company-code-card">
              <h3 className="auth-card-title font-manrope">Workspace Code</h3>
              <p className="auth-card-desc">Enter the code to view a workspace profile.</p>
              {accessErr && <div className="alert-error mb-4" data-testid="access-error">{accessErr}</div>}
              <form onSubmit={handleCompanyAccess} className="auth-form">
                <Input label="Access Code" value={companyPassword} onChange={e => setCompanyPassword(e.target.value)} placeholder="Enter access code" data-testid="company-password-input" />
                <Btn type="submit" disabled={accessLoading} className="w-full mt-2" data-testid="company-access-btn">
                  {accessLoading ? 'Accessing...' : 'View Workspace'}
                </Btn>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (view === 'invite-gate') {
    return (
      <div className="landing landing--auth" data-testid="invite-gate-page">
        {leftPanel}
        <div className="landing-right">
          <div className="landing-right-inner">
            <button className="landing-back" onClick={() => { setView('home'); setInviteGateFor(null); setGateErr(''); }} data-testid="invite-gate-back-btn">
              <ChevronLeft size={16} /> Back
            </button>
            <div className="auth-card" data-testid="invite-gate-card">
              <h2 className="auth-card-heading font-manrope">Enter Invite Code</h2>
              <p className="auth-card-desc" style={{ marginBottom: 16 }}>You need an invite code to create a Projek account.</p>
              {gateErr && <div className="alert-error mb-4" data-testid="invite-gate-error">{gateErr}</div>}
              <form onSubmit={handleGateSubmit} className="auth-form">
                <Input label="Invite Code" value={gateCode} onChange={e => setGateCode(e.target.value)}
                  placeholder="Enter your invite code" required data-testid="invite-gate-code-input" />
                <Btn type="submit" disabled={gateLoading} className="w-full mt-2" data-testid="invite-gate-submit-btn">
                  {gateLoading ? 'Checking…' : 'Continue'}
                </Btn>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'forgot' || view === 'reset') {
    return (
      <div className="landing landing--auth" data-testid="landing-page">
        {leftPanel}
        <div className="landing-right">
          <div className="landing-right-inner">
            <button className="landing-back" onClick={() => { setView('signin'); setResetErr(''); setResetSuccess(''); }} data-testid="forgot-back-btn">
              <ChevronLeft size={16} /> Back to Sign In
            </button>
            <div className="auth-card" data-testid="forgot-card">
              {view === 'forgot' ? (
                <>
                  <h2 className="auth-card-heading font-manrope">Forgot Password</h2>
                  <p className="auth-card-desc" style={{ marginBottom: 16 }}>Enter your email and we'll send a 6-digit reset code.</p>
                  {resetErr && <div className="alert-error mb-4" data-testid="reset-error">{resetErr}</div>}
                  <form onSubmit={handleForgotSend} className="auth-form">
                    <Input label="Email" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                      placeholder="you@company.com" required data-testid="forgot-email-input" />
                    <Btn type="submit" disabled={resetLoading} className="w-full mt-2" data-testid="forgot-send-btn">
                      {resetLoading ? 'Sending...' : 'Send Reset Code'}
                    </Btn>
                  </form>
                  <button className="auth-workspace-code-link" style={{ marginTop: 12 }}
                    onClick={() => setView('reset')} data-testid="already-have-code-btn">
                    Already have a code?
                  </button>
                </>
              ) : (
                <>
                  <h2 className="auth-card-heading font-manrope">Reset Password</h2>
                  <p className="auth-card-desc" style={{ marginBottom: 16 }}>Enter the 6-digit code sent to <strong>{resetEmail || 'your email'}</strong> and choose a new password.</p>
                  {resetErr && <div className="alert-error mb-4" data-testid="reset-error">{resetErr}</div>}
                  {resetSuccess && <div className="alert-success mb-4" data-testid="reset-success">{resetSuccess}</div>}
                  <form onSubmit={handleResetPassword} className="auth-form">
                    {!resetEmail && (
                      <Input label="Email" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                        placeholder="you@company.com" required data-testid="reset-email-input" />
                    )}
                    <Input label="6-Digit Code" value={resetCode} onChange={e => setResetCode(e.target.value)}
                      placeholder="123456" maxLength={6} required data-testid="reset-code-input" />
                    <PasswordInput label="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      placeholder="Min 6 chars, 1 number" required data-testid="reset-new-password-input" />
                    <Btn type="submit" disabled={resetLoading} className="w-full mt-2" data-testid="reset-submit-btn">
                      {resetLoading ? 'Resetting...' : 'Reset Password'}
                    </Btn>
                  </form>
                  <button className="auth-workspace-code-link" style={{ marginTop: 12 }}
                    onClick={() => { setView('forgot'); setResetErr(''); }} data-testid="resend-code-btn">
                    Resend code
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'signin' || view === 'signup') {
    return (
      <div className="landing landing--auth" data-testid="landing-page">
        {leftPanel}
        <div className="landing-right">
          <div className="landing-right-inner">
            <button className="landing-back" onClick={() => setView('home')} data-testid="auth-back-btn"><ChevronLeft size={16} /> Back</button>
            <div className="auth-card" data-testid="auth-card">
              <h2 className="auth-card-heading font-manrope" data-testid="auth-heading">
                {view === 'signin' ? 'Sign In' : 'Create Account'}
              </h2>
              <div className="auth-tabs">
                <button className={`auth-tab ${view === 'signin' ? 'active' : ''}`} onClick={() => { setView('signin'); setErr(''); setShowCodeInput(false); }} data-testid="signin-tab">Sign In</button>
                <button className={`auth-tab ${view === 'signup' ? 'active' : ''}`} onClick={() => { setErr(''); setShowCodeInput(false); openInviteGate('signup'); }} data-testid="signup-tab">Sign Up</button>
              </div>
              <button className="google-auth-btn" onClick={() => {
                if (view === 'signup' && !verifiedCode) { openInviteGate('google'); return; }
                if (view === 'signup') { sessionStorage.setItem('hm_pending_invite_code', verifiedCode); }
                handleGoogleAuth();
              }} data-testid="google-auth-btn">
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {view === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}
              </button>
              <div className="auth-divider"><span>or</span></div>
              {err && <div className="alert-error mb-4" data-testid="auth-error">{err}</div>}
              <form onSubmit={handleSubmit} className="auth-form">
                {view === 'signup' && (
                  <>
                    <Input label="Username" value={form.username} onChange={e => set('username', e.target.value)} placeholder="johndoe" required data-testid="signup-username" />
                    <Input label="Workspace Name" value={form.company} onChange={e => set('company', e.target.value)} placeholder="Acme Repairs LLC" required data-testid="signup-company" />
                  </>
                )}
                <Input label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@company.com" required data-testid="auth-email" />
                <PasswordInput label="Password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={view === 'signup' ? 'Min 6 chars, 1 number' : '--------'} required data-testid="auth-password" />
                <Btn type="submit" disabled={loading} className="w-full mt-2" data-testid="auth-submit-btn">
                  {loading ? 'Please wait...' : (view === 'signin' ? 'Sign In' : 'Create Account')}
                </Btn>
              </form>
              <button
                className="auth-workspace-code-link"
                onClick={() => { setShowCodeInput(v => !v); setAccessErr(''); }}
                data-testid="auth-workspace-code-link"
              >
                <Key size={14} /> Have a workspace code?
                <ChevronDown size={13} style={{ marginLeft: 4, transition: 'transform 0.2s', transform: showCodeInput ? 'rotate(180deg)' : 'none' }} />
              </button>
              {showCodeInput && (
                <div className="auth-code-dropdown" data-testid="auth-code-dropdown">
                  {accessErr && <div className="alert-error mb-3" data-testid="access-error">{accessErr}</div>}
                  <form onSubmit={handleCompanyAccess} className="auth-form">
                    <Input label="Access Code" value={companyPassword} onChange={e => setCompanyPassword(e.target.value)}
                      placeholder="Enter workspace code" data-testid="company-password-input" autoFocus />
                    <Btn type="submit" disabled={accessLoading} className="w-full mt-1" data-testid="company-access-btn">
                      {accessLoading ? 'Accessing...' : 'View Workspace'}
                    </Btn>
                  </form>
                </div>
              )}
              {view === 'signin' && (
                <button className="auth-workspace-code-link" style={{ marginTop: 8 }}
                  onClick={() => { setView('forgot'); setResetErr(''); setResetEmail(form.email || ''); }}
                  data-testid="forgot-password-link">
                  Forgot password?
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'company-code') {
    return (
      <div className="landing landing--auth" data-testid="landing-page">
        {leftPanel}
        <div className="landing-right">
          <div className="landing-right-inner">
            <button className="landing-back" onClick={() => { setView('home'); setAccessErr(''); }} data-testid="code-back-btn"><ChevronLeft size={16} /> Back</button>
            <div className="auth-card" data-testid="company-code-card">
              <h3 className="auth-card-title font-manrope">Workspace Code</h3>
              <p className="auth-card-desc">Enter the code to view a workspace profile.</p>
              {accessErr && <div className="alert-error mb-4" data-testid="access-error">{accessErr}</div>}
              <form onSubmit={handleCompanyAccess} className="auth-form">
                <Input label="Access Code" value={companyPassword} onChange={e => setCompanyPassword(e.target.value)} placeholder="Enter access code" data-testid="company-password-input" />
                <Btn type="submit" disabled={accessLoading} className="w-full mt-2" data-testid="company-access-btn">
                  {accessLoading ? 'Accessing...' : 'View Workspace'}
                </Btn>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing" data-testid="landing-page">
      {leftPanel}
      <div className="landing-right">
        <div className="landing-right-inner">
          {guestErr && <div className="alert-error mb-2" data-testid="guest-error">{guestErr}</div>}
          <input className="landing-try-input" value={guestCompany} onChange={e => setGuestCompany(e.target.value)} placeholder="Your workspace name" data-testid="guest-company-input" />
          <div className="landing-buttons landing-buttons--stacked" data-testid="landing-actions">
            <button className="landing-btn-primary" onClick={handleGuestCreate} disabled={guestLoading} data-testid="guest-create-btn">
              {guestLoading ? 'Creating...' : 'Try for Free'}
            </button>
            <button className="landing-btn-primary" onClick={() => openInviteGate('signup')} data-testid="landing-signup-btn">Sign up</button>
            <button className="landing-btn-primary" onClick={() => setView('signin')} data-testid="landing-signin-btn">Sign in</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function Dashboard({ workspace }) {
  const { user } = useAuth();
  const { jobs, members, tasks, goToTeamMember, goToProjectSearch, goToPage } = useData();
  const today = todayStr();

  const recentTasks = useMemo(() => [...tasks]
    .filter(t => t.status !== 'done')
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .slice(0, 8), [tasks]);

  const recentJobs = useMemo(() => [...jobs].sort((a, b) =>
    (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 8), [jobs]);

  const upcomingJobs = useMemo(() => jobs
    .filter(j => j.install_start && j.install_start >= today)
    .sort((a, b) => a.install_start.localeCompare(b.install_start))
    .slice(0, 6), [jobs, today]);

  return (
    <div className="page-content dashboard-page-fill" data-testid="dashboard-page">
      <div className="page-header dash-header-row">
        <MobileWorkspaceBrand workspace={workspace || user} />
        <div>
          <h2 className="page-title font-manrope" data-testid="dashboard-heading">Dashboard</h2>
          <p className="page-sub" data-testid="dashboard-welcome">Welcome back, here's what's happening today.</p>
        </div>
      </div>

      <div className="dashboard-grid" data-testid="dashboard-grid">
        {/* To Do List Activity */}
        <div className="dash-card" data-testid="dashboard-tasks-panel">
          <button className="dash-card-title-btn" onClick={() => goToPage('projects')} data-testid="dash-punchlist-header">
            <h3 className="dash-card-title font-manrope">To Do List</h3>
            <ChevronRight size={14} className="dash-header-arrow" />
          </button>
          {recentTasks.length === 0 ? (
            <EmptyState icon={CheckCircle} title="No tasks yet" desc="Add tasks to your job to-do lists to track progress." />
          ) : (
            <DashboardPreview className="job-list-mini" testId="dashboard-tasks-preview">
              {recentTasks.map(t => {
                const parentJob = jobs.find(j => j.id === t.project_id);
                const statusMeta = PUNCH_COLS.find(c => c.id === t.status) || PUNCH_COLS[0];
                const statusLabel = statusMeta.label;
                const statusColor = statusMeta.color;
                return (
                  <button key={t.id} className="job-row-mini dash-task-row"
                    onClick={() => parentJob && goToProjectSearch(parentJob.name)}
                    data-testid={`dash-task-${t.id}`}>
                    <div className="job-color-dot" style={{ background: parentJob?.color || '#6B7280' }} />
                    <div className="job-info-mini" style={{ flex: 1 }}>
                      <div className="job-name-mini">{t.title}</div>
                      <div className="job-client-mini">{parentJob?.name || 'Unknown job'}</div>
                    </div>
                    <span className="dash-task-status" style={{ color: statusColor }}>{statusLabel}</span>
                  </button>
                );
              })}
            </DashboardPreview>
          )}
        </div>

        {/* Upcoming Schedule */}
        <div className="dash-card" data-testid="dashboard-schedule-panel">
          <button className="dash-card-title-btn" onClick={() => goToPage('schedule')} data-testid="dash-schedule-header">
            <h3 className="dash-card-title font-manrope">Upcoming Schedule</h3>
            <ChevronRight size={14} className="dash-header-arrow" />
          </button>
          {upcomingJobs.length === 0 ? (
            <EmptyState icon={Calendar} title="Nothing scheduled" desc="Assign start dates to jobs to see them here." />
          ) : (
            <DashboardPreview className="job-list-mini" testId="dashboard-schedule-preview">
              {upcomingJobs.map(j => {
                const assigned = members.filter(m => (j.member_ids||[]).includes(m.id)).slice(0,3);
                return (
                  <button key={j.id} className="job-row-mini"
                    onClick={() => goToProjectSearch(j.name)}
                    data-testid={`upcoming-job-${j.id}`}>
                    <div className="job-color-dot" style={{ background: j.color }} />
                    <div className="job-info-mini">
                      <div className="job-name-mini">{j.name}</div>
                      <div className="job-client-mini">{j.client}</div>
                    </div>
                    {assigned.length > 0 && (
                      <div className="mini-avatar-stack">
                        {assigned.map(m => <Avatar key={m.id} name={m.name} photo={m.photo_url} color={m.color} size={22} />)}
                      </div>
                    )}
                    <span className="job-date-mini">{fmtShort(j.install_start)}</span>
                  </button>
                );
              })}
            </DashboardPreview>
          )}
        </div>

        {/* Recent Jobs */}
        <div className="dash-card" data-testid="dashboard-projects-panel">
          <button className="dash-card-title-btn" onClick={() => goToPage('projects')} data-testid="dash-jobs-header">
            <h3 className="dash-card-title font-manrope">Active Projects</h3>
            <ChevronRight size={14} className="dash-header-arrow" />
          </button>
          {recentJobs.length === 0 ? (
            <EmptyState icon={Briefcase} title="No projects yet" desc="Create your first project to get started." />
          ) : (
            <DashboardPreview className="dash-jobs-2col" testId="dashboard-projects-preview">
              {recentJobs.map(j => {
                const assigned = members.filter(m => (j.member_ids||[]).includes(m.id)).slice(0,2);
                return (
                  <button key={j.id} className="job-row-mini"
                    onClick={() => goToProjectSearch(j.name)}
                    data-testid={`recent-job-${j.id}`}>
                    <div className="job-color-dot" style={{ background: j.color }} />
                    <div className="job-info-mini">
                      <div className="job-name-mini">{j.name}</div>
                      <div className="job-client-mini">{j.client}</div>
                    </div>
                    {assigned.length > 0 && (
                      <div className="mini-avatar-stack">
                        {assigned.map(m => <Avatar key={m.id} name={m.name} photo={m.photo_url} color={m.color} size={20} />)}
                      </div>
                    )}
                  </button>
                );
              })}
            </DashboardPreview>
          )}
        </div>

        {/* Team */}
          <div className="dash-card" data-testid="dashboard-team-panel">
            <button className="dash-card-title-btn" onClick={() => goToPage('team')} data-testid="dash-team-header">
              <h3 className="dash-card-title font-manrope">Team ({members.length})</h3>
              <ChevronRight size={14} className="dash-header-arrow" />
            </button>
            {members.length === 0 ? (
              <EmptyState icon={Users} title="No team members yet" desc="Add your crew to get started." />
            ) : (
            <DashboardPreview className="team-avatars" testId="dashboard-team-preview">
              {members.slice(0, 10).map(m => (
                <button key={m.id} className="team-avatar-item"
                  onClick={() => goToTeamMember(m.name)}
                  data-testid={`dash-member-${m.id}`}>
                  <Avatar name={m.name} photo={m.photo_url} color={m.color} size={40} />
                  <span className="team-avatar-name">{m.name.split(' ')[0]}</span>
                </button>
              ))}
            </DashboardPreview>
            )}
          </div>
      </div>
    </div>
  );
}

// ============================================================
// JOB DETAIL PANEL (slide-over)
// ============================================================
const JOB_DETAIL_ICON_BY_ID = {
  client: User, install_start: Calendar, install_end: Calendar, delivery_date: ArrowRight,
  address: MapPin, site_instructions: Lock, description: FileText, status: Circle, priority: AlertTriangle,
};
function JobDetailPanel({ job, onClose, onEdit, onStatusChange, onToggleMember, members, fieldSchema }) {
  const { tasks, editTask, removeTask } = useData();
  const [activeTab, setActiveTab] = useState('details');
  const [note, setNote] = useState('');
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`job_notes_${job?.id}`) || '[]'); } catch { return []; }
  });

  useEffect(() => {
    if (!job?.id) return;
    try { setNotes(JSON.parse(localStorage.getItem(`job_notes_${job?.id}`) || '[]')); } catch { setNotes([]); }
    setShowAllMembers(false);
  }, [job?.id]);

  const jobTasks = useMemo(() =>
    tasks.filter(t => t.project_id === job?.id).sort((a,b) => (a.created_at||'').localeCompare(b.created_at||'')),
    [tasks, job?.id]);

  const assignedIds = useMemo(() => new Set(job?.member_ids || []), [job]);
  const assignedMembers = useMemo(() => members.filter(m => assignedIds.has(m.id)), [members, assignedIds]);
  const unassignedMembers = useMemo(() => members.filter(m => !assignedIds.has(m.id)), [members, assignedIds]);

  const mapsUrl = job?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address)}` : null;

  const toggleTask = (t) => editTask(t.id, { status: t.status === 'done' ? 'todo' : 'done' });

  const addNote = () => {
    if (!note.trim()) return;
    const updated = [{ id: Date.now(), text: note.trim(), ts: new Date().toLocaleString() }, ...notes];
    setNotes(updated);
    localStorage.setItem(`job_notes_${job.id}`, JSON.stringify(updated));
    setNote('');
  };

  if (!job) return null;
  const doneCount = jobTasks.filter(t => t.status === 'done').length;

  return (
    <>
      <div className="panel-backdrop" onClick={onClose} data-testid="panel-backdrop" />
      <div className="detail-panel open" data-testid="job-detail-panel">
        {/* Header */}
        <div className="panel-header">
          <div className="panel-header-top">
            <div className="panel-color-bar" style={{ background: job.color }} />
            <div className="panel-title-group">
              <span className="panel-job-num">{job.job_number}</span>
              <h3 className="panel-title font-manrope">{job.name}</h3>
              {job.client && <span className="panel-client-tag">{job.client}</span>}
            </div>
            <div className="panel-header-actions">
              <button className="icon-btn" onClick={() => onEdit(job)} data-testid="panel-edit-btn" title="Edit job">
                <Edit2 size={15} />
              </button>
              <button className="icon-btn" onClick={onClose} data-testid="panel-close-btn">
                <X size={18} />
              </button>
            </div>
          </div>

          {jobTasks.length > 0 && (
            <div className="panel-status-row">
              <span className="task-progress-badge">{doneCount}/{jobTasks.length} tasks</span>
            </div>
          )}

          {/* ── Interactive Team Strip ── */}
          <div className="panel-team-strip" data-testid="panel-team-strip">
            <div className="panel-team-label-row">
              <span className="panel-team-label"><Users size={12} /> Team</span>
              {members.length > 0 && (
                <button className="team-toggle-btn"
                  onClick={() => setShowAllMembers(s => !s)}
                  data-testid="toggle-member-list-btn">
                  {showAllMembers ? 'Done' : (assignedMembers.length === 0 ? '+ Assign' : '+ Add / Remove')}
                </button>
              )}
            </div>

            {/* Assigned members — always visible */}
            {assignedMembers.length === 0 && !showAllMembers && (
              <p className="team-unassigned">No one assigned yet.</p>
            )}
            {assignedMembers.length > 0 && (
              <div className="panel-team-members">
                {assignedMembers.map(m => (
                  <div key={m.id} className="panel-team-chip assigned" data-testid={`panel-member-${m.id}`}>
                    <Avatar name={m.name} photo={m.photo_url} color={m.color} size={30} />
                    <div className="panel-chip-info">
                      <span className="panel-chip-name">{m.name}</span>
                      {m.role && <span className="panel-chip-role">{m.role}</span>}
                    </div>
                    <button className="chip-remove-btn"
                      onClick={() => onToggleMember(job.id, m.id, job.member_ids)}
                      data-testid={`remove-member-${m.id}`}
                      title="Remove from job">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Expanded: unassigned members to add */}
            {showAllMembers && unassignedMembers.length > 0 && (
              <div className="panel-add-members" data-testid="panel-add-members">
                <div className="add-members-label">Click to assign:</div>
                {unassignedMembers.map(m => (
                  <button key={m.id} className="panel-team-chip unassigned"
                    onClick={() => onToggleMember(job.id, m.id, job.member_ids)}
                    data-testid={`assign-member-${m.id}`}>
                    <Avatar name={m.name} photo={m.photo_url} color={m.color} size={30} />
                    <div className="panel-chip-info">
                      <span className="panel-chip-name">{m.name}</span>
                      {m.role && <span className="panel-chip-role">{m.role}</span>}
                    </div>
                    <Plus size={14} className="chip-add-icon" />
                  </button>
                ))}
              </div>
            )}
            {showAllMembers && members.length === 0 && (
              <p className="team-unassigned">Add team members first via the Team page.</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="panel-tabs">
          {[
            { id: 'details',   label: 'Details',    icon: FileText },
            { id: 'punchlist', label: `To Do List${jobTasks.length ? ` (${jobTasks.length})` : ''}`, icon: CheckCircle },
            { id: 'notes',     label: `Notes${notes.length ? ` (${notes.length})` : ''}`, icon: StickyNote },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} className={`panel-tab ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)} data-testid={`panel-tab-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        <div className="panel-body">
          {/* DETAILS TAB */}
          {activeTab === 'details' && (
            <div className="panel-details" data-testid="panel-details">
              {getOrderedJobFields(job, fieldSchema).map(f => {
                const Icon = JOB_DETAIL_ICON_BY_ID[f.id] || FileText;
                return (
                  <div key={f.id} className="detail-row" data-testid={`panel-field-${f.id}`}>
                    <Icon size={14} className="detail-icon" />
                    <div style={f.isAddress ? { flex: 1 } : undefined}>
                      <div className="detail-label">{f.label}</div>
                      <div className={`detail-value${f.id === 'site_instructions' ? ' site-instructions' : ''}`}>{f.value}</div>
                      {f.isAddress && mapsUrl && (
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="maps-link" data-testid="panel-maps-link">
                          Open in Google Maps <ArrowRight size={11} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
              {assignedMembers.length > 0 && (
                <div className="detail-row">
                  <Users size={14} className="detail-icon" />
                  <div>
                    <div className="detail-label">Assigned Team</div>
                    <div className="detail-team">
                      {assignedMembers.map(m => (
                        <div key={m.id} className="detail-member">
                          <Avatar name={m.name} photo={m.photo_url} color={m.color} size={28} />
                          <div>
                            <div className="member-name-sm">{m.name}</div>
                            {m.role && <div className="member-role-sm">{m.role}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PUNCH LIST TAB — uses same component as Jobs page inline list */}
          {activeTab === 'punchlist' && (
            <div className="panel-punchlist" data-testid="panel-punchlist">
              <PunchListDropdown jobId={job.id} open={true} />
            </div>
          )}

          {/* NOTES TAB */}
          {activeTab === 'notes' && (
            <div className="panel-notes" data-testid="panel-notes">
              <div className="add-note-row">
                <textarea className="form-input form-textarea add-note-input"
                  value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Add a note or update…" rows={3} data-testid="add-note-input" />
                <Btn size="sm" onClick={addNote} disabled={!note.trim()} data-testid="add-note-btn">
                  <Plus size={13} /> Add Note
                </Btn>
              </div>
              {notes.length === 0 ? (
                <div className="empty-punchlist"><StickyNote size={28}/><p>No notes yet.</p></div>
              ) : (
                <div className="notes-list">
                  {notes.map(n => (
                    <div key={n.id} className="note-item" data-testid={`note-${n.id}`}>
                      <p className="note-text">{n.text}</p>
                      <span className="note-ts">{n.ts}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================
// ============================================================
// PUNCH LIST DROPDOWN (3-column kanban for job tasks)
// ============================================================
const PUNCH_COLS = [
  { id: 'todo', label: 'To Do', color: '#6B7280' },
  { id: 'in-progress', label: 'In Progress', color: '#3B82F6' },
  { id: 'done', label: 'Completed', color: '#059669' },
];

function PunchListDropdown({ jobId, open }) {
  const { tasks, addTask, editTask, removeTask, isReadOnly, canChangeTaskStatus } = useData();
  const { token } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newAttachment, setNewAttachment] = useState(null);
  const [newUploading, setNewUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [expandedTask, setExpandedTask] = useState(null);
  const [uploadingTaskId, setUploadingTaskId] = useState(null);

  const jobTasks = useMemo(() =>
    tasks.filter(t => t.project_id === jobId),
    [tasks, jobId]);

  const columns = useMemo(() => ({
    'todo': jobTasks.filter(t => t.status === 'todo'),
    'in-progress': jobTasks.filter(t => t.status === 'in-progress'),
    'done': jobTasks.filter(t => t.status === 'done'),
  }), [jobTasks]);

  const totalCount = jobTasks.length;
  const doneCount = columns['done'].length;

  const handleNewFileUpload = async (file) => {
    if (!file) return;
    setNewUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload-attachment`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setNewAttachment({ url: data.url, filename: data.filename });
    } catch (e) { alert(e.message); }
    finally { setNewUploading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await addTask({
        title: newTitle.trim(),
        project_id: jobId,
        status: 'todo',
        priority: newPriority,
        due_date: newDueDate || null,
        notes: newNotes.trim() || '',
        attachment_url: newAttachment?.url || null,
        attachment_filename: newAttachment?.filename || null,
      });
      setNewTitle(''); setNewDueDate(''); setNewNotes(''); setNewPriority('medium'); setNewAttachment(null);
      setShowAddForm(false);
    } finally { setAdding(false); }
  };

  const advanceTask = (task) => {
    const next = task.status === 'todo' ? 'in-progress' : task.status === 'in-progress' ? 'done' : 'todo';
    editTask(task.id, { status: next });
  };

  const handleTaskFileUpload = async (taskId, file) => {
    if (!file) return;
    setUploadingTaskId(taskId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload-attachment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      await editTask(taskId, { attachment_url: data.url, attachment_filename: data.filename });
    } catch (e) { alert(e.message); }
    finally { setUploadingTaskId(null); }
  };

  const handleDragStart = (e, task) => {
    setDragItem(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    if (dragItem && dragItem.status !== targetStatus) {
      if (canChangeTaskStatus || !isReadOnly) {
        editTask(dragItem.id, { status: targetStatus });
      }
    }
    setDragItem(null);
  };

  if (!open) return null;

  return (
    <div className="punchlist-dropdown" data-testid={`punchlist-${jobId}`}>
      {/* Add task - toggle or form */}
      {!isReadOnly && (!showAddForm ? (
        <button className="punchlist-add-toggle"
          onClick={() => setShowAddForm(true)}
          data-testid={`punchlist-show-add-${jobId}`}>
          <Plus size={14} /> Add Task
        </button>
      ) : (
        <form onSubmit={handleAdd} className="punchlist-add-form" data-testid={`punchlist-add-form-${jobId}`}>
          <input className="form-input punchlist-add-input" value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Task name *" autoFocus data-testid={`punchlist-add-input-${jobId}`} />
          <div className="punchlist-add-fields">
            <div className="punchlist-add-field">
              <label className="punchlist-field-label"><Calendar size={11} /> Due Date & Time</label>
              <input type="datetime-local" className="form-input punchlist-add-date" value={newDueDate}
                onChange={e => setNewDueDate(e.target.value)}
                data-testid={`punchlist-add-due-${jobId}`} />
            </div>
            <div className="punchlist-add-field">
              <label className="punchlist-field-label">Priority</label>
              <select className="form-input punchlist-add-date" value={newPriority}
                onChange={e => setNewPriority(e.target.value)}
                data-testid={`punchlist-add-priority-${jobId}`}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="punchlist-add-field punchlist-add-field-full">
              <label className="punchlist-field-label"><FileText size={11} /> Details</label>
              <textarea className="form-input punchlist-add-notes" value={newNotes}
                onChange={e => setNewNotes(e.target.value)}
                placeholder="Additional info, instructions, specs..."
                rows={2} data-testid={`punchlist-add-notes-${jobId}`} />
            </div>
            <div className="punchlist-add-field punchlist-add-field-full">
              <label className="punchlist-field-label"><Paperclip size={11} /> Attachment</label>
              {newAttachment ? (
                <div className="attachment-preview">
                  <Paperclip size={13}/> <span className="attachment-filename">{newAttachment.filename}</span>
                  <button type="button" className="icon-btn-sm" onClick={() => setNewAttachment(null)}><X size={11}/></button>
                </div>
              ) : (
                <label className="attachment-upload-btn">
                  <Paperclip size={13}/> <span>{newUploading ? 'Uploading...' : 'Attach File'}</span>
                  <input type="file" className="sr-only" onChange={e => handleNewFileUpload(e.target.files?.[0])} disabled={newUploading}
                    data-testid={`punchlist-add-attach-${jobId}`} />
                </label>
              )}
            </div>
          </div>
          <div className="punchlist-add-actions">
            <Btn type="submit" size="sm" disabled={adding || !newTitle.trim()}
              data-testid={`punchlist-add-btn-${jobId}`}>
              {adding ? 'Adding…' : 'Add Task'}
            </Btn>
            <button type="button" className="punchlist-cancel-btn"
              onClick={() => { setShowAddForm(false); setNewTitle(''); setNewDueDate(''); setNewNotes(''); setNewPriority('medium'); setNewAttachment(null); }}>
              Cancel
            </button>
          </div>
        </form>
      ))}

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="punchlist-progress">
          <div className="punchlist-progress-bar">
            <div className="punchlist-progress-fill" style={{ width: `${(doneCount / totalCount) * 100}%` }} />
          </div>
          <span className="punchlist-progress-label">{doneCount}/{totalCount} done</span>
        </div>
      )}

      {/* 3 columns */}
      <div className="punchlist-columns" data-testid={`punchlist-columns-${jobId}`}>
        {PUNCH_COLS.map(col => (
          <div key={col.id} className="punchlist-col"
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, col.id)}
            data-testid={`punchlist-col-${col.id}-${jobId}`}>
            <div className="punchlist-col-header">
              <span className="punchlist-col-dot" style={{ background: col.color }} />
              <span className="punchlist-col-title">{col.label}</span>
              <span className="punchlist-col-count">{columns[col.id].length}</span>
            </div>
            <div className="punchlist-col-body">
              {columns[col.id].length === 0 && (
                <div className="punchlist-empty">Drop here</div>
              )}
              {columns[col.id].map(t => (
                <div key={t.id}
                  className={`punchlist-task ${dragItem?.id === t.id ? 'punchlist-task-dragging' : ''} ${expandedTask === t.id ? 'punchlist-task-expanded' : ''}`}
                  draggable={canChangeTaskStatus || !isReadOnly}
                  onDragStart={e => (canChangeTaskStatus || !isReadOnly) && handleDragStart(e, t)}
                  onDragEnd={() => setDragItem(null)}
                  data-testid={`punchlist-task-${t.id}`}>
                  <div className="punchlist-task-row">
                    {(canChangeTaskStatus || !isReadOnly) ? (
                      <button className="punchlist-advance-btn"
                        onClick={() => advanceTask(t)}
                        data-testid={`punchlist-advance-${t.id}`}
                        title={col.id === 'done' ? 'Move back to To Do' : `Move to ${col.id === 'todo' ? 'In Progress' : 'Completed'}`}>
                        {col.id === 'done'
                          ? <CheckCircle size={16} style={{ color: col.color }} />
                          : col.id === 'in-progress'
                            ? <Clock size={16} style={{ color: col.color }} />
                            : <Circle size={16} style={{ color: col.color }} />}
                      </button>
                    ) : (
                      <span className="punchlist-advance-btn" style={{ cursor: 'default' }}>
                        {col.id === 'done'
                          ? <CheckCircle size={16} style={{ color: col.color }} />
                          : col.id === 'in-progress'
                            ? <Clock size={16} style={{ color: col.color }} />
                            : <Circle size={16} style={{ color: col.color }} />}
                      </span>
                    )}
                    <button className={`punchlist-task-title-btn ${col.id === 'done' ? 'punchlist-task-done' : ''}`}
                      onClick={() => setExpandedTask(expandedTask === t.id ? null : t.id)}
                      data-testid={`punchlist-task-expand-${t.id}`}>
                      {t.title}
                    </button>
                    {!isReadOnly && (
                      <button className="punchlist-delete-btn"
                        onClick={() => removeTask(t.id)}
                        data-testid={`punchlist-delete-${t.id}`}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </div>
                  {/* Expanded details */}
                  {expandedTask === t.id && (
                    <div className="punchlist-task-details" data-testid={`punchlist-details-${t.id}`}>
                      {t.due_date && (
                        <div className="punchlist-detail-row">
                          <Calendar size={11} />
                          <span className="punchlist-detail-label">Due:</span>
                          <span className={`punchlist-detail-value ${t.due_date < todayStr() && col.id !== 'done' ? 'punchlist-overdue' : ''}`}>
                            {fmtDate(t.due_date)}
                          </span>
                        </div>
                      )}
                      {t.notes && (
                        <div className="punchlist-detail-row punchlist-detail-notes">
                          <FileText size={11} />
                          <span className="punchlist-detail-value">{t.notes}</span>
                        </div>
                      )}
                      {/* File attachment */}
                      {t.attachment_url ? (
                        <div className="punchlist-detail-row punchlist-attachment-row">
                          <Paperclip size={11} />
                          <a href={`${BACKEND_URL}${t.attachment_url}`} target="_blank" rel="noopener noreferrer"
                            className="punchlist-attachment-link">{t.attachment_filename || 'Attachment'}</a>
                          {!isReadOnly && (
                            <button className="punchlist-attach-remove" onClick={() => editTask(t.id, { attachment_url: null, attachment_filename: null })}>
                              <X size={10} />
                            </button>
                          )}
                        </div>
                      ) : !isReadOnly ? (
                        <label className="punchlist-attach-btn" data-testid={`task-attach-${t.id}`}>
                          <Paperclip size={11} />
                          <span>{uploadingTaskId === t.id ? 'Uploading...' : 'Attach File'}</span>
                          <input type="file" className="sr-only" disabled={!!uploadingTaskId}
                            onChange={e => handleTaskFileUpload(t.id, e.target.files?.[0])} />
                        </label>
                      ) : null}
                      {!t.due_date && !t.notes && !t.attachment_url && (
                        <div className="punchlist-detail-row text-muted" style={{ fontSize: 11 }}>No additional details</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// JOBS PAGE
// ============================================================
// Clients assigned to a project, shown beside the Team block on project cards
const JobCardClients = ({ clients, testSuffix = '', onOpen }) => clients.length ? (
  <div className="job-card-team" data-testid={`job-clients${testSuffix}`}>
    <span className="job-card-field-label"><UserCheck size={11} /> {clients.length > 1 ? 'Clients' : 'Client'}</span>
    <div className="job-card-team-list">
      {clients.map(c => (
        <button key={c.id} type="button" className="job-card-member-chip"
          onClick={(e) => { e.stopPropagation(); onOpen?.(c.name); }}
          data-testid={`job-client-chip${testSuffix}-${c.id}`} title={`View ${c.name} in Clients`}>
          <Avatar name={c.name} photo={c.photo_url} color={c.color} size={22} />
          <span className="job-card-client-name">{c.name}</span>
        </button>
      ))}
    </div>
  </div>
) : null;

function ProjectsPage() {
  const { jobs, members, tasks, addJob, editJob, archiveJob, refreshJobs, goToTeamMember, goToClient, expandedPunchListJobId, setExpandedPunchListJobId, focusJobId, setFocusJobId, projectSearchQuery, setProjectSearchQuery, isReadOnly, fieldSchema: contextFieldSchema } = useData();
  const { token, user } = useAuth();
  const [search, setSearch] = usePersistentState('projects_search', '');
  const [clientFilter, setClientFilter] = usePersistentState('projects_client_filter', '');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [panelJob, setPanelJob] = useState(null);
  const [openPunchList, setOpenPunchList] = useState(null);
  const [shareJob, setShareJob] = useState(null);
  const [viewMode, setViewMode] = usePersistentState('jobs_view_mode', 'card');
  const [expandedListJobId, setExpandedListJobId] = useState(null);
  const [clients, setClients] = useState([]);
  const [fieldSchema, setFieldSchema] = useState(contextFieldSchema || []);
  const canManage = ['owner', 'manager'].includes(user?.workspace_role) && !isReadOnly;
  const sel = useMultiSelect();
  const bulkArchive = async () => {
    sel.setBusy(true);
    try { for (const id of sel.selected) await archiveJob(id); }
    finally { sel.exit(); }
  };

  // Clients are manager-only; a 403 just means no client chips on the cards
  useEffect(() => {
    if (!token) return;
    apiFetch('/clients', {}, token)
      .then(list => { registerLinkNames(list); setClients(list); })
      .catch(() => setClients([]));
  }, [token]);

  // Current field template — drives card field order/labels (re-fetched when the
  // project modal closes, since Configure Fields may have changed the template).
  // In read-only guest/code views there is no token, so use the schema handed
  // down via DataCtx (fetched once by the public access endpoint) instead.
  useEffect(() => {
    if (isReadOnly) { setFieldSchema(contextFieldSchema || []); return; }
    if (!token) return;
    apiFetch('/field-templates', {}, token).then(data => setFieldSchema(data.fields || [])).catch(() => {});
  }, [token, modalOpen, isReadOnly, contextFieldSchema]);

  // Sync from external navigation (dashboard task click)
  useEffect(() => {
    if (expandedPunchListJobId) {
      setOpenPunchList(expandedPunchListJobId);
      setExpandedPunchListJobId(null);
    }
  }, [expandedPunchListJobId, setExpandedPunchListJobId]);

  // Sync from external navigation (dashboard upcoming schedule / recent projects click)
  // Searches arriving from a calendar/dashboard jump are temporary: they are
  // dropped when the user returns to Projects so the filter never sticks.
  const navSearchRef = useRef(false);
  useEffect(() => {
    if (projectSearchQuery) {
      setSearch(projectSearchQuery);
      setProjectSearchQuery('');
      navSearchRef.current = true;
      try { localStorage.setItem('projects_search_auto', '1'); } catch { /* quota */ }
      return;
    }
    if (navSearchRef.current) return;
    if (localStorage.getItem('projects_search_auto')) {
      localStorage.removeItem('projects_search_auto');
      setSearch('');
    }
  }, [projectSearchQuery, setProjectSearchQuery, setSearch]);

  // Sync from Schedule → Project navigation: clear filters, then scroll target to TOP
  useEffect(() => {
    if (!focusJobId?.id) return;
    setSearch('');
    setClientFilter('');
    const id = focusJobId.id;
    setExpandedListJobId(id);
    const scrollToTarget = () => {
      const el = document.getElementById(`job-card-${id}`) || document.getElementById(`job-row-${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    // Wait for filter clear + potential re-render
    const t1 = setTimeout(scrollToTarget, 100);
    const t2 = setTimeout(scrollToTarget, 350);
    setFocusJobId(null);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focusJobId, setFocusJobId, setSearch, setClientFilter]);

  const filtered = useMemo(() => jobs
    .filter(j => !clientFilter || j.client === clientFilter)
    .filter(j => !search || j.name.toLowerCase().includes(search.toLowerCase()) || (j.client || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [jobs, search, clientFilter]);

  // Unique clients from workspace jobs
  const availableClients = useMemo(() =>
    [...new Set(jobs.map(j => j.client).filter(Boolean))].sort(),
    [jobs]);

  const hasActiveFilter = !!clientFilter;
  const clearFilters = () => { setClientFilter(''); };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (j) => { setEditTarget(j); setModalOpen(true); setPanelJob(null); };

  const handleDuplicateJob = async (jobId) => {
    try {
      const newJob = await apiFetch(`/projects/${jobId}/duplicate`, { method: 'POST' }, token);
      if (newJob?.id) refreshJobs();
    } catch (e) { console.error('Duplicate failed', e); }
  };

  const handleToggleMember = useCallback(async (jobId, memberId, currentIds) => {
    const ids = currentIds || [];
    const updated = ids.includes(memberId) ? ids.filter(x => x !== memberId) : [...ids, memberId];
    await editJob(jobId, { member_ids: updated });
  }, [editJob]);

  // Keep panel job in sync when jobs update (e.g. after edit)
  useEffect(() => {
    setPanelJob(prev => {
      if (!prev) return prev;
      const updated = jobs.find(j => j.id === prev.id);
      return updated || prev;
    });
  }, [jobs]);

  return (
    <div className="page-content" data-testid="jobs-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope">Projects</h2>
          <p className="page-sub">{jobs.length} total projects</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="jobs-view-toggle" data-testid="jobs-view-toggle">
            <button className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              data-testid="view-card-btn" title="Card view">
              <LayoutGrid size={14} /> Cards
            </button>
            <button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn" title="List view">
              <List size={14} /> List
            </button>
          </div>
          {canManage && jobs.length > 0 && (
            <button className={`view-toggle-btn ${sel.selectMode ? 'active' : ''}`}
              onClick={() => sel.selectMode ? sel.exit() : sel.setSelectMode(true)}
              data-testid="projects-archive-select-toggle" title="Select to archive">
              <Archive size={14} /> Select
            </button>
          )}
          {!isReadOnly && (
            <Btn onClick={openCreate} data-testid="create-job-btn">
              <Plus size={16} /> New Project
            </Btn>
          )}
        </div>
      </div>

      {sel.selectMode && (
        <MultiSelectBar testid="projects-select-bar"
          count={sel.selected.size} total={filtered.length}
          onSelectAll={() => sel.selectAll(filtered.map(j => j.id))}
          onClear={sel.clear} onCancel={sel.exit}
          onAction={bulkArchive} actionLabel="Archive selected" ActionIcon={Archive} busy={sel.busy} />
      )}

      <div className="toolbar">
        <div className="search-box">
          <Search size={15} className="search-icon" />
          <input className="search-input" placeholder="Search projects..." value={search}
            onChange={e => setSearch(e.target.value)} data-testid="job-search-input" />
          {search && (
            <button className="search-clear-btn" onClick={() => setSearch('')} data-testid="job-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Briefcase} title="No projects found"
          desc={search ? 'Try a different search term.' : 'Create your first job to get started.'}
          action={!search && !isReadOnly && <Btn onClick={openCreate} className="mt-4" data-testid="empty-create-project-btn"><Plus size={14}/> New Project</Btn>} />
      ) : viewMode === 'list' ? (
        /* ===== LIST VIEW ===== */
        <div className="job-list-table" data-testid="job-list-table">
          {filtered.map(j => {
            const assignedMembers = members.filter(m => (j.member_ids || []).includes(m.id));
            const isVis = (fid) => !j.field_visibility || j.field_visibility[fid] !== false;
            const dateStr = j.install_start ? fmtShort(j.install_start) : (j.delivery_date ? fmtShort(j.delivery_date) : null);
            const _today = new Date().toISOString().slice(0, 10);
            const overdue = (() => {
              const dates = [j.install_start, j.install_end, j.delivery_date].filter(Boolean).map(d => d.slice(0, 10));
              if (!dates.length) return false;
              const latest = dates.reduce((a, b) => (a > b ? a : b));
              return latest < _today;
            })();
            const isExpanded = expandedListJobId === j.id;
            const mapsUrl = j.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.address)}` : null;
            return (
              <div key={j.id} id={`job-row-${j.id}`} className={`job-list-item-wrap${overdue ? ' jl-overdue' : ''}`} data-testid={`job-list-wrap-${j.id}`}>
                <div
                  className={`job-list-row-item${isExpanded ? ' jl-active' : ''}`}
                  data-testid={`job-list-row-${j.id}`}
                  onClick={() => setExpandedListJobId(isExpanded ? null : j.id)}>
                  <div className="job-list-color-bar" style={{ background: j.color || '#10B981' }} />
                  <div className="job-list-main">
                    <div className="job-list-title">{j.name}</div>
                    {isVis('client') && j.client && <div className="job-list-sub">{j.client}</div>}
                  </div>
                  <div className="job-list-meta">
                    {overdue && <span className="job-list-overdue-tag" data-testid={`list-overdue-${j.id}`}>Overdue</span>}
                    {dateStr && <span>{dateStr}</span>}
                    {isVis('member_ids') && assignedMembers.length > 0 && (
                      <div className="mini-avatar-stack">
                        {assignedMembers.slice(0, 3).map(m => <Avatar key={m.id} name={m.name} photo={m.photo_url} color={m.color} size={22} />)}
                      </div>
                    )}
                    <ChevronDown size={14} className={`jl-chevron ${isExpanded ? 'jl-chevron-open' : ''}`} />
                  </div>
                  <div className="job-list-actions" onClick={e => e.stopPropagation()}>
                    {sel.selectMode && <SelectCheck id={j.id} selected={sel.selected.has(j.id)} onToggle={sel.toggle} testid={`select-project-${j.id}`} />}
                    {!isReadOnly && (
                      <>
                        <button className="icon-btn" onClick={() => openEdit(j)} data-testid={`edit-job-list-${j.id}`}><Edit2 size={13} /></button>
                        <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(j)} data-testid={`delete-job-list-${j.id}`}><Archive size={13} /></button>
                      </>
                    )}
                  </div>
                </div>
                {/* ===== INLINE CARD EXPANSION ===== */}
                {isExpanded && (
                  <div className="job-list-expanded-card" id={`job-card-${j.id}`} data-testid={`job-list-expanded-${j.id}`}>
                    <div className="job-card-top" style={{ borderLeftColor: j.color }}>
                      <div className="job-card-title-row">
                        <div className="job-card-title-info">
                          <span className="job-card-name font-manrope">{j.name}</span>
                        </div>
                        <div className="job-card-actions">
                          <button className="icon-btn" onClick={() => setShareJob(j)} data-testid={`share-job-list-${j.id}`} title="Share"><Share2 size={13} /></button>
                          {!isReadOnly && (
                            <>
                              <button className="icon-btn" onClick={() => handleDuplicateJob(j.id)} data-testid={`dup-job-list-${j.id}`} title="Duplicate"><Copy size={13} /></button>
                              <button className="icon-btn" onClick={() => openEdit(j)} data-testid={`edit-job-list-expanded-${j.id}`}><Edit2 size={13} /></button>
                              <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(j)} data-testid={`del-job-list-expanded-${j.id}`}><Archive size={13} /></button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="job-card-details">
                      {getOrderedJobFields(j, fieldSchema).map(f => (
                        <div key={f.id} className={`job-card-field${f.id === 'site_instructions' || f.id === 'description' ? ' job-card-field-full' : ''}`}>
                          <span className="job-card-field-label">{f.label}</span>
                          <span className={`job-card-field-value${f.id === 'site_instructions' || f.id === 'description' ? ' job-card-field-truncate' : ''}`}>
                            {f.isAddress && mapsUrl ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="job-card-link">{f.value}</a> : f.value}
                          </span>
                        </div>
                      ))}
                    </div>
                    {(() => {
                      const assignedClients = clients.filter(c => (c.project_ids || []).includes(j.id));
                      const showTeam = isVis('member_ids') && assignedMembers.length > 0;
                      if (!showTeam && !assignedClients.length) return null;
                      return (
                        <div className="job-card-people-row">
                          {showTeam && (
                            <div className="job-card-team">
                              <span className="job-card-field-label"><Users size={11} /> Team</span>
                              <div className="job-card-team-list">
                                {assignedMembers.map(m => (
                                  <button key={m.id} className="job-card-member-chip" onClick={() => goToTeamMember(m.name)}
                                    data-testid={`job-member-link-list-${m.id}`} title={`View ${m.name} in Team`}>
                                    <Avatar name={m.name} photo={m.photo_url} color={m.color} size={22} />
                                    <span>{m.name.split(' ')[0]}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <JobCardClients clients={assignedClients} testSuffix={`-list-${j.id}`} onOpen={goToClient} />
                        </div>
                      );
                    })()}
                    {/* To Do List Toggle */}
                    {(() => {
                      const taskCount = tasks.filter(t => t.project_id === j.id).length;
                      const doneCount = tasks.filter(t => t.project_id === j.id && t.status === 'done').length;
                      const isOpen = openPunchList === j.id;
                      return (
                        <>
                          <button className={`punchlist-toggle ${isOpen ? 'punchlist-toggle-open' : ''}`}
                            onClick={() => setOpenPunchList(isOpen ? null : j.id)}
                            data-testid={`punchlist-toggle-list-${j.id}`}>
                            <Wrench size={13} /><span>To Do List</span>
                            {taskCount > 0 && <span className="punchlist-toggle-count">{doneCount}/{taskCount}</span>}
                            <ChevronDown size={14} className={`punchlist-chevron ${isOpen ? 'punchlist-chevron-open' : ''}`} />
                          </button>
                          <PunchListDropdown jobId={j.id} open={isOpen} />
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ===== CARD VIEW ===== */
        <div className="job-cards-grid" data-testid="job-cards-grid">
          {filtered.map(j => {
            const assignedMembers = members.filter(m => (j.member_ids || []).includes(m.id));
            const mapsUrl = j.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.address)}` : null;
            const isVis = (fid) => !j.field_visibility || j.field_visibility[fid] !== false;
            const _today2 = new Date().toISOString().slice(0, 10);
            const overdue = (() => {
              const dates = [j.install_start, j.install_end, j.delivery_date].filter(Boolean).map(d => d.slice(0, 10));
              if (!dates.length) return false;
              const latest = dates.reduce((a, b) => (a > b ? a : b));
              return latest < _today2;
            })();
            return (
              <div key={j.id} id={`job-card-${j.id}`} className={`job-card ${panelJob?.id === j.id ? 'job-card-active' : ''}`}
                data-testid={`job-card-${j.id}`}>
                {overdue && (
                  <div className="job-card-overdue-banner" data-testid={`job-overdue-${j.id}`}>
                    Overdue
                  </div>
                )}
                <div className="job-card-top" style={{ borderLeftColor: j.color }}>
                  <div className="job-card-title-row">
                    <div className="job-card-title-info">
                      <button className="job-card-name-btn" onClick={() => setPanelJob(panelJob?.id === j.id ? null : j)}
                        data-testid={`job-open-panel-${j.id}`}>
                        <span className="job-card-name font-manrope">{j.name}</span>
                      </button>
                    </div>
                    <div className="job-card-actions">
                      {sel.selectMode && <SelectCheck id={j.id} selected={sel.selected.has(j.id)} onToggle={sel.toggle} testid={`select-project-card-${j.id}`} />}
                      <button className="icon-btn" onClick={() => setShareJob(j)}
                        data-testid={`share-job-${j.id}`} title="Share this job">
                        <Share2 size={13} />
                      </button>
                      {!isReadOnly && (
                        <>
                          <button className="icon-btn" onClick={() => handleDuplicateJob(j.id)}
                            data-testid={`duplicate-job-${j.id}`} title="Duplicate project">
                            <Copy size={13} />
                          </button>
                          <button className="icon-btn" onClick={() => openEdit(j)} data-testid={`edit-job-${j.id}`}>
                            <Edit2 size={13} />
                          </button>
                          <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(j)}
                            data-testid={`delete-job-${j.id}`}>
                            <Archive size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="job-card-details">
                  {getOrderedJobFields(j, fieldSchema).map(f => (
                    <div key={f.id} className={`job-card-field${f.id === 'site_instructions' || f.id === 'description' ? ' job-card-field-full' : ''}`}
                      data-testid={`job-field-${f.id}-${j.id}`}>
                      <span className="job-card-field-label">{f.label}</span>
                      <span className={`job-card-field-value${f.id === 'site_instructions' || f.id === 'description' ? ' job-card-field-truncate' : ''}`}>
                        {f.isAddress && mapsUrl ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="job-card-link">{f.value}</a> : f.value}
                      </span>
                    </div>
                  ))}
                </div>


                {(() => {
                  const assignedClients = clients.filter(c => (c.project_ids || []).includes(j.id));
                  const showTeam = isVis('member_ids') && assignedMembers.length > 0;
                  if (!showTeam && !assignedClients.length) return null;
                  return (
                    <div className="job-card-people-row">
                      {showTeam && (
                        <div className="job-card-team" data-testid={`job-team-${j.id}`}>
                          <span className="job-card-field-label"><Users size={11} /> Team</span>
                          <div className="job-card-team-list">
                            {assignedMembers.map(m => (
                              <button key={m.id} className="job-card-member-chip"
                                onClick={() => goToTeamMember(m.name)}
                                data-testid={`job-member-link-${m.id}`}
                                title={`View ${m.name} in Team`}>
                                <Avatar name={m.name} photo={m.photo_url} color={m.color} size={22} />
                                <span>{m.name.split(' ')[0]}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <JobCardClients clients={assignedClients} testSuffix={`-${j.id}`} onOpen={goToClient} />
                    </div>
                  );
                })()}

                {/* To Do List Toggle */}
                {(() => {
                  const taskCount = tasks.filter(t => t.project_id === j.id).length;
                  const doneCount = tasks.filter(t => t.project_id === j.id && t.status === 'done').length;
                  const isOpen = openPunchList === j.id;
                  return (
                    <>
                      <button className={`punchlist-toggle ${isOpen ? 'punchlist-toggle-open' : ''}`}
                        onClick={() => setOpenPunchList(isOpen ? null : j.id)}
                        data-testid={`punchlist-toggle-${j.id}`}>
                        <Wrench size={13} />
                        <span>To Do List</span>
                        {taskCount > 0 && <span className="punchlist-toggle-count">{doneCount}/{taskCount}</span>}
                        <ChevronDown size={14} className={`punchlist-chevron ${isOpen ? 'punchlist-chevron-open' : ''}`} />
                      </button>
                      <PunchListDropdown jobId={j.id} open={isOpen} />
                    </>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      <JobModal open={modalOpen} onClose={() => { setModalOpen(false); if (!editTarget) setSearch(''); }}
        onSave={editTarget ? (data) => editJob(editTarget.id, data) : addJob}
        job={editTarget} members={members} />

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await archiveJob(deleteTarget.id); setDeleteTarget(null); }}
        title="Archive Project" message={`Archive "${deleteTarget?.name}"? You can restore it from the Archive menu.`} confirmLabel="Archive" />

      {panelJob && (
        <JobDetailPanel job={panelJob} onClose={() => setPanelJob(null)}
          onEdit={openEdit} members={members} fieldSchema={fieldSchema}
          onStatusChange={(id, status) => editJob(id, { status })}
          onToggleMember={handleToggleMember} />
      )}
      {shareJob && <ShareJobModal job={shareJob} onClose={() => setShareJob(null)} />}
    </div>
  );
}

// ============================================================
// SCHEDULE PAGE
// ============================================================
// ============================================================
// MEMBER FILTER PICKER — bubbly pill dropdown for Schedule page
// ============================================================
function MemberFilterPicker({ members, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setOpen(false));
  const selected = value === 'all' ? null : members.find(m => m.id === value);
  return (
    <div className="member-filter-picker" ref={ref} data-testid="member-filter">
      <button className={`member-filter-pill ${open ? 'active' : ''}`}
        onClick={() => setOpen(o => !o)} data-testid="member-filter-btn">
        {selected ? (
          <>
            <Avatar name={selected.name} photo={selected.photo_url} color={selected.color} size={20} />
            <span>{selected.name.split(' ')[0]}</span>
          </>
        ) : (
          <>
            <Users size={14} />
            <span>All Team</span>
          </>
        )}
        <ChevronDown size={12} className={`member-filter-chevron ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className="member-filter-dropdown" data-testid="member-filter-dropdown">
          <button className={`member-filter-option ${value === 'all' ? 'active' : ''}`}
            onClick={() => { onChange('all'); setOpen(false); }}
            data-testid="member-filter-all">
            <div className="member-filter-option-avatar all-avatar"><Users size={14} /></div>
            <span>All Team</span>
            {value === 'all' && <Check size={13} className="member-filter-check" />}
          </button>
          {members.map(m => (
            <button key={m.id}
              className={`member-filter-option ${value === m.id ? 'active' : ''}`}
              onClick={() => { onChange(m.id); setOpen(false); }}
              data-testid={`member-filter-${m.id}`}>
              <Avatar name={m.name} photo={m.photo_url} color={m.color} size={26} />
              <span>{m.name}</span>
              {value === m.id && <Check size={13} className="member-filter-check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SchedulePage() {
  const { jobs, members, isReadOnly, editJob } = useData();
  const { token } = useAuth();
  const [dateSchema, setDateSchema] = useState([]);
  useEffect(() => {
    if (!token || isReadOnly) return;
    let active = true;
    apiFetch('/field-templates', {}, token).then(data => { if (active) setDateSchema(data.fields || []); }).catch(() => {});
    return () => { active = false; };
  }, [token, isReadOnly]);
  const [view, setView] = usePersistentState('schedule_view_v2', 'list');
  const [dayDate, setDayDate] = useState(todayStr);
  const [weekStart, setWeekStart] = useState(() => getWeekStart(todayStr()));
  const [monthDate, setMonthDate] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [memberFilter, setMemberFilter] = usePersistentState('schedule_member_filter', 'all');
  const [schedSearch, setSchedSearch] = usePersistentState('schedule_search', '');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addDate, setAddDate] = useState('');
  const [schedEditJob, setSchedEditJob] = useState(null);
  const { addJob } = useData();

  const filteredJobs = useMemo(() => jobs.filter(j => {
    if (memberFilter !== 'all' && !(j.member_ids || []).includes(memberFilter)) return false;
    if (schedSearch && !j.name?.toLowerCase().includes(schedSearch.toLowerCase()) && !j.client?.toLowerCase().includes(schedSearch.toLowerCase())) return false;
    return true;
  }), [jobs, memberFilter, schedSearch]);

  const handleAddOnDate = (date) => { if (!isReadOnly) { setAddDate(date); setAddModalOpen(true); } };
  const activeFilters = [schedSearch, memberFilter !== 'all'].filter(Boolean).length;

  return (
    <div className="page-content" data-testid="schedule-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope">Schedule</h2>
          <p className="page-sub">{filteredJobs.length} projects{activeFilters ? ` (${activeFilters} filter${activeFilters > 1 ? 's' : ''})` : ''}</p>
        </div>
        <div className="schedule-controls">
          {/* Search */}
          <div className="sched-search-wrap">
            <Search size={13} className="sched-search-icon" />
            <input className="sched-search-input" placeholder="Search projects…" value={schedSearch}
              onChange={e => setSchedSearch(e.target.value)} data-testid="sched-search" />
            {schedSearch && (
              <button className="sched-search-clear" onClick={() => setSchedSearch('')} data-testid="sched-search-clear">
                <X size={12} />
              </button>
            )}
          </div>
          {/* Member filter — bubbly pill picker */}
          {members.length > 0 && (
            <MemberFilterPicker
              members={members}
              value={memberFilter}
              onChange={setMemberFilter}
            />
          )}
          <div className="view-switcher" data-testid="view-switcher">
            <button className={`view-btn ${view === 'day' ? 'active' : ''}`}
              onClick={() => setView('day')} data-testid="view-day-btn">
              <CalendarDays size={15} /> Day
            </button>
            <button className={`view-btn ${view === 'week' ? 'active' : ''}`}
              onClick={() => setView('week')} data-testid="view-week-btn">
              <List size={15} /> Week
            </button>
            <button className={`view-btn ${view === 'month' ? 'active' : ''}`}
              onClick={() => setView('month')} data-testid="view-month-btn">
              <Grid3X3 size={15} /> Month
            </button>
            <button className={`view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')} data-testid="view-list-btn">
              <List size={15} /> List
            </button>
          </div>
        </div>
      </div>

      {view === 'day' && (
        <DayView jobs={filteredJobs} members={members} selectedDay={dayDate}
          onPrev={() => setDayDate(d => addDays(d, -1))}
          onNext={() => setDayDate(d => addDays(d, 1))}
          onToday={() => setDayDate(todayStr())}
          onAdd={handleAddOnDate}
          onEditJob={!isReadOnly ? setSchedEditJob : null} />
      )}
      {view === 'week' && (
        <WeekView jobs={filteredJobs} members={members} weekStart={weekStart}
          onPrev={() => setWeekStart(d => addDays(d, -7))}
          onNext={() => setWeekStart(d => addDays(d, 7))}
          onAdd={handleAddOnDate}
          onEditJob={!isReadOnly ? setSchedEditJob : null} />
      )}
      {view === 'month' && (
        <MonthView jobs={filteredJobs} members={members}
          year={monthDate.year} month={monthDate.month}
          onPrev={() => setMonthDate(d => {
            const m = d.month === 0 ? 11 : d.month - 1;
            const y = d.month === 0 ? d.year - 1 : d.year;
            return { year: y, month: m };
          })}
          onNext={() => setMonthDate(d => {
            const m = d.month === 11 ? 0 : d.month + 1;
            const y = d.month === 11 ? d.year + 1 : d.year;
            return { year: y, month: m };
          })}
          onAdd={handleAddOnDate}
          onEditJob={!isReadOnly ? setSchedEditJob : null} />
      )}
      {view === 'list' && (
        <ScheduleListView jobs={filteredJobs} members={members} onAdd={handleAddOnDate} dateSchema={dateSchema} />
      )}

      <JobModal open={addModalOpen} onClose={() => setAddModalOpen(false)}
        onSave={(data) => addJob({ ...data, install_start: addDate || data.install_start })}
        job={addDate ? { install_start: addDate } : null}
        members={members} />

      {schedEditJob && (
        <JobModal open={true} onClose={() => setSchedEditJob(null)}
          onSave={async (data) => {
            const wasUnscheduled = !getJobBestDate(schedEditJob);
            const updated = await editJob(schedEditJob.id, data);
            if (wasUnscheduled && updated) {
              const newDate = (updated.install_start || updated.delivery_date)?.split('T')[0];
              if (newDate) {
                const d = new Date(newDate + 'T00:00:00');
                setMonthDate({ year: d.getFullYear(), month: d.getMonth() });
                setWeekStart(getWeekStart(newDate));
                setDayDate(newDate);
              }
            }
          }}
          job={schedEditJob} members={members} />
      )}
    </div>
  );
}

function DayView({ jobs, members, selectedDay, onPrev, onNext, onToday, onAdd, onEditJob }) {
  const today = todayStr();
  const { isReadOnly, goToTeamMember, goToProjectSearch } = useData();
  const dayJobs = jobs.filter(j => jobOnDay(j, selectedDay));
  const hours = Array.from({ length: 13 }, (_, i) => i + 7);

  const getHourNum = (job) => {
    const s = getJobBestDate(job) || '';
    if (!s.includes('T')) return null;                        // date-only → all day
    const t = s.split('T')[1] || '';
    if (!t || t.startsWith('00:00')) return null;             // midnight UTC → all day
    const [h, m] = t.split(':').map(Number);
    return h + m / 60;
  };

  const timedJobs = dayJobs.filter(j => getHourNum(j) !== null);
  const allDayJobs = dayJobs.filter(j => getHourNum(j) === null);

  const dayLabel = new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="day-view" data-testid="day-view">
      <div className="day-nav">
        <button className="icon-btn" onClick={onPrev} data-testid="day-prev"><ChevronLeft size={18} /></button>
        <div className="day-nav-center">
          <span className={`day-nav-label font-manrope ${selectedDay === today ? 'day-today-label' : ''}`}>{dayLabel}</span>
          {selectedDay !== today && (
            <button className="day-today-btn" onClick={onToday} data-testid="day-today-btn">Today</button>
          )}
        </div>
        <button className="icon-btn" onClick={onNext} data-testid="day-next"><ChevronRight size={18} /></button>
      </div>

      {allDayJobs.length > 0 && (
        <div className="day-allday-row">
          <div className="day-hour-label">All Day</div>
          <div className="day-allday-jobs">
            {allDayJobs.map(j => {
              const assigned = members.filter(m => (j.member_ids || []).includes(m.id));
              return (
                <div key={j.id} className="day-job-chip" style={{ borderLeftColor: j.color || 'hsl(var(--primary))', cursor: 'pointer' }}
                  data-testid={`day-job-${j.id}`}
                  onClick={() => goToProjectSearch(j.name)}>
                  <span className="day-job-name">{j.name}</span>
                  <div className="day-job-avatars">
                    {assigned.map(m => (
                      <button key={m.id} style={{ background: 'none', border: 'none', padding: 0, cursor: isReadOnly ? 'default' : 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); !isReadOnly && goToTeamMember(m.name); }} title={m.name}>
                        <Avatar name={m.name} photo={m.photo_url} color={m.color} size={16} />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="day-timeline">
        {hours.map(h => {
          const jobsHere = timedJobs.filter(j => Math.floor(getHourNum(j)) === h);
          const label = h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`;
          return (
            <div key={h} className={`day-hour-row ${jobsHere.length === 0 ? 'day-hour-empty' : ''}`}
              onClick={() => !isReadOnly && onAdd && onAdd(`${selectedDay}T${String(h).padStart(2,'0')}:00`)}>
              <div className="day-hour-label">{label}</div>
              <div className="day-hour-content">
                {jobsHere.map(j => {
                  const assigned = members.filter(m => (j.member_ids || []).includes(m.id));
                  return (
                    <div key={j.id} className="day-job-chip" style={{ borderLeftColor: j.color || 'hsl(var(--primary))', cursor: 'pointer' }}
                      data-testid={`day-job-${j.id}`}
                      onClick={(e) => { e.stopPropagation(); goToProjectSearch(j.name); }}>
                      <span className="day-job-name">{j.name}</span>
                      {j.client && <span className="day-job-client">{j.client}</span>}
                      <div className="day-job-avatars">
                        {assigned.map(m => (
                          <button key={m.id} style={{ background: 'none', border: 'none', padding: 0, cursor: isReadOnly ? 'default' : 'pointer' }}
                            onClick={e => { e.stopPropagation(); !isReadOnly && goToTeamMember(m.name); }} title={m.name}>
                            <Avatar name={m.name} photo={m.photo_url} color={m.color} size={16} />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {jobsHere.length === 0 && !isReadOnly && (
                  <div className="day-hour-add-hint">+ Add job at {label}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dayJobs.length === 0 && (
        <EmptyState title="No projects scheduled" sub={`Nothing scheduled for ${fmtDate(selectedDay)}`}
          action={!isReadOnly && <Btn onClick={() => onAdd && onAdd(selectedDay)}><Plus size={14}/> Add Job</Btn>} />
      )}
    </div>
  );
}

function ScheduleListView({ jobs, members, onAdd, dateSchema }) {
  const today = todayStr();
  const grouped = useMemo(() => groupByPeriod(jobs), [jobs]);
  const { goToTeamMember, isReadOnly, goToProjectSearch } = useData();

  return (
    <div className="list-view" data-testid="schedule-list-view">
      {Object.entries(grouped).map(([period, periodJobs]) => {
        if (periodJobs.length === 0) return null;
        return (
          <div key={period} className="period-group">
            <div className="period-header">
              <span className={`period-label ${period === 'Unscheduled' ? 'label-warning' : 'label-muted'}`}>
                {period}
              </span>
              <span className="period-count">{periodJobs.length}</span>
            </div>
            {periodJobs.map(j => {
              const assignedMembers = members.filter(m => (j.member_ids || []).includes(m.id));
              return (
                <div key={j.id} className="list-job-row" data-testid={`schedule-job-${j.id}`}
                  onClick={() => goToProjectSearch(j.name)}
                  style={{ cursor: 'pointer' }}
                  title={`Open ${j.name} in Projects`}>
                  <div className="list-job-indicator" style={{ background: j.color }} />
                  <div className="list-job-info">
                    <div className="list-job-name">{j.name}</div>
                    {j.client && <div className="list-job-client">re: {j.client}</div>}
                  </div>
                  {j.description && <div className="list-job-desc text-muted">{j.description}</div>}
                  <div className="list-job-meta">
                    <ProjectScheduleDates job={j} schema={dateSchema} />
                    <div className="list-assignee-strip">
                      {assignedMembers.length === 0 ? (
                        <span className="text-muted" style={{fontSize:11}}>Not assigned</span>
                      ) : (
                        <>
                          {assignedMembers.slice(0,4).map(m => (
                            <button key={m.id} className="list-assignee-chip" title={`View ${m.name} in Team`}
                              onClick={(e) => { e.stopPropagation(); !isReadOnly && goToTeamMember(m.name); }}
                              style={{ cursor: isReadOnly ? 'default' : 'pointer', background: 'none', border: 'none', padding: 0 }}>
                              <Avatar name={m.name} photo={m.photo_url} color={m.color} size={22} />
                              <span className="list-assignee-name">{m.name.split(' ')[0]}</span>
                            </button>
                          ))}
                          {assignedMembers.length > 4 && (
                            <span className="list-assignee-more">+{assignedMembers.length - 4}</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {jobs.length === 0 && (
        <EmptyState icon={Calendar} title="No jobs scheduled"
          desc="Create a job to see it on the schedule."
          action={!isReadOnly && <Btn onClick={() => onAdd('')} className="mt-4"><Plus size={14}/> New Job</Btn>} />
      )}
    </div>
  );
}

function WeekView({ jobs, members, weekStart, onPrev, onNext, onAdd, onEditJob }) {
  const today = todayStr();
  const { goToTeamMember, isReadOnly, goToProjectSearch } = useData();
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = days[6];
  const monthLabel = (() => {
    const d = new Date(weekStart + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="week-view" data-testid="schedule-week-view">
      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={onPrev} data-testid="week-prev-btn"><ChevronLeft size={16}/></button>
        <span className="cal-nav-label font-manrope">{monthLabel}</span>
        <button className="cal-nav-btn" onClick={onNext} data-testid="week-next-btn"><ChevronRight size={16}/></button>
      </div>
      <div className="week-grid">
        <div className="week-header-row">
          <div className="week-time-col" />
          {days.map(d => {
            const dayObj = new Date(d + 'T00:00:00');
            const isToday = d === today;
            return (
              <div key={d} className={`week-day-header ${isToday ? 'today-header' : ''}`}>
                <span className="day-abbr">{DAY_NAMES[dayObj.getDay()]}</span>
                <span className={`day-num ${isToday ? 'today-num' : ''}`}>{dayObj.getDate()}</span>
              </div>
            );
          })}
        </div>
        {/* Time slots — project day-fill strips overlaid inside grid */}
        <div className="week-time-area">
          {/* Absolute project fills spanning full day height */}
          {jobs.filter(j => {
            const start = (getJobBestDate(j) || '').split('T')[0];
            const end = (getJobEndDate(j) || '').split('T')[0];
            return start && end >= days[0] && start <= days[6];
          }).map(j => {
            const start = (getJobBestDate(j) || '').split('T')[0] || days[0];
            const end = (getJobEndDate(j) || start).split('T')[0];
            const startIdx = Math.max(0, days.findIndex(d => d >= start));
            const endIdx = Math.max(startIdx, days.reduce((best, d, i) => (d <= end ? i : best), startIdx));
            const spanDays = endIdx - startIdx + 1;
            const color = j.color || 'hsl(var(--primary))';
            return (
              <div key={j.id} className="week-day-fill"
                style={{
                  left: `calc(72px + ${startIdx} * ((100% - 72px) / 7))`,
                  width: `calc(${spanDays} * (100% - 72px) / 7)`,
                  borderLeftColor: color,
                  background: color + '18',
                }}
                onClick={(e) => { e.stopPropagation(); goToProjectSearch(j.name); }}
                data-testid={`week-fill-${j.id}`}>
                <span className="week-fill-name">{j.name}</span>
                <div className="week-chip-avatars">
                  {members.filter(m => (j.member_ids||[]).includes(m.id)).slice(0,2).map(m => (
                    <button key={m.id} style={{ background:'none', border:'none', padding:0, cursor:'pointer' }}
                      onClick={e => { e.stopPropagation(); if (!isReadOnly) goToTeamMember(m.name); }}>
                      <Avatar name={m.name} photo={m.photo_url} color={m.color} size={16} />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {/* 24 hourly rows */}
          {Array.from({ length: 24 }, (_, i) => {
            const h = i;
            const label = h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;
            return (
              <div key={h} className="week-time-row">
                <div className="week-time-col week-time-label">{label}</div>
                {days.map(d => (
                  <div key={d}
                    className={`week-time-cell ${d === today ? 'today-col' : ''}`}
                    onClick={() => !isReadOnly && onAdd(d)} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const MAX_BAR_LANES = 4;

// Lays out the projects overlapping a week as continuous bars: one lane per
// row of non-overlapping projects, so a project with several dates reads as a
// single bar instead of a chip in every day cell.
function weekBars(week, jobs) {
  const weekStart = week[0].dateStr;
  const weekEnd = week[6].dateStr;
  const dp = s => (s ? s.split('T')[0] : '');
  const spans = jobs.map(job => {
    const start = dp(getJobBestDate(job));
    if (!start) return null;
    const end = dp(getJobEndDate(job)) || start;
    if (end < weekStart || start > weekEnd) return null;
    const startIdx = start <= weekStart ? 0 : week.findIndex(d => d.dateStr === start);
    const endIdx = end >= weekEnd ? 6 : week.findIndex(d => d.dateStr === end);
    if (startIdx < 0 || endIdx < 0) return null;
    return { job, startIdx, endIdx, continuesLeft: start < weekStart, continuesRight: end > weekEnd };
  }).filter(Boolean);
  spans.sort((a, b) => a.startIdx - b.startIdx || (b.endIdx - b.startIdx) - (a.endIdx - a.startIdx));
  const lanes = [];
  spans.forEach(span => {
    let lane = lanes.findIndex(taken => taken.every(s => s.endIdx < span.startIdx || s.startIdx > span.endIdx));
    if (lane === -1) { lanes.push([]); lane = lanes.length - 1; }
    lanes[lane].push(span);
    span.lane = lane;
  });
  return spans;
}

function MonthView({ jobs, members, year, month, onPrev, onNext, onAdd, onEditJob }) {
  const today = todayStr();
  const { goToTeamMember, isReadOnly, goToProjectSearch } = useData();
  const days = useMemo(() => getMonthDays(year, month), [year, month]);
  const weeks = useMemo(() => Array.from({ length: days.length / 7 }, (_, i) => days.slice(i * 7, i * 7 + 7)), [days]);
  const [popupJob, setPopupJob] = useState(null);
  const handleDayClick = (dateStr, e) => {
    e.stopPropagation();
    if (isReadOnly) return;
    setPopupJob(null);
    onAdd(dateStr);
  };

  useEffect(() => {
    const close = () => { setPopupJob(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div className="month-layout" data-testid="schedule-month-view" onClick={() => { setPopupJob(null); }}>
      <div className="month-main">
        <div className="cal-nav">
          <button className="cal-nav-btn" onClick={onPrev} data-testid="month-prev-btn"><ChevronLeft size={16}/></button>
          <span className="cal-nav-label font-manrope">{MONTH_NAMES[month]} {year}</span>
          <button className="cal-nav-btn" onClick={onNext} data-testid="month-next-btn"><ChevronRight size={16}/></button>
        </div>
        <div className="month-grid">
          {DAY_NAMES.map(d => <div key={d} className="month-dow-header">{d}</div>)}
        </div>
        {weeks.map(week => {
          const bars = weekBars(week, jobs);
          const laneCount = bars.reduce((max, b) => Math.max(max, b.lane + 1), 0);
          const cellMinHeight = Math.max(100, 34 + Math.min(laneCount, MAX_BAR_LANES) * 20);
          return (
            <div key={week[0].dateStr} className="month-week-row">
              {week.map(({ dateStr, isCurrentMonth }, dayIdx) => {
                const isToday = dateStr === today;
                const dayBars = bars.filter(b => b.startIdx <= dayIdx && dayIdx <= b.endIdx);
                const hidden = dayBars.filter(b => b.lane >= MAX_BAR_LANES).length;
                return (
                  <div key={dateStr} style={{ minHeight: cellMinHeight }}
                    className={`month-day-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today-cell' : ''}`}
                    onClick={(e) => handleDayClick(dateStr, e)}
                    data-testid={`month-day-${dateStr}`}>
                    <div className="month-day-head">
                      <span className={`month-day-num ${isToday ? 'today-day-num' : ''}`}>
                        {new Date(dateStr + 'T00:00:00').getDate()}
                      </span>
                      {dayBars.length > 0 && (
                        <span className="month-visit-count">{dayBars.length} {dayBars.length === 1 ? 'Visit' : 'Visits'}</span>
                      )}
                    </div>
                    {hidden > 0 && (
                      <div className="month-more" style={{ marginTop: MAX_BAR_LANES * 20 }}>+{hidden} more</div>
                    )}
                  </div>
                );
              })}
              <div className="month-week-bars">
                {bars.filter(b => b.lane < MAX_BAR_LANES).map(({ job: j, startIdx, endIdx, lane, continuesLeft, continuesRight }) => {
                  const chipMembers = members.filter(m => (j.member_ids || []).includes(m.id)).slice(0, 2);
                  const edge = continuesLeft && continuesRight ? '' : continuesLeft ? 'month-bar-end'
                    : continuesRight ? 'month-bar-start' : 'month-bar-single';
                  return (
                    <button key={j.id}
                      className={`month-bar ${edge} ${continuesLeft ? 'month-bar-continues' : ''} ${j.status === 'completed' ? 'month-bar-completed' : ''}`}
                      style={{
                        left: `calc(${(startIdx / 7) * 100}% + 4px)`,
                        width: `calc(${((endIdx - startIdx + 1) / 7) * 100}% - 8px)`,
                        top: 30 + lane * 20,
                        borderLeftColor: j.color,
                        background: j.color + '22',
                        color: j.color,
                      }}
                      onClick={(e) => { e.stopPropagation(); goToProjectSearch(j.name); }}
                      data-testid={`month-job-${j.id}`}>
                      <span className="month-bar-name">{j.name}</span>
                      {chipMembers.length > 0 && (
                        <div className="month-bar-avatars">
                          {chipMembers.map(m => <Avatar key={m.id} name={m.name} photo={m.photo_url} color={m.color} size={14} />)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {popupJob && (() => {
        const assignedMembers = members.filter(m => (popupJob.member_ids || []).includes(m.id));
        return (
          <div className="job-popup" onClick={e => e.stopPropagation()} data-testid="job-popup">
            <div className="popup-header">
              <span className="popup-title">{popupJob.name}</span>
              <button className="popup-close" onClick={() => setPopupJob(null)}><X size={14}/></button>
            </div>
            {popupJob.client && (
              <div className="popup-detail">
                <User size={13}/> <span>{popupJob.client} · {popupJob.job_number}</span>
              </div>
            )}
            {assignedMembers.length > 0 && (
              <div className="popup-detail">
                <Users size={13}/>
                <div className="avatar-stack-sm">
                  {assignedMembers.map(m => (
                    <button key={m.id} style={{ background: 'none', border: 'none', padding: 0, cursor: isReadOnly ? 'default' : 'pointer' }}
                      title={`View ${m.name} in Team`}
                      onClick={() => { if (!isReadOnly) { goToTeamMember(m.name); } }}>
                      <Avatar name={m.name} photo={m.photo_url} color={m.color} size={22} />
                    </button>
                  ))}
                  <span>{assignedMembers.map(m => m.name.split(' ')[0]).join(', ')}</span>
                </div>
              </div>
            )}
            {popupJob.address && (
              <div className="popup-detail"><MapPin size={13}/> <span>{popupJob.address}</span></div>
            )}
            <div className="popup-dates">
              {popupJob.install_start && <div><label>Starts</label><span>{fmtDate(popupJob.install_start)}</span></div>}
              {popupJob.install_end && <div><label>Ends</label><span>{fmtDate(popupJob.install_end)}</span></div>}
            </div>
            <button className="popup-view-btn" onClick={() => { setPopupJob(null); goToProjectSearch(popupJob.name); }}
              data-testid="popup-view-card-btn">
              <ExternalLink size={13} /> View Card
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// ============================================================
// TEAM PAGE
// ============================================================
function MemberProjectsDropdown({ projects, open }) {
  const { goToProjectSearch } = useData();
  if (!open) return null;
  return (
    <div className="punchlist-dropdown" data-testid="member-projects-dropdown" onClick={e => e.stopPropagation()}>
      {projects.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 12, padding: '8px 4px' }}>No projects assigned yet.</div>
      ) : projects.map(j => (
        <button key={j.id} className="job-card-member-chip" style={{ width: '100%', justifyContent: 'flex-start' }}
          onClick={() => goToProjectSearch(j.name)} data-testid={`member-project-link-${j.id}`}
          title={`View ${j.name}`}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: j.color || '#10B981', flexShrink: 0 }} />
          <span>{j.name}</span>
          {j.client && <span className="text-muted" style={{ fontSize: 11 }}>· {j.client}</span>}
        </button>
      ))}
    </div>
  );
}

function ClientsPage() {
  const { token, user } = useAuth();
  if (!['owner', 'manager'].includes(user?.workspace_role)) return <div className="page-content" role="alert" data-testid="clients-access-denied">Only workspace owners and managers can access clients.</div>;
  return <ClientsDirectory key={token} token={token} />;
}

function ClientsDirectory({ token }) {
  const directory = useClientDirectory(token, apiFetch);
  if (directory.loading) return <div className="page-content" role="status" data-testid="clients-loading">Loading clients…</div>;
  if (directory.error) return <div className="page-content" role="alert" data-testid="clients-error">{directory.error}<button className="btn" onClick={directory.refreshMembers} data-testid="clients-retry">Retry</button></div>;
  return <TeamPage directory={directory} />;
}

function TeamPage({ directory }) {
  const { members, jobs, addMember, editMember, archiveMember, refreshMembers, teamSearchQuery, setTeamSearchQuery, clientSearchQuery, setClientSearchQuery, isReadOnly } = { ...useData(), ...directory };
  const kind = directory ? 'client' : 'member';
  const noun = directory ? 'Client' : 'Member';
  const section = directory ? 'clients' : 'team';
  const { token, user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = usePersistentState(`${section}_search`, '');
  const [shareMember, setShareMember] = useState(null);
  const [viewMode, setViewMode] = usePersistentState(`${section}_view_mode`, 'card');
  const [expandedListMemberId, setExpandedListMemberId] = useState(null);
  const [openProjects, setOpenProjects] = useState(null);
  const [fieldSchema, setFieldSchema] = useState([]);
  const canManage = ['owner', 'manager'].includes(user?.workspace_role) && !isReadOnly;
  const sel = useMultiSelect();
  const bulkArchive = async () => {
    sel.setBusy(true);
    try { for (const id of sel.selected) await archiveMember(id); }
    finally { sel.exit(); }
  };

  // Searches arriving from a "view in Team/Clients" jump are temporary: they are
  // dropped when the user comes back to the page so the filter never sticks.
  const navSearchRef = useRef(false);
  const autoKey = `${section}_search_auto`;
  const incomingSearch = directory ? clientSearchQuery : teamSearchQuery;
  const clearIncomingSearch = directory ? setClientSearchQuery : setTeamSearchQuery;
  useEffect(() => {
    if (incomingSearch) {
      setSearch(incomingSearch);
      clearIncomingSearch('');
      navSearchRef.current = true;
      try { localStorage.setItem(autoKey, '1'); } catch { /* quota */ }
      return;
    }
    if (navSearchRef.current) return;
    if (localStorage.getItem(autoKey)) {
      localStorage.removeItem(autoKey);
      setSearch('');
    }
  }, [incomingSearch, clearIncomingSearch, autoKey, setSearch]);

  useEffect(() => {
    if (!token) return;
    apiFetch(`/${kind}-field-templates`, {}, token)
      .then(data => setFieldSchema(data.fields || []))
      .catch(() => {});
  }, [token, modalOpen, kind]);

  const isVis = useCallback((fid) => {
    const f = fieldSchema.find(x => x.id === fid);
    if (f) return f.showOnCard !== false;
    // Not found in schema. Fresh workspace (empty schema) → show defaults.
    if (fieldSchema.length === 0) return true;
    // Schema exists but this default field is missing → user deleted it.
    const isDefault = ['name', 'email', 'phone', 'role'].includes(fid);
    return !isDefault;
  }, [fieldSchema]);

  const getLabel = useCallback((fid, fallback) => {
    const f = fieldSchema.find(x => x.id === fid);
    return f?.label || fallback;
  }, [fieldSchema]);

  const filtered = useMemo(() => members
    .filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()) || (m.role || '').toLowerCase().includes(search.toLowerCase()) || (m.email || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [members, search]);

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (m) => { setEditTarget(m); setModalOpen(true); };

  const handleDuplicateMember = async (memberId) => {
    try {
      const newMember = await apiFetch(`/${kind}s/${memberId}/duplicate`, { method: 'POST' }, token);
      if (newMember?.id) refreshMembers();
    } catch (e) { console.error('Duplicate failed', e); }
  };

  const renderMemberDetails = (m) => (
    <div className="job-card-details">
      {isVis('role') && m.role && (
        <div className="job-card-field" data-testid={`${kind}-field-role-${m.id}`}>
          <span className="job-card-field-label">{getLabel('role', 'Role')}</span>
          <span className="job-card-field-value">{m.role}</span>
        </div>
      )}
      {isVis('email') && m.email && (
        <div className="job-card-field" data-testid={`${kind}-field-email-${m.id}`}>
          <span className="job-card-field-label">{getLabel('email', 'Email')}</span>
          <span className="job-card-field-value"><a href={`mailto:${m.email}`} className="job-card-link" data-testid={`${kind}-email-link-${m.id}`}>{m.email}</a></span>
        </div>
      )}
      {isVis('phone') && m.phone && (
        <div className="job-card-field" data-testid={`${kind}-field-phone-${m.id}`}>
          <span className="job-card-field-label">{getLabel('phone', 'Phone')}</span>
          <span className="job-card-field-value"><a href={`tel:${m.phone}`} className="job-card-link" data-testid={`${kind}-phone-link-${m.id}`}>{m.phone}</a></span>
        </div>
      )}
      {(m.custom_fields || []).filter(cf => isVis(cf.id) && hasFieldValue(cf)).map(cf => (
        <div key={cf.id} className="job-card-field" data-testid={`${kind}-field-custom-${m.id}-${cf.id}`}>
          <span className="job-card-field-label">{cf.label}</span>
          <span className="job-card-field-value">{fmtFieldValue(cf)}</span>
        </div>
      ))}
    </div>
  );

  const renderProjectsToggle = (m, assignedJobs, testSuffix = '') => {
    const isOpen = openProjects === m.id;
    return (
      <>
        <button className={`punchlist-toggle ${isOpen ? 'punchlist-toggle-open' : ''}`}
          onClick={(e) => { e.stopPropagation(); setOpenProjects(isOpen ? null : m.id); }}
          data-testid={`${kind}-projects-toggle${testSuffix}-${m.id}`}>
          <Briefcase size={13} />
          <span>{kind === 'client' ? 'Assigned Project' : 'Assigned Projects'}</span>
          {assignedJobs.length > 0 && <span className="punchlist-toggle-count">{assignedJobs.length}</span>}
          <ChevronDown size={14} className={`punchlist-chevron ${isOpen ? 'punchlist-chevron-open' : ''}`} />
        </button>
        <MemberProjectsDropdown projects={assignedJobs} open={isOpen} />
      </>
    );
  };

  return (
    <div className="page-content" data-testid={`${section}-page`}>
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope" data-testid={`${section}-heading`}>{directory ? 'Clients' : 'Team'}</h2>
          <p className="page-sub" data-testid={`${section}-count`}>{members.length} total {kind}s</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="jobs-view-toggle" data-testid={`${section}-view-toggle`}>
            <button className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              data-testid={`${section}-view-card-btn`} title="Card view">
              <LayoutGrid size={14} /> Cards
            </button>
            <button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              data-testid={`${section}-view-list-btn`} title="List view">
              <List size={14} /> List
            </button>
          </div>
          {canManage && members.length > 0 && (
            <button className={`view-toggle-btn ${sel.selectMode ? 'active' : ''}`}
              onClick={() => sel.selectMode ? sel.exit() : sel.setSelectMode(true)}
              data-testid={`${section}-archive-select-toggle`} title={`Select to archive`}>
              <Archive size={14} /> Select
            </button>
          )}
          {!isReadOnly && (
            <Btn onClick={openCreate} data-testid={`add-${kind}-btn`}>
              <Plus size={16} /> Add {noun}
            </Btn>
          )}
        </div>
      </div>

      {sel.selectMode && (
        <MultiSelectBar testid={`${section}-select-bar`}
          count={sel.selected.size} total={filtered.length}
          onSelectAll={() => sel.selectAll(filtered.map(m => m.id))}
          onClear={sel.clear} onCancel={sel.exit}
          onAction={bulkArchive} actionLabel="Archive selected" ActionIcon={Archive} busy={sel.busy} />
      )}

      <div className="toolbar">
        <div className="search-box">
          <Search size={15} className="search-icon" />
          <input className="search-input" placeholder={directory ? 'Search clients...' : 'Search team members...'} value={search}
            onChange={e => setSearch(e.target.value)} data-testid={`${section}-search-input`} />
          {search && (
            <button className="search-clear-btn" onClick={() => setSearch('')} data-testid={`${section}-search-clear`}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title={search ? 'No matches found' : `No ${directory ? 'clients' : 'team members'}`}
          desc={search ? `No ${kind}s matching "${search}".` : ''}
          action={!search && !isReadOnly && <Btn onClick={openCreate} className="mt-4" data-testid={`empty-add-${kind}-btn`}><Plus size={14}/> Add {noun}</Btn>} />
      ) : viewMode === 'list' ? (
        /* ===== TEAM LIST VIEW ===== */
        <div className="job-list-table" data-testid={`${section}-list-table`}>
          {filtered.map(m => {
            const assignedJobs = jobs.filter(j => directory ? (m.project_ids || []).includes(j.id) : (j.member_ids || []).includes(m.id));
            const isExpanded = expandedListMemberId === m.id;
            return (
              <div key={m.id} className="job-list-item-wrap" data-testid={`${section}-list-wrap-${m.id}`}>
                <div
                  className={`job-list-row-item${isExpanded ? ' jl-active' : ''}`}
                  data-testid={`${section}-list-row-${m.id}`}
                  onClick={() => setExpandedListMemberId(isExpanded ? null : m.id)}>
                  <Avatar name={m.name} photo={m.photo_url} color={m.color} size={32} />
                  <div className="job-list-main">
                    <div className="job-list-title">{m.name}</div>
                    {m.role && <div className="job-list-sub">{m.role}</div>}
                  </div>
                  <div className="job-list-meta">
                    <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Briefcase size={11}/> {assignedJobs.length}
                    </span>
                    <ChevronDown size={14} className={`jl-chevron ${isExpanded ? 'jl-chevron-open' : ''}`} />
                  </div>
                  <div className="job-list-actions" onClick={e => e.stopPropagation()}>
                    {sel.selectMode && <SelectCheck id={m.id} selected={sel.selected.has(m.id)} onToggle={sel.toggle} testid={`select-${kind}-${m.id}`} />}
                    {!isReadOnly && (
                      <>
                        <button className="icon-btn" onClick={() => openEdit(m)} data-testid={`edit-${kind}-list-${m.id}`} title={`Edit ${kind}`}><Edit2 size={13}/></button>
                        <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(m)} data-testid={`delete-${kind}-list-${m.id}`} title={`Archive ${kind}`}><Archive size={13}/></button>
                      </>
                    )}
                  </div>
                </div>
                {/* ===== INLINE CARD EXPANSION ===== */}
                {isExpanded && (
                  <div className="job-list-expanded-card" id={`${kind}-card-${m.id}`} data-testid={`${section}-list-expanded-${m.id}`}>
                    <div className="job-card-top" style={{ borderLeft: 'none' }}>
                      <div className="job-card-title-row">
                        <div className="job-card-title-info" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={m.name} photo={m.photo_url} color={m.color} size={36} />
                          <span className="job-card-name font-manrope">{m.name}</span>
                        </div>
                        <div className="job-card-actions">
                          <button className="icon-btn" onClick={() => setShareMember(m)} data-testid={`share-${kind}-list-${m.id}`} title="Share"><Share2 size={13} /></button>
                          {!isReadOnly && (
                            <>
                              <button className="icon-btn" onClick={() => handleDuplicateMember(m.id)} data-testid={`dup-${kind}-list-${m.id}`} title="Duplicate"><Copy size={13} /></button>
                              <button className="icon-btn" onClick={() => openEdit(m)} data-testid={`edit-${kind}-list-expanded-${m.id}`} title="Edit"><Edit2 size={13} /></button>
                              <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(m)} data-testid={`del-${kind}-list-expanded-${m.id}`} title="Archive"><Archive size={13} /></button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {renderMemberDetails(m)}
                    {renderProjectsToggle(m, assignedJobs, '-list')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ===== TEAM CARD VIEW ===== */
        <div className="job-cards-grid" data-testid={`${section}-cards-grid`}>
          {filtered.map(m => {
            const assignedJobs = jobs.filter(j => directory ? (m.project_ids || []).includes(j.id) : (j.member_ids || []).includes(m.id));
            return (
              <div key={m.id} id={`${kind}-card-${m.id}`} className="job-card" data-testid={`${kind}-card-${m.id}`}>
                <div className="job-card-top" style={{ borderLeft: 'none' }}>
                  <div className="job-card-title-row">
                    <div className="job-card-title-info" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={m.name} photo={m.photo_url} color={m.color} size={40} />
                      <button className="job-card-name-btn" onClick={() => openEdit(m)}
                        data-testid={`${kind}-open-${m.id}`}>
                        <span className="job-card-name font-manrope">{m.name}</span>
                      </button>
                    </div>
                    <div className="job-card-actions">
                      {sel.selectMode && <SelectCheck id={m.id} selected={sel.selected.has(m.id)} onToggle={sel.toggle} testid={`select-${kind}-card-${m.id}`} />}
                      <button className="icon-btn" onClick={() => setShareMember(m)}
                        data-testid={`share-${kind}-${m.id}`} title={`Share ${directory ? 'client' : 'profile'}`}>
                        <Share2 size={13}/>
                      </button>
                      {!isReadOnly && (
                        <>
                          <button className="icon-btn" onClick={() => handleDuplicateMember(m.id)}
                            data-testid={`duplicate-${kind}-${m.id}`} title={`Duplicate ${kind}`}>
                            <Copy size={13} />
                          </button>
                          <button className="icon-btn" onClick={() => openEdit(m)} data-testid={`edit-${kind}-${m.id}`} title={`Edit ${kind}`}>
                            <Edit2 size={13}/>
                          </button>
                          <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(m)}
                            data-testid={`delete-${kind}-${m.id}`} title={`Archive ${kind}`}>
                            <Archive size={13}/>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {renderMemberDetails(m)}
                {renderProjectsToggle(m, assignedJobs)}
              </div>
            );
          })}
        </div>
      )}

      <MemberModal open={modalOpen} onClose={() => setModalOpen(false)}
        onSave={editTarget ? (data) => editMember(editTarget.id, data) : addMember}
        member={editTarget} token={token} kind={kind} />

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await archiveMember(deleteTarget.id); setDeleteTarget(null); }}
        title={`Archive ${noun}`} message={`Archive "${deleteTarget?.name}"? They can be restored from the Archive menu.`} confirmLabel="Archive" />
      {shareMember && (directory
        ? <ClientShareDialog client={shareMember} token={token} apiFetch={apiFetch} Modal={Modal} Btn={Btn} onClose={() => setShareMember(null)} />
        : <ShareMemberModal member={shareMember} onClose={() => setShareMember(null)} />)}
    </div>
  );
}

// ============================================================
// MY COMPANIES TAB
// ============================================================
function CompaniesTab({ token, user, onSwitch, onNavigate }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  // Sent invites (legacy username-based invites — still viewable/cancelable)
  const [sentInvites, setSentInvites] = useState([]);
  const [showInvites, setShowInvites] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await apiFetch('/profile/companies', {}, token);
      setCompanies(list);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const loadSentInvites = useCallback(async () => {
    try {
      const invites = await apiFetch('/invites/sent', {}, token);
      setSentInvites(invites);
    } catch (_) {}
  }, [token]);

  useEffect(() => { loadSentInvites(); }, [loadSentInvites]);

  const cancelInvite = async (inviteId) => {
    try {
      await apiFetch(`/invites/${inviteId}`, { method: 'DELETE' }, token);
      await loadSentInvites();
    } catch (e) { setErr(e.message); }
  };

  const removeManager = async (companyId, managerId) => {
    if (!window.confirm('Remove this manager from the workspace?')) return;
    try {
      await apiFetch(`/workspaces/managers/${companyId}/${managerId}`, { method: 'DELETE' }, token);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const createCompany = async () => {
    if (!newName.trim()) return;
    setCreating(true); setErr('');
    try {
      await apiFetch('/profile/companies', { method: 'POST', body: JSON.stringify({ company: newName.trim() }) }, token);
      setNewName('');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setCreating(false); }
  };

  const switchTo = async (companyId) => {
    try {
      const data = await apiFetch(`/auth/switch/${companyId}`, { method: 'POST' }, token);
      onSwitch(data.token, data.user);
    } catch (e) { setErr(e.message); }
  };

  const archiveCompany = async (companyId) => {
    const isActive = companyId === user?.id;
    const remaining = companies.filter(c => c.id !== companyId);
    if (remaining.length === 0) {
      setErr('You cannot archive your only workspace. Create another workspace first.');
      return;
    }
    try {
      await apiFetch(`/profile/companies/${companyId}`, { method: 'DELETE' }, token);
      if (isActive && remaining.length > 0) {
        await switchTo(remaining[0].id);
      }
      await load();
    } catch (e) { setErr(e.message); }
  };

  const primaryId = user?.primary_account_id || user?.id;

  if (loading) return <div className="loading-spinner" />;

  return (
    <div className="settings-section" data-testid="companies-settings">
      <h3 className="settings-section-title font-manrope">My Workspaces</h3>
      <p className="text-muted" style={{marginBottom: 20}}>
        Manage multiple workspaces under one login.
      </p>
      {err && <div className="alert-error mb-4">{err}</div>}

      <div className="companies-list">
        {companies.map(co => {
          const isActive = co.id === user?.id;
          const isPrimary = co.id === primaryId && !co.primary_account_id;
          const isManaged = co.is_managed;
          const managers = co.managers || [];
          return (
            <div key={co.id} className={`company-item ${isActive ? 'company-item-active' : ''}`}
              data-testid={`company-item-${co.id}`}>
              <div className="company-item-info">
                <Avatar name={co.company || co.username} color="hsl(var(--primary))" size={36} />
                <div>
                  <div className="company-item-name font-manrope">{co.company || co.username}</div>
                  <div className="company-item-meta">
                    {isManaged ? <span className="company-badge-managed">Managed</span>
                      : isPrimary ? <span className="company-badge-primary">Primary</span>
                      : <span className="company-badge-sub">Workspace</span>}
                    {isActive && <span className="company-badge-active">Active</span>}
                    {!isManaged && managers.length > 0 && (
                      <span className="company-badge-managers"><Users size={10}/> {managers.length} manager{managers.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="company-item-actions">
                {!isActive && (
                  <Btn size="sm" onClick={() => switchTo(co.id)} data-testid={`switch-company-${co.id}`}>
                    Switch
                  </Btn>
                )}
                {!isManaged && (
                  <button className="icon-btn icon-btn-danger" onClick={() => archiveCompany(co.id)}
                    data-testid={`archive-company-${co.id}`} title="Archive workspace">
                    <Archive size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="company-new-form">
        <h4 className="font-manrope" style={{marginBottom: 12, fontSize: 14}}>Add New Workspace</h4>
        <div className="address-row">
          <input className="form-input" placeholder="Workspace name..." value={newName}
            onChange={e => setNewName(e.target.value)} data-testid="new-company-name-input"
            onKeyDown={e => e.key === 'Enter' && createCompany()} />
          <Btn onClick={createCompany} disabled={creating || !newName.trim()} data-testid="create-company-btn">
            {creating ? 'Creating...' : <><Plus size={14}/> Create</>}
          </Btn>
        </div>
      </div>

      {/* Sent Invitations */}
      <div className="company-trash-section" data-testid="sent-invites-section">
        <button className="company-trash-toggle" onClick={() => setShowInvites(s => !s)}
          data-testid="toggle-sent-invites-btn">
          <Mail size={14} />
          <span>Sent Invitations ({sentInvites.length})</span>
          <ChevronDown size={14} className={showInvites ? 'rotate-180' : ''} />
        </button>
        {showInvites && (
          <div className="company-trash-list">
            {sentInvites.length === 0 ? (
              <p className="text-muted" style={{fontSize: 12, padding: '12px 0'}}>No invitations sent yet.</p>
            ) : (
              sentInvites.map(inv => (
                <div key={inv.id} className="company-trash-item" data-testid={`sent-invite-${inv.id}`}>
                  <div className="company-trash-item-info">
                    <Avatar name={inv.to_username} color="hsl(var(--primary))" size={28} />
                    <div>
                      <div className="company-item-name font-manrope" style={{fontSize: 13}}>{inv.to_username}</div>
                      <div className="company-trash-meta" style={{gap: 6}}>
                        <span>{inv.company_name}</span>
                        <span className={`invite-status-badge invite-status-${inv.status}`}>{inv.status}</span>
                      </div>
                    </div>
                  </div>
                  {inv.status === 'pending' && (
                    <button className="icon-btn icon-btn-danger" onClick={() => cancelInvite(inv.id)}
                      data-testid={`cancel-invite-${inv.id}`} title="Cancel invitation">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Received Invitations */}
      <ReceivedInvitationsSection />
    </div>
  );
}

// ============================================================
// SETTINGS PAGE (Profile + Theme Customizer)
// ============================================================
// ============================================================
// CHANGE PASSWORD SECTION (shown in Settings > Profile for non-Google users)
// ============================================================
function ChangePasswordSection({ token }) {
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.old_password || !form.new_password || !form.confirm_password) {
      setMsg('All fields are required.'); setMsgType('error'); return;
    }
    if (form.new_password.length < 6 || !/\d/.test(form.new_password)) {
      setMsg('New password must be at least 6 characters and contain at least 1 number.'); setMsgType('error'); return;
    }
    if (form.new_password !== form.confirm_password) {
      setMsg('New passwords do not match.'); setMsgType('error'); return;
    }
    setSaving(true); setMsg('');
    try {
      await apiFetch('/auth/change_password', {
        method: 'POST',
        body: JSON.stringify({ old_password: form.old_password, new_password: form.new_password }),
      }, token);
      setMsg('Password changed successfully.'); setMsgType('success');
      setForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (e) { setMsg(e.message); setMsgType('error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="settings-section" style={{ marginTop: 24, borderTop: '1px solid hsl(var(--border))', paddingTop: 20 }} data-testid="change-password-section">
      <h3 className="settings-section-title font-manrope" style={{ fontSize: 15, marginBottom: 12 }}>Change Password</h3>
      <div className="flex flex-col gap-4">
        <PasswordInput label="Current Password" value={form.old_password} onChange={e => set('old_password', e.target.value)}
          placeholder="Enter current password" data-testid="current-password-input" />
        <PasswordInput label="New Password" value={form.new_password} onChange={e => set('new_password', e.target.value)}
          placeholder="Min 6 chars, 1 number" data-testid="new-password-input" />
        <PasswordInput label="Confirm New Password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)}
          placeholder="Repeat new password" data-testid="confirm-password-input" />
        {msg && <div className={msgType === 'success' ? 'alert-success' : 'alert-error'} data-testid="change-password-msg">{msg}</div>}
        <Btn onClick={handleSubmit} disabled={saving} data-testid="change-password-btn" className="w-fit">
          <Lock size={14} /> {saving ? 'Saving…' : 'Change Password'}
        </Btn>
      </div>
    </div>
  );
}

// ---- Workspace App Icon (favicon + PWA icon) upload & crop ----
function WorkspaceIconSection({ token, user, setUser }) {
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) { setMsg('Only JPG or PNG allowed'); return; }
    setMsg(''); setCropSrc(URL.createObjectURL(file));
  };
  const closeCrop = () => { setCropSrc(null); setCrop({ x: 0, y: 0 }); setZoom(1); setCropPixels(null); };

  const saveCroppedIcon = async () => {
    if (!cropSrc || !cropPixels) return;
    setSaving(true); setMsg('');
    try {
      const blob = await getCroppedImageBlob(cropSrc, cropPixels);
      const fd = new FormData();
      fd.append('file', blob, 'icon.png');
      const res = await fetch(`${BACKEND_URL}/api/profile/workspace-icon`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      const me = await apiFetch('/auth/me', {}, token);
      setUser(me);
      setMsg('App icon updated!');
      closeCrop();
    } catch (err) { setMsg(err.message); }
    finally { setSaving(false); }
  };

  const handleRemove = async () => {
    setSaving(true); setMsg('');
    try {
      await apiFetch('/profile/workspace-icon', { method: 'DELETE' }, token);
      const me = await apiFetch('/auth/me', {}, token);
      setUser(me);
      setMsg('App icon reset to default');
    } catch (err) { setMsg(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="settings-section" style={{ marginTop: 24, borderTop: '1px solid hsl(var(--border))', paddingTop: 20 }} data-testid="workspace-icon-section">
      <h3 className="settings-section-title font-manrope" style={{ fontSize: 15, marginBottom: 4 }}>App Icon</h3>
      <p style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginBottom: 12 }}>
        Custom browser tab &amp; home-screen icon for your workspace — seen by your team and anyone viewing via a guest access code.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {user?.workspace_icon_url ? (
          <img src={`${BACKEND_URL}${user.workspace_icon_url}`} alt="App icon" className="workspace-icon-preview" data-testid="workspace-icon-preview" />
        ) : (
          <div className="workspace-icon-preview workspace-icon-preview-empty" data-testid="workspace-icon-preview-empty"><ImageIcon size={20} /></div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <label className="btn btn-outline" style={{ cursor: saving ? 'wait' : 'pointer' }}>
            <Camera size={14} /> {user?.workspace_icon_url ? 'Change Icon' : 'Upload Icon'}
            <input type="file" accept="image/png,image/jpeg" hidden onChange={onFileSelect} disabled={saving} data-testid="workspace-icon-file-input" />
          </label>
          {user?.workspace_icon_url && (
            <button type="button" className="btn btn-ghost" onClick={handleRemove} disabled={saving} data-testid="remove-workspace-icon-btn">Remove</button>
          )}
        </div>
      </div>
      {msg && <div className={/updated|reset/.test(msg) ? 'alert-success' : 'alert-error'} style={{ marginTop: 10 }} data-testid="workspace-icon-msg">{msg}</div>}

      <Modal open={!!cropSrc} onClose={closeCrop} title="Crop App Icon" width="max-w-md">
        <div className="icon-crop-stage" data-testid="icon-crop-stage">
          {cropSrc && (
            <Cropper
              image={cropSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCropPixels(pixels)}
            />
          )}
        </div>
        <input type="range" min={1} max={3} step={0.01} value={zoom}
          onChange={e => setZoom(Number(e.target.value))}
          className="icon-crop-zoom" data-testid="icon-crop-zoom-slider" />
        <div className="flex justify-end gap-2" style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-ghost" onClick={closeCrop} data-testid="icon-crop-cancel-btn">Cancel</button>
          <Btn onClick={saveCroppedIcon} disabled={saving || !cropPixels} data-testid="icon-crop-save-btn">
            {saving ? 'Saving…' : 'Save Icon'}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// ---- Join a Workspace via Manager Access Code ----
function JoinWorkspaceSection({ token }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const handleJoin = async () => {
    if (!code.trim()) { setMsg('Invalid code'); return; }
    setLoading(true); setMsg('');
    try {
      const res = await apiFetch('/workspaces/join', { method: 'POST', body: JSON.stringify({ code: code.trim() }) }, token);
      setMsg(`You're now a manager of ${res.company}!`);
      setCode('');
    } catch (e) { setMsg(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="settings-section" style={{ marginTop: 24, borderTop: '1px solid hsl(var(--border))', paddingTop: 20 }} data-testid="join-workspace-section">
      <h3 className="settings-section-title font-manrope" style={{ fontSize: 15, marginBottom: 4 }}>Join a Workspace</h3>
      <p style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginBottom: 12 }}>
        Have a manager access code? Enter it to instantly become a manager of that workspace.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input className="form-input" value={code} onChange={e => setCode(e.target.value)}
          placeholder="Enter manager access code" data-testid="join-workspace-code-input" style={{ flex: 1, minWidth: 180 }} />
        <Btn onClick={handleJoin} disabled={loading || !code.trim()} data-testid="join-workspace-btn">
          {loading ? 'Joining…' : 'Join'}
        </Btn>
      </div>
      {msg && <div className={/manager of/.test(msg) ? 'alert-success' : 'alert-error'} style={{ marginTop: 10 }} data-testid="join-workspace-msg">{msg}</div>}
    </div>
  );
}

// ============================================================
// SETTINGS PAGE
// ============================================================
// ---- Received Invitations in Workspaces tab ----
function ReceivedInvitationsSection() {
  const { pendingInvites, respondToInvite, loadPendingInvites } = useData();
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(null);

  const respond = async (invId, action) => {
    setProcessing(invId);
    try { await respondToInvite(invId, action); } finally { setProcessing(null); }
  };

  return (
    <div className="company-trash-section" data-testid="received-invites-section">
      <button className="company-trash-toggle" onClick={() => { setOpen(o => !o); if (!open && loadPendingInvites) loadPendingInvites(); }}
        data-testid="toggle-received-invites-btn">
        <Bell size={14} />
        <span>Received Invitations ({pendingInvites?.length || 0})</span>
        {(pendingInvites?.length || 0) > 0 && <span className="notification-badge" style={{ position: 'relative', top: 0, right: 0, marginLeft: 4 }}>{pendingInvites.length}</span>}
        <ChevronDown size={14} className={open ? 'rotate-180' : ''} />
      </button>
      {open && (
        <div className="company-trash-list">
          {!pendingInvites?.length ? (
            <p className="text-muted" style={{ fontSize: 12, padding: '12px 0' }}>No pending invitations.</p>
          ) : (
            pendingInvites.map(inv => (
              <div key={inv.id} className="company-trash-item" data-testid={`received-invite-${inv.id}`}>
                <div className="company-trash-item-info">
                  <Avatar name={inv.from_username} color="hsl(var(--primary))" size={28} />
                  <div>
                    <div className="company-item-name font-manrope" style={{ fontSize: 13 }}>{inv.from_username}</div>
                    <div className="company-trash-meta">{inv.company_name}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Btn size="sm" onClick={() => respond(inv.id, 'accept')} disabled={processing === inv.id}
                    data-testid={`accept-invite-settings-${inv.id}`}><UserCheck size={13}/> Accept</Btn>
                  <Btn size="sm" variant="outline" onClick={() => respond(inv.id, 'deny')} disabled={processing === inv.id}
                    data-testid={`deny-invite-settings-${inv.id}`}><UserX size={13}/> Deny</Btn>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SettingsPage({ guestSessionBanner }) {
  const { token, user, setUser, handleAuth } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { goToPage } = useData();
  const [tab, setTab] = useState(null); // null = show mobile menu, string = show tab content
  const [form, setForm] = useState({ display_name: user?.display_name || '', phone: user?.phone || '', company: user?.company || '', company_bio: user?.company_bio || '', company_phone: user?.company_phone || '', company_password: user?.workspace_code || '', manager_access_code: user?.manager_code || '', guest_edits_allowed: user?.guest_edits_allowed || false });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [showCompanyPw, setShowCompanyPw] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);
  const [showMgrPw, setShowMgrPw] = useState(false);
  const [mgrPwTouched, setMgrPwTouched] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // On desktop, default to 'profile' tab
  useEffect(() => {
    if (!isMobile && !tab) setTab('profile');
  }, [isMobile, tab]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const saveProfile = async () => {
    setSaving(true); setMsg('');
    try {
      // Only send company_password/manager_access_code if the user actually
      // edited those fields this session — otherwise the always-blank input
      // would wipe the saved codes on every unrelated settings save.
      const body = { ...form };
      if (!pwTouched) delete body.company_password;
      if (!mgrPwTouched) delete body.manager_access_code;
      const updated = await apiFetch('/profile', { method: 'PATCH', body: JSON.stringify(body) }, token);
      setUser(updated);
      setMsg('Profile saved!');
      setPwTouched(false);
      setMgrPwTouched(false);
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      setMsg('Only JPG or PNG allowed'); return;
    }
    setUploadingLogo(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/profile/company-logo`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      const me = await apiFetch('/auth/me', {}, token);
      setUser(me);
      setMsg('Logo updated!');
    } catch (err) { setMsg(err.message); }
    finally { setUploadingLogo(false); e.target.value = ''; }
  };
  const handleLogoRemove = async () => {
    setUploadingLogo(true); setMsg('');
    try {
      await apiFetch('/profile/company-logo', { method: 'DELETE' }, token);
      const me = await apiFetch('/auth/me', {}, token);
      setUser(me);
      setMsg('Logo reset to default');
    } catch (err) { setMsg(err.message); }
    finally { setUploadingLogo(false); }
  };

  const moreMenuItems = [
    { id: 'profile', label: 'Profile', Icon: User, action: () => setTab('profile') },
    ...(['owner', 'manager'].includes(user?.workspace_role) ? [{ id: 'clients', label: 'Clients', Icon: UserSquare, action: () => goToPage('clients') }] : []),
    { id: 'company', label: 'Workspace', Icon: Building, action: () => setTab('company') },
    { id: 'archive', label: 'Archive', Icon: Archive, action: () => goToPage('archive') },
    { id: 'logout', label: 'Log Out', Icon: LogOut, action: () => handleAuth(null), danger: true },
  ];

  // Mobile: show expanded menu when no tab selected
  if (isMobile && !tab) {
    return (
      <div className="page-content" data-testid="settings-page">
        <div className="page-header">
          <h2 className="page-title font-manrope">More</h2>
        </div>
        <div className="more-menu" data-testid="more-menu">
          {moreMenuItems.map(({ id, label, Icon, action, danger }) => (
            <button key={id} className={`more-menu-item ${danger ? 'more-menu-item-danger' : ''}`}
              onClick={action} data-testid={`more-menu-${id}`}>
              <Icon size={20} />
              <span>{label}</span>
              <ChevronRight size={16} className="more-menu-arrow" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-content" data-testid="settings-page">
      <div className="page-header">
        {isMobile && tab && (
          <button className="icon-btn" style={{marginRight: 8}} onClick={() => setTab(null)} data-testid="settings-back-btn">
            <ChevronLeft size={20} />
          </button>
        )}
        <h2 className="page-title font-manrope">{isMobile ? (tab === 'profile' ? 'Profile' : tab === 'company' ? 'Workspace' : 'Settings') : 'Settings'}</h2>
      </div>
      {!isMobile && (
      <div className="settings-layout">
        <div className="settings-sidebar">
          {[
            { id: 'profile', label: 'Profile', Icon: User },
            { id: 'company', label: 'Workspace', Icon: Building },
          ].map(({ id, label, Icon }) => (
            <button key={id}
              className={`settings-tab ${tab === id ? 'active' : ''}`}
              onClick={() => setTab(id)}
              data-testid={`settings-tab-${id}`}>
              <Icon size={16}/> {label}
            </button>
          ))}
        </div>
        <div className="settings-content">
          {tab === 'profile' && (
            <div className="settings-section" data-testid="profile-settings">
              <h3 className="settings-section-title font-manrope">Your Profile</h3>
              <div className="settings-top-actions">
                {msg && <div className={msg.includes('saved') || msg.includes('updated') ? 'alert-success' : 'alert-error'}>{msg}</div>}
                <Btn onClick={saveProfile} disabled={saving || user?.is_code_session} data-testid="save-profile-btn" className="w-fit">
                  <Save size={14}/> {saving ? 'Saving…' : 'Save Profile'}
                </Btn>
              </div>
              {user?.is_code_session && (
                <p className="field-hint" style={{marginTop:8,marginBottom:12}} data-testid="code-session-profile-note">A workspace code session can't change profile settings.</p>
              )}
              <div className="flex flex-col gap-4">
                <div className="profile-photo-section" data-testid="profile-photo-section">
                  <Avatar name={user?.display_name || user?.username} photo={user?.photo_url} size={72} color="hsl(var(--primary))" />
                  {!user?.is_code_session && (
                  <label className="btn btn-outline profile-photo-btn" data-testid="upload-profile-photo">
                    <Camera size={14} /> Change Photo
                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/profile/photo`, {
                          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
                        });
                        const data = await res.json();
                        if (res.ok) { setUser(prev => ({ ...prev, photo_url: data.photo_url })); setMsg('Photo updated!'); }
                        else setMsg(data.detail || 'Upload failed');
                      } catch { setMsg('Upload failed'); }
                    }} />
                  </label>
                  )}
                </div>
                <Input label="Display Name" value={form.display_name}
                  onChange={e => set('display_name', e.target.value)}
                  data-testid="profile-display-name" />
                <Input label="Phone" type="tel" value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  data-testid="profile-phone" />
                <div className="form-field">
                  <label className="form-label">Email</label>
                  <input className="form-input" value={user?.email || ''} disabled />
                </div>
              </div>
              {/* Appearance — Dark/Light mode */}
              <div className="settings-subsection-divider" data-testid="appearance-divider" />
              <div className="dark-mode-toggle-row" data-testid="dark-mode-toggle-row">
                <div>
                  <div style={{fontWeight:600,fontSize:14}}>Appearance</div>
                  <div style={{fontSize:12,color:'hsl(var(--muted-foreground))'}}>Switch between light and dark mode</div>
                </div>
                <button className="dark-mode-pill-btn" onClick={toggleDarkMode} data-testid="dark-mode-toggle">
                  {darkMode ? <Sun size={15}/> : <Moon size={15}/>}
                  <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>
              {/* Join a Workspace via Manager Access Code */}
              {!user?.is_code_session && <JoinWorkspaceSection token={token} />}
              {/* Submit a Suggestion */}
              <div style={{ marginBottom: 20 }}>
                <SuggestionBox token={token} compact={false} />
              </div>
              {/* Change Password */}
              {!user?.google_id && !user?.is_code_session && (
                <ChangePasswordSection token={token} />
              )}
            </div>
          )}
          {tab === 'company' && (
            <div className="settings-section" data-testid="company-settings">
              <h3 className="settings-section-title font-manrope">Workspace Settings</h3>
              <div className="settings-top-actions">
                {msg && <div className={msg.includes('saved') ? 'alert-success' : 'alert-error'}>{msg}</div>}
                <Btn onClick={saveProfile} disabled={saving || user?.is_code_session} data-testid="save-company-btn" className="w-fit">
                  <Save size={14}/> {saving ? 'Saving…' : 'Save'}
                </Btn>
              </div>
              {user?.is_code_session && (
                <p className="field-hint" style={{marginTop:8,marginBottom:12}} data-testid="code-session-company-note">A workspace code session can't change workspace settings.</p>
              )}
              <div className="flex flex-col gap-4">
                {/* Workspace Logo */}
                <div className="form-field" data-testid="workspace-logo-section">
                  <label className="form-label">Workspace Logo</label>
                  <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginBottom:10}}>
                    Upload a JPG or PNG to replace the Projek wordmark in your sidebar. Max 5MB.
                  </p>
                  <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                    <div style={{
                      width:120, height:56, borderRadius:8, background:'hsl(var(--sidebar))',
                      display:'flex', alignItems:'center', justifyContent:'center', padding:8,
                      border:'1px solid hsl(var(--border))'
                    }}>
                      {user?.company_logo_url ? (
                        <img src={`${BACKEND_URL}${user.company_logo_url}`} alt="Workspace logo"
                          style={{maxHeight:'100%',maxWidth:'100%',objectFit:'contain'}}
                          data-testid="workspace-logo-preview" />
                      ) : (
                        <span className="font-manrope" style={{color:'hsl(var(--sidebar-fg))',fontWeight:800,fontSize:15}}>
                          Projek<span className="brand-dot">.</span>
                        </span>
                      )}
                    </div>
                    <label className="btn btn-outline" data-testid="upload-workspace-logo-btn"
                      style={{cursor: uploadingLogo ? 'wait' : 'pointer'}}>
                      <Camera size={14}/> {uploadingLogo ? 'Uploading…' : (user?.company_logo_url ? 'Change Logo' : 'Upload Logo')}
                      <input type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" hidden
                        onChange={handleLogoUpload} disabled={uploadingLogo} />
                    </label>
                    {user?.company_logo_url && (
                      <button type="button" className="btn btn-ghost" onClick={handleLogoRemove}
                        disabled={uploadingLogo} data-testid="remove-workspace-logo-btn">
                        <X size={14}/> Remove
                      </button>
                    )}
                  </div>
                </div>

                <WorkspaceIconSection token={token} user={user} setUser={setUser} />

                <Input label="Workspace Name" value={form.company}
                  onChange={e => set('company', e.target.value)}
                  data-testid="company-name-input" />
                <p className="field-hint" data-testid="company-name-optional-hint">
                  Optional — leave blank if you'd rather not set a workspace name.
                </p>

                {/* Workspace Access Code */}
                <div className="form-field">
                  <label className="form-label">Workspace Access Code</label>
                  <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginBottom:8}}>
                    Set a workspace access code to make your private workspace available to be viewed with that code. Leave blank to keep the workspace open.
                  </p>
                  <div className="address-row">
                    <input type={showCompanyPw ? 'text' : 'password'} className="form-input"
                      value={form.company_password || ''}
                      onChange={e => { set('company_password', e.target.value); setPwTouched(true); }}
                      placeholder="Set access code (optional)…"
                      data-testid="company-password-input" />
                    <button type="button" className="icon-btn" onClick={() => setShowCompanyPw(s=>!s)}>
                      {showCompanyPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                </div>

                {/* Workspace Manager Access Code */}
                <div className="form-field">
                  <label className="form-label">Workspace Manager Access Code</label>
                  <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginBottom:8}}>
                    Create a separate code to give someone manager permissions. Anyone who redeems it instantly becomes a manager of this workspace.
                  </p>
                  <div className="address-row">
                    <input type={showMgrPw ? 'text' : 'password'} className="form-input"
                      value={form.manager_access_code || ''}
                      onChange={e => { set('manager_access_code', e.target.value); setMgrPwTouched(true); }}
                      placeholder="Set manager access code (optional)…"
                      data-testid="manager-access-code-input" />
                    <button type="button" className="icon-btn" onClick={() => setShowMgrPw(s=>!s)}>
                      {showMgrPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                </div>

                {msg && <div className={msg.includes('saved') ? 'alert-success' : 'alert-error'} style={{display:'none'}}>{msg}</div>}
              </div>
              {/* Visibly separated Workspaces list — same tab, distinct section */}
              {!user?.is_code_session && (<>
                <div className="settings-subsection-divider" data-testid="workspaces-subsection-divider" />
                <CompaniesTab token={token} user={user} onSwitch={handleAuth} onNavigate={goToPage} />
              </>)}
            </div>
          )}
        </div>
      </div>
      )}
      {/* Mobile: show single tab content with back button */}
      {isMobile && tab && (
        <div className="settings-mobile-content">
          {tab === 'profile' && guestSessionBanner}
          {tab === 'profile' && (
            <div className="settings-section" data-testid="profile-settings">
              <div className="settings-top-actions">
                {msg && <div className={msg.includes('saved') ? 'alert-success' : 'alert-error'}>{msg}</div>}
                <Btn onClick={saveProfile} disabled={saving || user?.is_code_session} data-testid="save-profile-btn" className="w-fit">
                  <Save size={14}/> {saving ? 'Saving…' : 'Save Profile'}
                </Btn>
              </div>
              {user?.is_code_session && (
                <p className="field-hint" style={{marginTop:8,marginBottom:12}} data-testid="mobile-code-session-profile-note">A workspace code session can't change profile settings.</p>
              )}
              <div className="flex flex-col gap-4">
                <Input label="Display Name" value={form.display_name}
                  onChange={e => set('display_name', e.target.value)}
                  data-testid="profile-display-name" />
                <Input label="Phone" type="tel" value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  data-testid="profile-phone" />
                <div className="form-field">
                  <label className="form-label">Email</label>
                  <input className="form-input" value={user?.email || ''} disabled />
                </div>
                <div style={{marginTop:24,paddingTop:20,borderTop:'1px solid hsl(var(--border))'}}>
                  <div className="dark-mode-toggle-row" data-testid="mobile-dark-mode-toggle-row">
                    <div>
                      <div style={{fontWeight:600,fontSize:14}}>Appearance</div>
                      <div style={{fontSize:12,color:'hsl(var(--muted-foreground))'}}>Light or dark mode</div>
                    </div>
                    <button className="dark-mode-pill-btn" onClick={toggleDarkMode} data-testid="mobile-dark-mode-toggle">
                      {darkMode ? <Sun size={15}/> : <Moon size={15}/>}
                      <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                  </div>
                </div>
                {!user?.is_code_session && <JoinWorkspaceSection token={token} />}
                {!user?.google_id && !user?.is_code_session && (
                <div style={{marginTop:24,paddingTop:20,borderTop:'1px solid hsl(var(--border))'}}>
                  <ChangePasswordSection token={token} />
                </div>
                )}
                <div style={{marginTop:20,paddingTop:20,borderTop:'1px solid hsl(var(--border))'}}>
                  <SuggestionBox token={token} />
                </div>
              </div>
            </div>
          )}
          {tab === 'company' && (
            <>
              <div className="settings-section" data-testid="mobile-company-settings">
                <div className="settings-top-actions">
                  {msg && <div className={msg.includes('saved') ? 'alert-success' : 'alert-error'}>{msg}</div>}
                  <Btn onClick={saveProfile} disabled={saving || user?.is_code_session} data-testid="mobile-save-company-btn" className="w-fit">
                    <Save size={14}/> {saving ? 'Saving…' : 'Save'}
                  </Btn>
                </div>
                {user?.is_code_session && (
                  <p className="field-hint" style={{marginTop:8,marginBottom:12}} data-testid="mobile-code-session-company-note">A workspace code session can't change workspace settings.</p>
                )}
                <div className="flex flex-col gap-4">
                  <div className="form-field" data-testid="mobile-workspace-logo-section">
                    <label className="form-label">Workspace Logo</label>
                    <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginBottom:10}}>
                      Upload a JPG or PNG to replace the Projek wordmark. Max 5MB.
                    </p>
                    <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                      <div style={{
                        width:100, height:48, borderRadius:8, background:'hsl(var(--sidebar))',
                        display:'flex', alignItems:'center', justifyContent:'center', padding:6,
                        border:'1px solid hsl(var(--border))'
                      }}>
                        {user?.company_logo_url ? (
                          <img src={`${BACKEND_URL}${user.company_logo_url}`} alt="Workspace logo"
                            style={{maxHeight:'100%',maxWidth:'100%',objectFit:'contain'}} />
                        ) : (
                          <span className="font-manrope" style={{color:'hsl(var(--sidebar-fg))',fontWeight:800,fontSize:14}}>
                            Projek<span className="brand-dot">.</span>
                          </span>
                        )}
                      </div>
                      <label className="btn btn-outline" style={{cursor: uploadingLogo ? 'wait' : 'pointer'}}>
                        <Camera size={14}/> {uploadingLogo ? 'Uploading…' : (user?.company_logo_url ? 'Change' : 'Upload')}
                        <input type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" hidden
                          onChange={handleLogoUpload} disabled={uploadingLogo} />
                      </label>
                      {user?.company_logo_url && (
                        <button type="button" className="btn btn-ghost" onClick={handleLogoRemove} disabled={uploadingLogo}>
                          <X size={14}/> Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <WorkspaceIconSection token={token} user={user} setUser={setUser} />

                  <Input label="Workspace Name" value={form.company}
                    onChange={e => set('company', e.target.value)}
                    data-testid="mobile-company-name-input" />
                  <p className="field-hint" data-testid="mobile-company-name-optional-hint">
                    Optional — leave blank if you'd rather not set a workspace name.
                  </p>

                  {/* Workspace Access Code (mobile) */}
                  <div className="form-field">
                    <label className="form-label">Workspace Access Code</label>
                    <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginBottom:8}}>
                      Set a workspace access code to make your private workspace available to be viewed with that code. Leave blank to keep the workspace open.
                    </p>
                    <div className="address-row">
                      <input type={showCompanyPw ? 'text' : 'password'} className="form-input"
                        value={form.company_password || ''}
                        onChange={e => { set('company_password', e.target.value); setPwTouched(true); }}
                        placeholder="Set access code (optional)…"
                        data-testid="mobile-company-password-input" />
                      <button type="button" className="icon-btn" onClick={() => setShowCompanyPw(s=>!s)}>
                        {showCompanyPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                  </div>

                  {/* Workspace Manager Access Code (mobile) */}
                  <div className="form-field">
                    <label className="form-label">Workspace Manager Access Code</label>
                    <p style={{fontSize:12,color:'hsl(var(--muted-foreground))',marginBottom:8}}>
                      Create a separate code to give someone manager permissions. Anyone who redeems it instantly becomes a manager of this workspace.
                    </p>
                    <div className="address-row">
                      <input type={showMgrPw ? 'text' : 'password'} className="form-input"
                        value={form.manager_access_code || ''}
                        onChange={e => { set('manager_access_code', e.target.value); setMgrPwTouched(true); }}
                        placeholder="Set manager access code (optional)…"
                        data-testid="mobile-manager-access-code-input" />
                      <button type="button" className="icon-btn" onClick={() => setShowMgrPw(s=>!s)}>
                        {showMgrPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {!user?.is_code_session && (<>
                <div className="settings-subsection-divider" data-testid="workspaces-subsection-divider-mobile" />
                <CompaniesTab token={token} user={user} onSwitch={handleAuth} onNavigate={goToPage} />
              </>)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN PAGE
// ============================================================
function AdminPage() {
  const { token, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [savedThemes, setSavedThemes] = useState([]);
  const [globalTheme, setGlobalTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [msg, setMsg] = useState('');
  // Referral admin
  const [referralUsage, setReferralUsage] = useState([]);
  const [referralSearch, setReferralSearch] = useState('');
  const [referralRequests, setReferralRequests] = useState([]);

  // Load data
  useEffect(() => {
    const load = async () => {
      try {
        const [u, gt, st, ru, rr] = await Promise.all([
          apiFetch('/admin/users', {}, token),
          apiFetch('/admin/global-theme', {}, token),
          apiFetch('/admin/saved-themes', {}, token),
          apiFetch('/admin/referrals', {}, token).catch(() => []),
          apiFetch('/admin/referral-requests', {}, token).catch(() => []),
        ]);
        setUsers(u);
        setGlobalTheme(gt);
        setSavedThemes(st);
        setReferralUsage(ru);
        setReferralRequests(rr);
      } catch (e) { setMsg(e.message); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const filteredReferrals = useMemo(() => {
    if (!referralSearch.trim()) return referralUsage;
    const q = referralSearch.toLowerCase();
    return referralUsage.filter(r =>
      r.buyer_username?.toLowerCase().includes(q) ||
      r.buyer_email?.toLowerCase().includes(q) ||
      r.code?.toLowerCase().includes(q) ||
      r.referrer_username?.toLowerCase().includes(q)
    );
  }, [referralUsage, referralSearch]);

  // Create/Edit user
  const handleSaveUser = async (data) => {
    if (editingUser) {
      const updated = await apiFetch(`/admin/users/${editingUser.id}`, {
        method: 'PATCH', body: JSON.stringify(data),
      }, token);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? updated : u));
    } else {
      const created = await apiFetch('/admin/users', {
        method: 'POST', body: JSON.stringify(data),
      }, token);
      setUsers(prev => [...prev, created]);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    await apiFetch(`/admin/users/${deleteTarget.id}`, { method: 'DELETE' }, token);
    setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  // Apply global theme
  const handleApplyGlobalTheme = async (t) => {
    const themeData = { id: t.id, name: t.name, primary: t.primary, primaryFg: t.primaryFg, accent: t.accent, radius: t.radius, preview: t.preview };
    await apiFetch('/admin/global-theme', {
      method: 'PATCH', body: JSON.stringify({ theme: themeData }),
    }, token);
    setGlobalTheme(themeData);
    applyTheme(themeData);
    setTheme(themeData);
    setMsg('Global theme applied!');
    setTimeout(() => setMsg(''), 3000);
  };

  // Clear global theme
  const handleClearGlobalTheme = async () => {
    await apiFetch('/admin/global-theme', { method: 'DELETE' }, token);
    setGlobalTheme(null);
    setMsg('Global theme cleared. Users will see their own themes.');
    setTimeout(() => setMsg(''), 3000);
  };

  // Save custom theme
  const [customTheme, setCustomTheme] = useState({
    name: '', primary: '221 83% 53%', primaryFg: '0 0% 100%', accent: '221 60% 92%', radius: '0.5rem', preview: '#3B82F6',
  });

  const handleSaveCustomTheme = async () => {
    if (!customTheme.name.trim()) return;
    const saved = await apiFetch('/admin/saved-themes', {
      method: 'POST', body: JSON.stringify(customTheme),
    }, token);
    setSavedThemes(prev => [...prev, saved]);
    setCustomTheme({ name: '', primary: '221 83% 53%', primaryFg: '0 0% 100%', accent: '221 60% 92%', radius: '0.5rem', preview: '#3B82F6' });
    setMsg('Theme saved!');
    setTimeout(() => setMsg(''), 3000);
  };

  const handleDeleteSavedTheme = async (id) => {
    await apiFetch(`/admin/saved-themes/${id}`, { method: 'DELETE' }, token);
    setSavedThemes(prev => prev.filter(t => t.id !== id));
  };

  if (loading) return <div className="page-content"><div className="loading-spinner" /></div>;

  return (
    <div className="page-content" data-testid="admin-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope"><Shield size={22} className="inline-icon" /> Admin Panel</h2>
          <p className="page-sub">Manage users and customize the website.</p>
        </div>
      </div>

      {msg && <div className={msg.includes('!') ? 'alert-success mb-4' : 'alert-error mb-4'} data-testid="admin-msg">{msg}</div>}

      {/* Tabs */}
      <div className="settings-tabs" data-testid="admin-tabs">
        {[
          { id: 'users', label: 'Users', icon: Users },
          { id: 'fields', label: 'Default Fields', icon: Grid3X3 },
          { id: 'suggestions', label: 'Inquiries', icon: MessageCircle },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} className={`settings-tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="settings-body">
        {/* ===== USERS TAB ===== */}
        {tab === 'users' && (
          <div className="settings-section" data-testid="admin-users-section">
            <div className="admin-section-header">
              <h3 className="settings-section-title font-manrope">All Users ({users.length})</h3>
              <Btn onClick={() => { setEditingUser(null); setUserModalOpen(true); }} data-testid="admin-create-user-btn">
                <UserPlus size={14} /> Create User
              </Btn>
            </div>
            <div className="admin-users-table-wrap">
              <table className="jobs-table admin-users-table" data-testid="admin-users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Workspace</th>
                    <th>Password</th>
                    <th>Role</th>
                    <th>Paid</th>
                    <th>Invite Code</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <td>
                        <div className="admin-user-cell">
                          <Avatar name={u.display_name || u.username} size={32} color="hsl(var(--primary))" />
                          <div>
                            <div className="job-name">{u.display_name || u.username}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>@{u.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-muted">{u.email}</td>
                      <td>{u.company}</td>
                      <td><BlurredPassword value={u.password_plain} /></td>
                      <td>
                        {u.is_admin
                          ? <Badge variant="danger">Admin</Badge>
                          : <Badge variant="neutral">Manager</Badge>}
                      </td>
                      <td>
                        <select className="admin-tier-select" value={u.is_paid ? 'paid' : 'unpaid'}
                          onChange={async (e) => {
                            try {
                              const updated = await apiFetch(`/admin/users/${u.id}/payment-status`, {
                                method: 'PATCH', body: JSON.stringify({ is_paid: e.target.value === 'paid' }),
                              }, token);
                              setUsers(prev => prev.map(x => x.id === u.id ? updated : x));
                            } catch (err) { setMsg(err.message); }
                          }}
                          data-testid={`admin-paid-status-${u.id}`}>
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                        </select>
                      </td>
                      <td className="text-muted" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {u.invite_code || '—'}
                      </td>
                      <td className="text-muted" style={{ fontSize: 12 }}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div className="row-actions">
                          <button className="icon-btn" onClick={() => { setEditingUser(u); setUserModalOpen(true); }}
                            data-testid={`admin-edit-user-${u.id}`} title="Edit user">
                            <Edit2 size={14} />
                          </button>
                          {u.id !== user.id && (
                            <button className="icon-btn icon-btn-danger" onClick={() => setDeleteTarget(u)}
                              data-testid={`admin-delete-user-${u.id}`} title="Delete user">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ===== GLOBAL THEME TAB ===== */}
        {tab === 'theme' && (
          <div className="settings-section" data-testid="admin-theme-section">
            <h3 className="settings-section-title font-manrope">Global Website Theme</h3>
            <p className="text-muted mb-4">
              Apply a theme that all users will see across the entire website. Individual user themes will be overridden.
            </p>

            {globalTheme && (
              <div className="admin-current-theme" data-testid="admin-current-global-theme">
                <div className="theme-preview-bar" style={{ background: `hsl(${globalTheme.primary})` }} />
                <div className="theme-preview-info">
                  <strong>{globalTheme.name || globalTheme.id}</strong>
                  <span className="text-muted">Currently active global theme</span>
                </div>
                <Btn variant="outline" size="sm" onClick={handleClearGlobalTheme} data-testid="admin-clear-theme-btn">
                  <X size={13} /> Remove
                </Btn>
              </div>
            )}

            <h4 className="admin-sub-title font-manrope">Preset Themes</h4>
            <div className="theme-grid">
              {THEMES.map(t => (
                <button key={t.id}
                  className={`theme-card ${globalTheme?.id === t.id ? 'theme-card-active' : ''}`}
                  onClick={() => handleApplyGlobalTheme(t)}
                  data-testid={`admin-theme-${t.id}`}>
                  <div className="theme-preview" style={{ background: t.preview }}>
                    {globalTheme?.id === t.id && <Check size={20} color="#fff" />}
                  </div>
                  <span className="theme-name">{t.name}</span>
                </button>
              ))}
            </div>

            {/* Saved custom themes */}
            {savedThemes.length > 0 && (
              <>
                <h4 className="admin-sub-title font-manrope" style={{ marginTop: 24 }}>Your Saved Themes</h4>
                <div className="theme-grid">
                  {savedThemes.map(t => (
                    <div key={t.id} className={`theme-card theme-card-custom ${globalTheme?.id === t.id ? 'theme-card-active' : ''}`}
                      data-testid={`admin-saved-theme-${t.id}`}>
                      <button className="theme-card-inner" onClick={() => handleApplyGlobalTheme(t)}>
                        <div className="theme-preview" style={{ background: t.preview || `hsl(${t.primary})` }}>
                          {globalTheme?.id === t.id && <Check size={20} color="#fff" />}
                        </div>
                        <span className="theme-name">{t.name}</span>
                      </button>
                      <button className="theme-delete-btn" onClick={() => handleDeleteSavedTheme(t.id)}
                        data-testid={`admin-delete-theme-${t.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ===== SAVED THEMES (Create) TAB ===== */}
        {tab === 'saved' && (
          <div className="settings-section" data-testid="admin-saved-themes-section">
            <h3 className="settings-section-title font-manrope">Create & Save Custom Themes</h3>
            <p className="text-muted mb-4">Build custom themes and save them. You can then apply them globally from the Global Theme tab.</p>

            <div className="admin-theme-builder" data-testid="admin-theme-builder">
              <Input label="Theme Name *" value={customTheme.name}
                onChange={e => setCustomTheme(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Ocean Breeze" data-testid="custom-theme-name" />

              <div className="grid grid-cols-2 gap-4">
                <div className="form-field">
                  <label className="form-label">Primary Color (HSL)</label>
                  <input className="form-input" value={customTheme.primary}
                    onChange={e => setCustomTheme(prev => ({ ...prev, primary: e.target.value }))}
                    placeholder="221 83% 53%" data-testid="custom-theme-primary" />
                </div>
                <div className="form-field">
                  <label className="form-label">Accent Color (HSL)</label>
                  <input className="form-input" value={customTheme.accent}
                    onChange={e => setCustomTheme(prev => ({ ...prev, accent: e.target.value }))}
                    placeholder="221 60% 92%" data-testid="custom-theme-accent" />
                </div>
                <div className="form-field">
                  <label className="form-label">Primary Foreground (HSL)</label>
                  <input className="form-input" value={customTheme.primaryFg}
                    onChange={e => setCustomTheme(prev => ({ ...prev, primaryFg: e.target.value }))}
                    placeholder="0 0% 100%" data-testid="custom-theme-fg" />
                </div>
                <div className="form-field">
                  <label className="form-label">Border Radius</label>
                  <input className="form-input" value={customTheme.radius}
                    onChange={e => setCustomTheme(prev => ({ ...prev, radius: e.target.value }))}
                    placeholder="0.5rem" data-testid="custom-theme-radius" />
                </div>
                <div className="form-field">
                  <label className="form-label">Preview Color (Hex)</label>
                  <div className="admin-color-input-row">
                    <input type="color"
                      value={customTheme.preview}
                      onChange={e => setCustomTheme(prev => ({ ...prev, preview: e.target.value }))}
                      className="admin-color-picker" data-testid="custom-theme-preview-picker" />
                    <input className="form-input" value={customTheme.preview}
                      onChange={e => setCustomTheme(prev => ({ ...prev, preview: e.target.value }))}
                      placeholder="#3B82F6" data-testid="custom-theme-preview" />
                  </div>
                </div>
              </div>

              {/* Live preview */}
              <div className="admin-theme-live-preview" data-testid="admin-theme-live-preview">
                <div className="theme-preview-bar" style={{ background: `hsl(${customTheme.primary})` }} />
                <div className="theme-preview-info">
                  <strong>{customTheme.name || 'Untitled Theme'}</strong>
                  <span className="text-muted">Live preview</span>
                </div>
                <div className="theme-preview-demo">
                  <span className="btn btn-sm" style={{ background: `hsl(${customTheme.primary})`, color: `hsl(${customTheme.primaryFg})`, borderRadius: customTheme.radius }}>
                    Button
                  </span>
                  <span className="badge" style={{ background: `hsl(${customTheme.accent})`, borderRadius: customTheme.radius }}>
                    Badge
                  </span>
                </div>
              </div>

              <Btn onClick={handleSaveCustomTheme} disabled={!customTheme.name.trim()} className="w-fit"
                data-testid="save-custom-theme-btn">
                <Save size={14} /> Save Theme
              </Btn>
            </div>

            {savedThemes.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4 className="admin-sub-title font-manrope">Saved Themes ({savedThemes.length})</h4>
                <div className="theme-grid">
                  {savedThemes.map(t => (
                    <div key={t.id} className="theme-card theme-card-custom" data-testid={`saved-theme-card-${t.id}`}>
                      <div className="theme-card-inner">
                        <div className="theme-preview" style={{ background: t.preview || `hsl(${t.primary})` }} />
                        <span className="theme-name">{t.name}</span>
                      </div>
                      <button className="theme-delete-btn" onClick={() => handleDeleteSavedTheme(t.id)}
                        data-testid={`delete-saved-theme-${t.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== FAQ MANAGEMENT TAB ===== */}
        {tab === 'faq' && (
          <div className="settings-section" data-testid="admin-faq-section">
            <h3 className="settings-section-title font-manrope">FAQ Management</h3>
            <p className="text-muted mb-4">Manage FAQ items that are visible to all users on the FAQ page.</p>
            <Btn data-testid="admin-go-to-faq" style={{opacity:0.7, pointerEvents:'none'}}>
              <HelpCircle size={14}/> Manage FAQs on the FAQ page
            </Btn>
            <p className="text-muted" style={{marginTop:12, fontSize:12}}>
              Go to the FAQ page (sidebar) to add, edit, and manage FAQ items. Admin controls are integrated there.
            </p>
          </div>
        )}

        {tab === 'fields' && (
          <AdminFieldTemplate token={token} />
        )}

        {tab === 'suggestions' && (
          <AdminSuggestionsTab token={token} />
        )}
        {tab === 'referrals' && (
          <div className="settings-section" data-testid="admin-referrals-section">
            {/* Referral Requests */}
            <div style={{ marginBottom: 28 }}>
              <div className="admin-section-header" style={{ marginBottom: 12 }}>
                <h3 className="font-manrope" style={{ fontSize: 16, fontWeight: 700 }}>Referral Code Requests</h3>
                <span className="text-muted" style={{ fontSize: 13 }}>{referralRequests.filter(r => r.status === 'pending').length} pending</span>
              </div>
              {referralRequests.length === 0 ? (
                <p className="text-muted" style={{ fontSize: 13, padding: '12px 0' }}>No referral code requests yet.</p>
              ) : (
                <div className="admin-referral-table" data-testid="admin-referral-requests-table">
                  <table className="jobs-table admin-users-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Desired Code</th>
                        <th>Requested</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralRequests.map(r => (
                        <tr key={r.id} data-testid={`referral-request-row-${r.id}`}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{r.user_name}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{r.user_email}</div>
                          </td>
                          <td>
                            {r.desired_code
                              ? <code style={{ fontSize: 12, background: 'hsl(var(--muted))', padding: '2px 6px', borderRadius: 4 }}>{r.desired_code}</code>
                              : <span className="text-muted" style={{ fontSize: 12 }}>Auto-generate</span>}
                          </td>
                          <td style={{ fontSize: 12 }}>{r.requested_at ? new Date(r.requested_at).toLocaleDateString() : '—'}</td>
                          <td>
                            <span className={`referral-affiliate-active ${r.status === 'approved' ? 'active' : 'inactive'}`}>
                              {r.status}
                            </span>
                            {r.status === 'approved' && r.assigned_code && (
                              <div style={{ fontSize: 11, marginTop: 2 }}><code>{r.assigned_code}</code></div>
                            )}
                          </td>
                          <td>
                            {r.status === 'pending' && (
                              <Btn size="sm" onClick={async () => {
                                try {
                                  const res = await apiFetch(`/admin/referral-requests/${r.id}/approve`, { method: 'POST' }, token);
                                  setReferralRequests(prev => prev.map(x => x.id === r.id ? { ...x, status: 'approved', assigned_code: res.code } : x));
                                  setMsg(`Approved! Code: ${res.code}`);
                                } catch (e) { setMsg(e.message); }
                              }} data-testid={`approve-referral-${r.id}`}>
                                <Check size={12} /> Approve
                              </Btn>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Referral Usage */}
            <div>
              <div className="admin-section-header" style={{ marginBottom: 12 }}>
                <h3 className="font-manrope" style={{ fontSize: 16, fontWeight: 700 }}>Referral Usage</h3>
                <span className="text-muted" style={{ fontSize: 13 }}>{referralUsage.length} total</span>
              </div>
              {referralUsage.length > 3 && (
                <input className="form-input" placeholder="Search by user, email, code, or referrer..."
                  value={referralSearch} onChange={e => setReferralSearch(e.target.value)}
                  style={{ marginBottom: 16 }} data-testid="admin-referral-search" />
              )}
              {filteredReferrals.length === 0 ? (
                <p className="text-muted" style={{ fontSize: 13, padding: '12px 0' }}>
                  {referralUsage.length === 0 ? 'No referral codes have been used yet.' : 'No matching results.'}
                </p>
              ) : (
                <div className="admin-referral-table" data-testid="admin-referral-table">
                  <table className="jobs-table admin-users-table">
                    <thead>
                      <tr>
                        <th>Buyer</th>
                        <th>Code Used</th>
                        <th>Referrer</th>
                        <th>Tier</th>
                        <th>Date</th>
                        <th>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReferrals.map(r => (
                        <tr key={r.id} data-testid={`admin-referral-row-${r.id}`}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{r.buyer_username}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{r.buyer_email}</div>
                          </td>
                          <td><code style={{ fontSize: 12, background: 'hsl(var(--muted))', padding: '2px 6px', borderRadius: 4 }}>{r.code}</code></td>
                          <td>{r.referrer_username}</td>
                          <td><span className="admin-tier-badge">{r.tier}</span></td>
                          <td style={{ fontSize: 12 }}>
                            {new Date(r.purchase_date).toLocaleDateString()}<br/>
                            <span className="text-muted">{new Date(r.purchase_date).toLocaleTimeString()}</span>
                          </td>
                          <td>
                            <span className={`referral-affiliate-active ${r.is_active ? 'active' : 'inactive'}`}>
                              {r.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* User Create/Edit Modal */}
      <AdminUserModal open={userModalOpen} onClose={() => { setUserModalOpen(false); setEditingUser(null); }}
        onSave={handleSaveUser} user={editingUser} />

      {/* Delete confirmation */}
      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Delete user "${deleteTarget?.display_name || deleteTarget?.username}"? This will also delete all their jobs, team members, and tasks. This cannot be undone.`} />
    </div>
  );
}

// ============================================================
// ADMIN — INVITE CODES PAGE
// ============================================================
function InviteCodesPage() {
  const { token, user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createLeaderId, setCreateLeaderId] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [editLeaderId, setEditLeaderId] = useState('');
  const [editCode, setEditCode] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        apiFetch('/admin/invite-codes', {}, token),
        apiFetch('/admin/team-leaders', {}, token),
      ]);
      setCodes(c); setLeaders(l);
    } catch (e) { setMsg(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!user?.is_admin) return null;

  const handleCreate = async () => {
    if (!createLeaderId) return;
    try {
      await apiFetch('/admin/invite-codes', { method: 'POST', body: JSON.stringify({ team_leader_id: createLeaderId, code: createCode.trim() || undefined }) }, token);
      setCreateOpen(false); setCreateLeaderId(''); setCreateCode('');
      load();
    } catch (e) { setMsg(e.message); }
  };

  const handleEditSave = async () => {
    if (!editTarget || !editCode.trim()) return;
    try {
      await apiFetch(`/admin/invite-codes/${editTarget.id}`, { method: 'PATCH', body: JSON.stringify({ team_leader_id: editLeaderId, code: editCode.trim() }) }, token);
      setEditTarget(null); setEditLeaderId(''); setEditCode('');
      load();
    } catch (e) { setMsg(e.message); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await apiFetch(`/admin/invite-codes/${deleteTarget.id}`, { method: 'DELETE' }, token);
    setDeleteTarget(null);
    load();
  };

  if (loading) return <div className="page-content"><div className="loading-spinner" /></div>;

  return (
    <div className="page-content" data-testid="invite-codes-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope"><Key size={22} className="inline-icon" /> Invite Codes</h2>
          <p className="page-sub">Codes required to create a new Projek account.</p>
        </div>
        <Btn onClick={() => setCreateOpen(true)} data-testid="create-invite-code-btn">
          <Plus size={14} /> Create Code
        </Btn>
      </div>

      {msg && <div className="alert-error mb-4" data-testid="invite-codes-msg">{msg}</div>}

      <div className="admin-users-table-wrap">
        <table className="jobs-table admin-users-table" data-testid="invite-codes-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Team Leader</th>
              <th>Uses</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id} data-testid={`invite-code-row-${c.id}`}>
                <td><span className="job-name" style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>{c.code}</span></td>
                <td>{c.team_leader_name}</td>
                <td><Badge variant="neutral">{c.uses_count}</Badge></td>
                <td className="text-muted" style={{ fontSize: 12 }}>
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                </td>
                <td>
                  <div className="row-actions">
                    <button className="icon-btn" title="Edit code"
                      onClick={() => { setEditTarget(c); setEditLeaderId(c.team_leader_id); setEditCode(c.code); }}
                      data-testid={`edit-invite-code-${c.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button className="icon-btn icon-btn-danger" title="Delete code"
                      onClick={() => setDeleteTarget(c)}
                      data-testid={`delete-invite-code-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {codes.length === 0 && (
              <tr><td colSpan={5} className="text-muted text-center" style={{ padding: 24 }}>No invite codes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create Invite Code" width="max-w-sm">
        <div className="form-field">
          <label className="form-label">Code (optional)</label>
          <Input value={createCode} onChange={e => setCreateCode(e.target.value.toUpperCase())}
            placeholder="Leave blank to auto-generate" data-testid="create-code-input" />
        </div>
        <div className="form-field">
          <label className="form-label">Team Leader</label>
          <select className="form-input" value={createLeaderId} onChange={e => setCreateLeaderId(e.target.value)} data-testid="create-code-leader-select">
            <option value="">Select a team leader…</option>
            {leaders.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2" style={{ marginTop: 16 }}>
          <Btn variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Btn>
          <Btn onClick={handleCreate} disabled={!createLeaderId} data-testid="confirm-create-code-btn">Create</Btn>
        </div>
      </Modal>

      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Edit Invite Code" width="max-w-sm">
        <div className="form-field">
          <label className="form-label">Code</label>
          <Input value={editCode} onChange={e => setEditCode(e.target.value.toUpperCase())}
            placeholder="Invite code" data-testid="edit-code-input" />
        </div>
        <div className="form-field">
          <label className="form-label">Team Leader</label>
          <select className="form-input" value={editLeaderId} onChange={e => setEditLeaderId(e.target.value)} data-testid="edit-code-leader-select">
            {leaders.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2" style={{ marginTop: 16 }}>
          <Btn variant="outline" onClick={() => setEditTarget(null)}>Cancel</Btn>
          <Btn onClick={handleEditSave} disabled={!editCode.trim()} data-testid="confirm-edit-code-btn">Save</Btn>
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="Delete Invite Code"
        message={`Delete code "${deleteTarget?.code}"? Accounts already created with it are not affected.`} />
    </div>
  );
}

// ============================================================
// ADMIN — TEAM LEADERS PAGE
// ============================================================
function TeamLeadersPage() {
  const { token, user } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try { setLeaders(await apiFetch('/admin/team-leaders', {}, token)); }
    catch (e) { setMsg(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!user?.is_admin) return null;

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await apiFetch('/admin/team-leaders', { method: 'POST', body: JSON.stringify({ name: newName.trim() }) }, token);
      setCreateOpen(false); setNewName('');
      load();
    } catch (e) { setMsg(e.message); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await apiFetch(`/admin/team-leaders/${deleteTarget.id}`, { method: 'DELETE' }, token);
    setDeleteTarget(null);
    load();
  };

  if (loading) return <div className="page-content"><div className="loading-spinner" /></div>;

  return (
    <div className="page-content" data-testid="team-leaders-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope"><UserCheck size={22} className="inline-icon" /> Team Leaders</h2>
          <p className="page-sub">Each invite code is assigned to a team leader.</p>
        </div>
        <Btn onClick={() => setCreateOpen(true)} data-testid="create-team-leader-btn">
          <Plus size={14} /> Add Team Leader
        </Btn>
      </div>

      {msg && <div className="alert-error mb-4" data-testid="team-leaders-msg">{msg}</div>}

      <div className="admin-users-table-wrap">
        <table className="jobs-table admin-users-table" data-testid="team-leaders-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Codes</th>
              <th>Total Uses</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {leaders.map(l => (
              <tr key={l.id} data-testid={`team-leader-row-${l.id}`}>
                <td><span className="job-name">{l.name}</span></td>
                <td>{l.codes?.length || 0}</td>
                <td><Badge variant="neutral">{l.total_uses || 0}</Badge></td>
                <td className="text-muted" style={{ fontSize: 12 }}>
                  {l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}
                </td>
                <td>
                  <div className="row-actions">
                    <button className="icon-btn icon-btn-danger" title="Remove team leader"
                      onClick={() => setDeleteTarget(l)}
                      data-testid={`delete-team-leader-${l.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {leaders.length === 0 && (
              <tr><td colSpan={5} className="text-muted text-center" style={{ padding: 24 }}>No team leaders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Add Team Leader" width="max-w-sm">
        <Input label="Name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Jane Smith" data-testid="new-team-leader-name-input" />
        <div className="flex justify-end gap-2" style={{ marginTop: 16 }}>
          <Btn variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Btn>
          <Btn onClick={handleCreate} disabled={!newName.trim()} data-testid="confirm-create-team-leader-btn">Add</Btn>
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="Remove Team Leader"
        message={`Remove "${deleteTarget?.name}" and their ${deleteTarget?.codes?.length || 0} invite code(s)? Accounts already created with those codes are not affected.`} />
    </div>
  );
}


function BlurredPassword({ value }) {
  const [revealed, setRevealed] = useState(false);
  if (!value) return <span className="text-muted" style={{ fontSize: 12 }}>—</span>;
  return (
    <span className={`admin-password-cell${revealed ? ' revealed' : ''}`}
      onClick={() => setRevealed(r => !r)} title={revealed ? 'Click to hide' : 'Click to reveal'}>
      {value}
    </span>
  );
}

function AdminUserModal({ open, onClose, onSave, user: editUser }) {
  const [form, setForm] = useState({ email: '', password: '', username: '', company: '', phone: '', is_admin: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (editUser) {
      setForm({
        email: editUser.email || '', password: '', username: editUser.username || '',
        company: editUser.company || '', phone: editUser.phone || '',
        display_name: editUser.display_name || '', is_admin: editUser.is_admin || false,
      });
    } else {
      setForm({ email: '', password: '', username: '', company: '', phone: '', is_admin: false });
    }
    setErr(''); setShowPw(false);
  }, [editUser, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!editUser && (!form.email.trim() || !form.password.trim() || !form.username.trim() || !form.company.trim())) {
      setErr('Email, password, username, and company are required.'); return;
    }
    if (editUser && !form.email.trim()) {
      setErr('Email is required.'); return;
    }
    setSaving(true); setErr('');
    try {
      if (editUser) {
        const payload = {};
        if (form.email !== editUser.email) payload.email = form.email;
        if (form.display_name !== editUser.display_name) payload.display_name = form.display_name;
        if (form.company !== editUser.company) payload.company = form.company;
        if (form.phone !== editUser.phone) payload.phone = form.phone;
        if (form.is_admin !== editUser.is_admin) payload.is_admin = form.is_admin;
        if (form.password?.trim()) payload.password = form.password;
        await onSave(payload);
      } else {
        await onSave(form);
      }
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editUser ? `Edit User: ${editUser.display_name || editUser.username}` : 'Create New User'} width="max-w-lg">
      {err && <div className="alert-error mb-4" data-testid="admin-user-modal-error">{err}</div>}
      <div className="flex flex-col gap-4">
        {!editUser && (
          <>
            <Input label="Username *" value={form.username} onChange={e => set('username', e.target.value)}
              placeholder="johndoe" data-testid="admin-user-username" />
            <div className="form-field">
              <label className="form-label">Password *</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input className="form-input" type={showPw ? 'text' : 'password'} value={form.password}
                  onChange={e => set('password', e.target.value)} placeholder="Min 6 chars, 1 number"
                  data-testid="admin-user-password" style={{ paddingRight: 36 }} />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))' }}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          </>
        )}
        {editUser && (
          <>
            <Input label="Display Name" value={form.display_name || ''} onChange={e => set('display_name', e.target.value)}
              placeholder="Display name" data-testid="admin-user-displayname" />
            <div className="form-field">
              <label className="form-label">Change Password</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input className="form-input" type={showPw ? 'text' : 'password'} value={form.password || ''}
                  onChange={e => set('password', e.target.value)} placeholder="Leave blank to keep current"
                  data-testid="admin-user-change-password" style={{ paddingRight: 36 }} />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))' }}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          </>
        )}
        <Input label="Email *" type="email" value={form.email} onChange={e => set('email', e.target.value)}
          placeholder="user@company.com" data-testid="admin-user-email" />
        <Input label="Workspace" value={form.company} onChange={e => set('company', e.target.value)}
          placeholder="Workspace Name" data-testid="admin-user-company" />
        <Input label="Phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
          placeholder="+1 (555) 000-0000" data-testid="admin-user-phone" />
        <label className="admin-checkbox-row" data-testid="admin-user-isadmin-toggle">
          <input type="checkbox" checked={form.is_admin} onChange={e => set('is_admin', e.target.checked)} />
          <Shield size={14} /> Grant admin privileges
        </label>
      </div>
      <div className="modal-footer">
        <Btn variant="outline" onClick={onClose}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={saving} data-testid="admin-user-save-btn">
          {saving ? 'Saving…' : (editUser ? 'Save Changes' : 'Create User')}
        </Btn>
      </div>
    </Modal>
  );
}

// ============================================================
// QUICK ADD FLOATING BUTTON
// ============================================================

function QuickTaskModal({ open, onClose, onSave, jobs }) {
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [jobId, setJobId] = useState(jobs[0]?.id || '');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('medium');
  const [attachment, setAttachment] = useState(null); // {url, filename}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload-attachment`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setAttachment({ url: data.url, filename: data.filename });
    } catch (e) { alert(e.message); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!title.trim() || !jobId) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(), project_id: jobId, status: 'todo', priority,
        due_date: dueDate || null, notes,
        attachment_url: attachment?.url || null,
        attachment_filename: attachment?.filename || null,
      });
      setTitle(''); setDueDate(''); setNotes(''); setPriority('medium'); setAttachment(null);
      onClose();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Quick Add Task" width="max-w-md">
      <div className="quick-task-form">
        <Select label="Project" value={jobId} onChange={e => setJobId(e.target.value)}
          options={jobs.map(j => ({ value: j.id, label: j.name }))}
          data-testid="quick-task-job-select" />
        <Input label="Task Name" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Task name..." autoFocus data-testid="quick-task-name" />
        <div className="quick-task-row">
          <div className="form-field" style={{flex:1}}>
            <label className="form-label">Priority</label>
            <select className="form-input" value={priority} onChange={e => setPriority(e.target.value)}
              data-testid="quick-task-priority">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="form-field" style={{flex:1}}>
            <label className="form-label">Due Date</label>
            <input className="form-input" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              data-testid="quick-task-due" />
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes..." data-testid="quick-task-notes" />
        </div>
        <div className="form-field">
          <label className="form-label">Attachment</label>
          {attachment ? (
            <div className="attachment-preview">
              <Paperclip size={13}/> <a href={`${BACKEND_URL}${attachment.url}`} target="_blank" rel="noopener noreferrer" className="attachment-filename">{attachment.filename}</a>
              <button className="icon-btn-sm" onClick={() => setAttachment(null)}><X size={11}/></button>
            </div>
          ) : (
            <label className="attachment-upload-btn">
              <Paperclip size={13}/> <span>{uploading ? 'Uploading...' : 'Attach File'}</span>
              <input type="file" className="sr-only" onChange={e => handleFileUpload(e.target.files?.[0])} disabled={uploading} data-testid="quick-task-attachment" />
            </label>
          )}
        </div>
      </div>
      <div className="modal-footer">
        <Btn variant="outline" onClick={onClose}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={saving || !title.trim()} data-testid="quick-task-save">
          {saving ? 'Adding...' : 'Add Task'}
        </Btn>
      </div>
    </Modal>
  );
}

// ============================================================
// APP SHELL
// ============================================================
function AppShell({ user, token, onLogout, onAuth }) {
  const [page, setPage] = useState('dashboard');
  const [moreNavKey, setMoreNavKey] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [expandedPunchListJobId, setExpandedPunchListJobId] = useState(null);
  const [focusJobId, setFocusJobId] = useState(null); // {id, ts} — bumps ProjectsPage to clear filters + scroll target to top

  const isGuestAccount = user?.email?.endsWith('@projek.local');
  const [guestSaveOpen, setGuestSaveOpen] = useState(false);

  // Invitation system state
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showInvitesPanel, setShowInvitesPanel] = useState(false);
  const [inviteProcessing, setInviteProcessing] = useState(null);
  const [showSuggestionPanel, setShowSuggestionPanel] = useState(false);

  // Load pending invites
  const loadPendingInvites = useCallback(async () => {
    if (isGuestAccount) return;
    try {
      const invites = await apiFetch('/invites/pending', {}, token);
      setPendingInvites(invites);
    } catch (_) {}
  }, [token, isGuestAccount]);

  useEffect(() => { loadPendingInvites(); }, [loadPendingInvites]);
  // Poll every 30s for new invites
  useEffect(() => {
    if (isGuestAccount) return;
    const iv = setInterval(loadPendingInvites, 30000);
    return () => clearInterval(iv);
  }, [loadPendingInvites, isGuestAccount]);

  const respondToInvite = useCallback(async (inviteId, action) => {
    setInviteProcessing(inviteId);
    try {
      if (action === 'accept') {
        await apiFetch(`/invites/${inviteId}/accept`, { method: 'POST' }, token);
      } else {
        await apiFetch(`/invites/${inviteId}/deny`, { method: 'POST' }, token);
      }
      await loadPendingInvites();
    } catch (e) {
      alert(e.message);
    } finally { setInviteProcessing(null); }
  }, [token, loadPendingInvites]);

  const handleExitGuest = async () => {
    if (user?.id) {
      try { await apiFetch(`/guests/${user.id}`, { method: 'DELETE' }); } catch (_) {}
    }
    onLogout();
  };

  const handleGuestUpgrade = async ({ username, email, password, inviteCode }) => {
    const data = await apiFetch('/auth/upgrade-guest', {
      method: 'POST',
      body: JSON.stringify({ username, email, password, invite_code: inviteCode }),
    }, token);
    setGuestSaveOpen(false);
    onAuth(data.token, data.user);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [j, m, t] = await Promise.all([
          apiFetch('/projects', {}, token),
          apiFetch('/members', {}, token),
          apiFetch('/tasks', {}, token),
        ]);
        setJobs(j); setMembers(m); setTasks(t);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  useEffect(() => { registerLinkNames(jobs); registerLinkNames(members); }, [jobs, members]);


  const refreshJobs = useCallback(async () => {
    try {
      const j = await apiFetch('/projects', {}, token);
      setJobs(j);
    } catch (e) { /* ignore */ }
  }, [token]);

  const addJob = useCallback(async (data) => {
    const j = await apiFetch('/projects', { method: 'POST', body: JSON.stringify(data) }, token);
    setJobs(prev => [...prev, j]);
    return j;
  }, [token]);

  const editJob = useCallback(async (id, data) => {
    const j = await apiFetch(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, token);
    setJobs(prev => prev.map(x => x.id === id ? j : x));
    return j;
  }, [token]);

  const archiveJob = useCallback(async (id) => {
    await apiFetch(`/projects/${id}/archive`, { method: 'PATCH' }, token);
    setJobs(prev => prev.filter(x => x.id !== id));
  }, [token]);

  const refreshMembers = useCallback(async () => {
    try {
      const m = await apiFetch('/members', {}, token);
      setMembers(m);
    } catch (e) { /* ignore */ }
  }, [token]);

  const addMember = useCallback(async (data) => {
    const m = await apiFetch('/members', { method: 'POST', body: JSON.stringify(data) }, token);
    setMembers(prev => [...prev, m]);
    return m;
  }, [token]);

  const editMember = useCallback(async (id, data) => {
    const m = await apiFetch(`/members/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, token);
    setMembers(prev => prev.map(x => x.id === id ? m : x));
    return m;
  }, [token]);

  const archiveMember = useCallback(async (id) => {
    await apiFetch(`/members/${id}/archive`, { method: 'PATCH' }, token);
    setMembers(prev => prev.filter(x => x.id !== id));
  }, [token]);

  const addTask = useCallback(async (data) => {
    const t = await apiFetch('/tasks', { method: 'POST', body: JSON.stringify(data) }, token);
    setTasks(prev => [...prev, t]);
    return t;
  }, [token]);

  const editTask = useCallback(async (id, data) => {
    const t = await apiFetch(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, token);
    setTasks(prev => prev.map(x => x.id === id ? t : x));
    return t;
  }, [token]);

  const removeTask = useCallback(async (id) => {
    await apiFetch(`/tasks/${id}`, { method: 'DELETE' }, token);
    setTasks(prev => prev.filter(x => x.id !== id));
  }, [token]);

  const goToTeamMember = useCallback((memberName) => {
    setTeamSearchQuery(memberName);
    setPage('team');
  }, []);

  const goToClient = useCallback((clientName) => {
    setClientSearchQuery(clientName);
    setPage('clients');
  }, []);

  const goToProjectSearch = useCallback((jobName) => {
    setProjectSearchQuery(jobName);
    setPage('projects');
  }, []);

  const goToJobPunchList = useCallback((jobId) => {
    setExpandedPunchListJobId(jobId);
    setPage('projects');
    // Signal ProjectsPage to clear active search/filter and scroll the target to the TOP
    setFocusJobId({ id: jobId, ts: Date.now() });
  }, []);

  const dataValue = { jobs, members, tasks, addJob, editJob, archiveJob, refreshJobs, refreshMembers, addMember, editMember, archiveMember, addTask, editTask, removeTask, goToTeamMember, teamSearchQuery, setTeamSearchQuery, goToClient, clientSearchQuery, setClientSearchQuery, goToJobPunchList, goToProjectSearch, projectSearchQuery, setProjectSearchQuery, expandedPunchListJobId, setExpandedPunchListJobId, focusJobId, setFocusJobId, isReadOnly: false, canChangeTaskStatus: true, goToPage: setPage, pendingInvites, respondToInvite, loadPendingInvites };

  const PAGE_MAP = { dashboard: Dashboard, projects: ProjectsPage, schedule: SchedulePage, team: TeamPage, clients: ClientsPage, settings: SettingsPage, admin: AdminPage, archive: ArchivePage, 'invite-codes': InviteCodesPage, 'team-leaders': TeamLeadersPage };
  const CurrentPage = PAGE_MAP[page] || Dashboard;

  if (loading) return (
    <div className="loading-screen" data-testid="loading-screen">
      <div className="loading-spinner" />
      <span>Loading your workspace…</span>
    </div>
  );

  return (
    <DataCtx.Provider value={dataValue}>
      <div className="app-shell" data-testid="app-shell" data-session-mode={isGuestAccount ? 'guest' : 'signed-in'}>
        {isGuestAccount && (
          <GuestSessionBanner onSave={() => setGuestSaveOpen(true)} onExit={handleExitGuest} />
        )}
        {isGuestAccount && guestSaveOpen && (
          <GuestSaveModal onSave={handleGuestUpgrade} onClose={() => setGuestSaveOpen(false)} />
        )}
        <div className="app-body">
        <Sidebar page={page} setPage={setPage} user={user} onLogout={isGuestAccount ? handleExitGuest : onLogout}
          collapsed={collapsed} setCollapsed={setCollapsed} />
        <main className={`main-area ${collapsed ? 'main-collapsed' : ''}`}>
          {/* Notification bell removed - moved to Settings */}
          {page === 'settings' ? <SettingsPage key={moreNavKey} guestSessionBanner={isGuestAccount ? <GuestSessionBanner profile onSave={() => setGuestSaveOpen(true)} onExit={handleExitGuest} /> : null} /> : <CurrentPage />}
        </main>
        </div>
        {/* Mobile bottom nav */}
        <div className="mobile-nav" data-testid="mobile-nav">
          <button className={`mobile-nav-item ${page === 'dashboard' ? 'active' : ''}`}
            onClick={() => setPage('dashboard')} data-testid="mobile-nav-dashboard">
            <Home size={20}/><span>Home</span>
          </button>
          <button className={`mobile-nav-item ${page === 'projects' ? 'active' : ''}`}
            onClick={() => setPage('projects')} data-testid="mobile-nav-projects">
            <Briefcase size={20}/><span>Projects</span>
          </button>
          <button className={`mobile-nav-item ${page === 'schedule' ? 'active' : ''}`}
            onClick={() => setPage('schedule')} data-testid="mobile-nav-schedule">
            <Calendar size={20}/><span>Schedule</span>
          </button>
          <button className={`mobile-nav-item ${page === 'team' ? 'active' : ''}`}
            onClick={() => setPage('team')} data-testid="mobile-nav-team">
            <Users size={20}/><span>Team</span>
          </button>
          {user?.is_admin && (
            <button className={`mobile-nav-item ${page === 'admin' ? 'active' : ''}`}
              onClick={() => setPage('admin')} data-testid="mobile-nav-admin">
              <Shield size={20}/><span>Admin</span>
            </button>
          )}
          <button className={`mobile-nav-item ${page === 'settings' ? 'active' : ''}`}
            onClick={() => { setPage('settings'); setMoreNavKey(k => k + 1); }} data-testid="mobile-nav-settings">
            <Menu size={20}/><span>More</span>
          </button>
        </div>
      </div>
    </DataCtx.Provider>
  );
}

// ============================================================
// ARCHIVE PAGE
// ============================================================
function ArchivePage() {
  const { token, user } = useAuth();
  const { refreshJobs, members } = useData();
  const [tab, setTab] = useState('projects');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const loadSequence = useRef(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // item to permanently delete
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const sel = useMultiSelect();
  const [projectFieldSchema, setProjectFieldSchema] = useState([]);
  useEffect(() => {
    if (!token) return;
    apiFetch('/field-templates', {}, token).then(data => setProjectFieldSchema(data.fields || [])).catch(() => {});
  }, [token]);

  const TABS = [
    { id: 'projects',   label: 'Projects' },
    { id: 'members',    label: 'Team Members' },
    ...(['owner', 'manager'].includes(user?.workspace_role) ? [{ id: 'clients', label: 'Clients' }] : []),
    { id: 'templates',  label: 'My Templates' },
    { id: 'workspaces', label: 'Workspaces' },
  ];

  const loadItems = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setLoadError('');
    setItems([]);
    try {
      let endpoint = tab === 'workspaces' ? '/profile/companies/trash' : `/archive/${tab}`;
      const data = await apiFetch(endpoint, {}, token);
      if (sequence === loadSequence.current) setItems(data || []);
    } catch (error) {
      if (sequence === loadSequence.current) setLoadError(error.message);
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [tab, token]);

  useEffect(() => {
    loadItems();
    return () => { loadSequence.current += 1; };
  }, [loadItems]);

  const restore = async (item) => {
    try {
      if (tab === 'workspaces') {
        await apiFetch(`/profile/companies/${item.id}/restore`, { method: 'POST' }, token);
      } else if (tab === 'projects') {
        await apiFetch(`/projects/${item.id}/restore`, { method: 'PATCH' }, token);
        if (refreshJobs) await refreshJobs();
      } else if (tab === 'members' || tab === 'clients') {
        await apiFetch(`/${tab}/${item.id}/restore`, { method: 'PATCH' }, token);
      } else if (tab === 'templates') {
        await apiFetch(`/user-templates/${item.id}/restore`, { method: 'PATCH' }, token);
      }
      setItems(prev => prev.filter(x => x.id !== item.id));
    } catch (e) { console.error('Restore failed', e); }
  };

  const deleteItemApi = async (item) => {
    if (tab === 'workspaces') {
      await apiFetch(`/profile/companies/${item.id}/permanent`, { method: 'DELETE' }, token);
    } else if (tab === 'projects') {
      await apiFetch(`/projects/${item.id}/permanent`, { method: 'DELETE' }, token);
    } else if (tab === 'members' || tab === 'clients') {
      await apiFetch(`/${tab}/${item.id}/permanent`, { method: 'DELETE' }, token);
    } else if (tab === 'templates') {
      await apiFetch(`/user-templates/${item.id}/permanent`, { method: 'DELETE' }, token);
    }
  };

  const permanentDelete = async (item) => {
    try {
      await deleteItemApi(item);
      setItems(prev => prev.filter(x => x.id !== item.id));
      setDeleteConfirm(null);
    } catch (e) { console.error('Permanent delete failed', e); alert('Delete failed: ' + e.message); }
  };

  const bulkDelete = async () => {
    sel.setBusy(true);
    const ids = [...sel.selected];
    try {
      for (const id of ids) {
        const item = items.find(x => x.id === id);
        if (item) await deleteItemApi(item);
      }
      setItems(prev => prev.filter(x => !sel.selected.has(x.id)));
    } catch (e) { console.error('Bulk delete failed', e); alert('Delete failed: ' + e.message); }
    finally { setBulkDeleteConfirm(false); sel.exit(); }
  };

  const filtered = useMemo(() => items.filter(item => {
    if (!search) return true;
    const name = (item.name || item.company || item.display_name || item.username || '').toLowerCase();
    return name.includes(search.toLowerCase());
  }), [items, search]);

  const getItemName = (item) =>
    item.name || item.company || item.display_name || item.username || 'Unnamed';

  // Format a date for display
  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  // Rich card for each archive type - uses same card components as normal menus
  const ItemCard = ({ item, isGrid }) => {
    const name = getItemName(item);

    if (tab === 'projects') {
      const archiveActions = (
        <div className="job-card-actions" style={{ opacity: 1 }}>
          <button className="icon-btn" onClick={() => restore(item)} data-testid={`restore-${item.id}`} title="Restore">
            <RotateCw size={14} />
          </button>
          <button className="icon-btn icon-btn-danger" onClick={() => setDeleteConfirm(item)}
            data-testid={`perma-delete-${item.id}`} title="Delete permanently">
            <Trash2 size={14} />
          </button>
        </div>
      );
      if (!isGrid) {
        return (
          <div className="job-list-row-item" data-testid={`archive-item-${item.id}`}>
            <div className="job-list-color-bar" style={{ background: item.color || 'hsl(var(--primary))' }} />
            <div className="job-list-main">
              <div className="job-list-title">{name}</div>
              {item.client && <div className="job-list-sub">{item.client}</div>}
            </div>
            <div className="job-list-meta">
              {item.install_start && <span>{fmtDate(item.install_start)}</span>}
            </div>
            <div className="job-list-actions" style={{ opacity: 1 }}>
              <button className="icon-btn" onClick={() => restore(item)} data-testid={`restore-${item.id}`} title="Restore"><RotateCw size={14} /></button>
              <button className="icon-btn icon-btn-danger" onClick={() => setDeleteConfirm(item)} data-testid={`perma-delete-${item.id}`} title="Delete permanently"><Trash2 size={14} /></button>
            </div>
          </div>
        );
      }
      const assignedMembers = members.filter(m => (item.member_ids || []).includes(m.id));
      const mapsUrl = item.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}` : null;
      const cardFields = getOrderedJobFields(item, projectFieldSchema);
      const showTeam = (!item.field_visibility || item.field_visibility.member_ids !== false) && assignedMembers.length > 0;
      return (
        <div className="job-card" data-testid={`archive-item-${item.id}`}>
          <div className="job-card-top" style={{ borderLeftColor: item.color || 'hsl(var(--primary))' }}>
            <div className="job-card-title-row">
              <div className="job-card-title-info">
                <span className="job-card-name font-manrope">{name}</span>
              </div>
              {archiveActions}
            </div>
          </div>
          <div className="job-card-details">
            {cardFields.map(f => (
              <div key={f.id} className={`job-card-field${f.id === 'site_instructions' || f.id === 'description' ? ' job-card-field-full' : ''}`}>
                <span className="job-card-field-label">{f.label}</span>
                <span className={`job-card-field-value${f.id === 'site_instructions' || f.id === 'description' ? ' job-card-field-truncate' : ''}`}>
                  {f.isAddress && mapsUrl ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="job-card-link">{f.value}</a> : f.value}
                </span>
              </div>
            ))}
          </div>
          {showTeam && (
            <div className="job-card-team">
              <span className="job-card-field-label"><Users size={11} /> Team</span>
              <div className="job-card-team-list">
                {assignedMembers.map(m => (
                  <div key={m.id} className="job-card-member-chip">
                    <Avatar name={m.name} photo={m.photo_url} color={m.color} size={22} />
                    <span>{m.name.split(' ')[0]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (tab === 'members' || tab === 'clients') {
      if (!isGrid) {
        return (
          <div className="job-list-row-item" data-testid={`archive-item-${item.id}`}>
            <Avatar name={name} photo={item.photo_url} color={item.color || '#10B981'} size={36} />
            <div className="job-list-main">
              <div className="job-list-title">{name}</div>
              <div className="job-list-sub">{item.role || ''}{item.email ? (item.role ? ' · ' : '') + item.email : ''}</div>
            </div>
            <div className="job-list-actions" style={{ opacity: 1 }}>
              <button className="icon-btn" onClick={() => restore(item)} data-testid={`restore-${item.id}`} title="Restore"><RotateCw size={14} /></button>
              <button className="icon-btn icon-btn-danger" onClick={() => setDeleteConfirm(item)} data-testid={`perma-delete-${item.id}`} title="Delete permanently"><Trash2 size={14} /></button>
            </div>
          </div>
        );
      }
      return (
        <div className="member-card" data-testid={`archive-item-${item.id}`}>
          <div className="member-card-header">
            <Avatar name={name} photo={item.photo_url} color={item.color || '#10B981'} size={48} />
            <div className="row-actions">
              <button className="icon-btn" onClick={() => restore(item)} data-testid={`restore-${item.id}`} title="Restore"><RotateCw size={14} /></button>
              <button className="icon-btn icon-btn-danger" onClick={() => setDeleteConfirm(item)} data-testid={`perma-delete-${item.id}`} title="Delete permanently"><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="member-name font-manrope">{name}</div>
          {item.role && <div className="member-role">{item.role}</div>}
          <div className="member-contacts">
            {item.email && <div className="member-contact"><Mail size={12}/> {item.email}</div>}
            {item.phone && <div className="member-contact"><Phone size={12}/> {item.phone}</div>}
          </div>
        </div>
      );
    }

    if (tab === 'templates') {
      const fields = (item.fields || []).filter(f => !f.system);
      return (
        <div className={isGrid ? 'archive-full-card' : 'archive-full-list-item'}
          data-testid={`archive-item-${item.id}`}>
          <div className="archive-template-icon"><FileText size={22} style={{ color: 'hsl(var(--primary))' }} /></div>
          <div className="archive-item-body">
            <div className="archive-item-top">
              <span className="archive-item-name">{name}</span>
              <span className="archive-meta-pill">{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
            </div>
            {fields.length > 0 && (
              <div className="archive-item-meta">
                {fields.slice(0, 5).map((f) => (
                  <span key={f.id || f.label} className="archive-meta-row">
                    <Hash size={11} /> {f.label} <span style={{ opacity: 0.5, fontSize: 10 }}>({f.type})</span>
                  </span>
                ))}
                {fields.length > 5 && <span className="archive-meta-row" style={{ opacity: 0.5 }}>+{fields.length - 5} more</span>}
              </div>
            )}
          </div>
          <div className={`archive-item-actions ${isGrid ? 'archive-item-actions-grid' : ''}`}>
            <button className="archive-restore-btn" onClick={() => restore(item)} data-testid={`restore-${item.id}`}><RotateCw size={13} /> Restore</button>
            <button className="archive-delete-btn" onClick={() => setDeleteConfirm(item)} data-testid={`perma-delete-${item.id}`} title="Permanently delete"><Trash2 size={13} /> Delete</button>
          </div>
        </div>
      );
    }

    // Workspaces
    return (
      <div className={isGrid ? 'archive-full-card' : 'archive-full-list-item'}
        data-testid={`archive-item-${item.id}`}>
        <div className="archive-template-icon"><Building size={22} style={{ color: 'hsl(var(--muted-foreground))' }} /></div>
        <div className="archive-item-body">
          <div className="archive-item-top">
            <span className="archive-item-name">{name}</span>
          </div>
          <div className="archive-item-meta">
            {item.username && item.username !== name && <span className="archive-meta-row"><User size={11} /> {item.username}</span>}
            {item.company_code && <span className="archive-meta-row"><Key size={11} /> Code: {item.company_code}</span>}
            {item.trashed_at && <span className="archive-meta-row">Archived {fmtDate(item.trashed_at)}</span>}
          </div>
        </div>
        <div className={`archive-item-actions ${isGrid ? 'archive-item-actions-grid' : ''}`}>
          <button className="archive-restore-btn" onClick={() => restore(item)} data-testid={`restore-${item.id}`}><RotateCw size={13} /> Restore</button>
          <button className="archive-delete-btn" onClick={() => setDeleteConfirm(item)} data-testid={`perma-delete-${item.id}`} title="Permanently delete"><Trash2 size={13} /> Delete</button>
        </div>
      </div>
    );
  };

  return (
    <div className="page-content" data-testid="archive-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope">Archive</h2>
          <p className="page-sub">Restore archived items or permanently remove them</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="archive-tab-bar">
        {TABS.map(t => (
          <button key={t.id} className={`archive-tab-btn ${tab === t.id ? 'active' : ''}`}
            onClick={() => { setSearch(''); setTab(t.id); sel.exit(); }} data-testid={`archive-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="toolbar" style={{ marginBottom: 16 }}>
        <div className="search-box" style={{ flex: 1, maxWidth: 360 }}>
          <Search size={15} className="search-icon" />
          <input className="search-input" placeholder={`Search archived ${tab}…`}
            value={search} onChange={e => setSearch(e.target.value)} data-testid="archive-search" />
          {search && (
            <button className="search-clear-btn" onClick={() => setSearch('')} data-testid="archive-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="view-toggle">
          <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')} data-testid="archive-list-view">
            <List size={14} /> <span>List</span>
          </button>
          <button className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
            onClick={() => setViewMode('card')} data-testid="archive-card-view">
            <LayoutGrid size={14} /> <span>Grid</span>
          </button>
        </div>
        {filtered.length > 0 && (
          <button className={`view-btn ${sel.selectMode ? 'active' : ''}`} style={{ marginLeft: 8 }}
            onClick={() => sel.selectMode ? sel.exit() : sel.setSelectMode(true)}
            data-testid="archive-select-toggle" title="Select to delete">
            <CheckSquare size={14} /> <span>Select</span>
          </button>
        )}
      </div>

      {sel.selectMode && (
        <MultiSelectBar testid="archive-select-bar" danger
          count={sel.selected.size} total={filtered.length}
          onSelectAll={() => sel.selectAll(filtered.map(i => i.id))}
          onClear={sel.clear} onCancel={sel.exit}
          onAction={() => sel.selected.size && setBulkDeleteConfirm(true)}
          actionLabel="Delete selected" ActionIcon={Trash2} busy={sel.busy} />
      )}

      {/* Items */}
      {loading ? (
        <div className="archive-empty" role="status" data-testid="archive-loading"><div className="spinner" />Loading archive…</div>
      ) : loadError ? (
        <div className="alert-error" role="alert" data-testid="archive-load-error">{loadError}
          <Btn onClick={loadItems} data-testid="archive-retry-btn">Retry</Btn>
        </div>
      ) : filtered.length === 0 ? (
        <div className="archive-empty" data-testid="archive-empty">
          <Archive size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>No archived {tab} yet</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className={['projects', 'members', 'clients'].includes(tab) ? 'job-list-table' : 'archive-full-list'} data-testid="archive-list">
          {filtered.map(item => (
            sel.selectMode ? (
              <div key={item.id} className={`archive-row-select ${sel.selected.has(item.id) ? 'selected' : ''}`}>
                <SelectCheck id={item.id} selected={sel.selected.has(item.id)} onToggle={sel.toggle} testid={`archive-select-${item.id}`} />
                <div className="archive-row-select-item"><ItemCard item={item} isGrid={false} /></div>
              </div>
            ) : <ItemCard key={item.id} item={item} isGrid={false} />
          ))}
        </div>
      ) : (
        <div className="archive-full-grid" data-testid="archive-card-grid">
          {filtered.map(item => (
            sel.selectMode ? (
              <div key={item.id} className={`archive-grid-select ${sel.selected.has(item.id) ? 'selected' : ''}`}>
                <div className="archive-grid-select-check">
                  <SelectCheck id={item.id} selected={sel.selected.has(item.id)} onToggle={sel.toggle} testid={`archive-select-${item.id}`} />
                </div>
                <ItemCard item={item} isGrid={true} />
              </div>
            ) : <ItemCard key={item.id} item={item} isGrid={true} />
          ))}
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal open onClose={() => setDeleteConfirm(null)} title="Permanently Delete?" width="max-w-sm">
          <div style={{ padding: '4px 0 12px' }}>
            <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
              This will <strong>permanently delete</strong> <em>"{getItemName(deleteConfirm)}"</em> and cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="cancel-perma-delete">Cancel</Btn>
              <Btn onClick={() => permanentDelete(deleteConfirm)}
                style={{ background: '#EF4444', borderColor: '#EF4444', color: '#fff' }}
                data-testid="confirm-perma-delete">
                Delete Permanently
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Permanent Delete Confirmation */}
      {bulkDeleteConfirm && (
        <Modal open onClose={() => setBulkDeleteConfirm(false)} title="Permanently Delete?" width="max-w-sm">
          <div style={{ padding: '4px 0 12px' }}>
            <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
              This will <strong>permanently delete</strong> <em>{sel.selected.size} selected item{sel.selected.size !== 1 ? 's' : ''}</em> and cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="outline" onClick={() => setBulkDeleteConfirm(false)} data-testid="cancel-bulk-delete">Cancel</Btn>
              <Btn onClick={bulkDelete} disabled={sel.busy}
                style={{ background: '#EF4444', borderColor: '#EF4444', color: '#fff' }}
                data-testid="confirm-bulk-delete">
                {sel.busy ? 'Deleting…' : 'Delete Permanently'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// SUBSCRIPTION PAGE
// ============================================================
// ============================================================
// ESSENTIAL CONTACT MODAL
// ============================================================
function FAQPage() {
  const { token, user } = useAuth();
  const isAdmin = user?.is_admin;
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [voted, setVoted] = useState({});

  useEffect(() => {
    apiFetch('/faq').then(setFaqs).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const vote = async (id, direction) => {
    if (voted[id]) return;
    try {
      const updated = await apiFetch(`/faq/${id}/vote`, { method: 'POST', body: JSON.stringify({ direction }) });
      setFaqs(prev => prev.map(f => f.id === id ? updated : f));
      setVoted(v => ({ ...v, [id]: direction }));
    } catch (e) { /* silent */ }
  };

  const saveFaq = async (data) => {
    if (editModal?.id) {
      const updated = await apiFetch(`/admin/faq/${editModal.id}`, { method: 'PATCH', body: JSON.stringify(data) }, token);
      setFaqs(prev => prev.map(f => f.id === editModal.id ? updated : f));
    } else {
      const created = await apiFetch('/admin/faq', { method: 'POST', body: JSON.stringify(data) }, token);
      setFaqs(prev => [...prev, created]);
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ item?')) return;
    await apiFetch(`/admin/faq/${id}`, { method: 'DELETE' }, token);
    setFaqs(prev => prev.filter(f => f.id !== id));
  };

  const [search, setSearch] = useState('');
  const categories = [...new Set(faqs.map(f => f.category || 'General'))];
  const filteredFaqs = search.trim()
    ? faqs.filter(f =>
        f.question.toLowerCase().includes(search.toLowerCase()) ||
        (f.answer || '').toLowerCase().includes(search.toLowerCase())
      )
    : faqs;
  const filteredCategories = search.trim()
    ? [...new Set(filteredFaqs.map(f => f.category || 'General'))]
    : categories;

  if (loading) return <div className="page-content"><div className="loading-spinner" /></div>;

  return (
    <div className="page-content" data-testid="faq-page">
      <div className="page-header">
        <div>
          <h2 className="page-title font-manrope">
            <HelpCircle size={22} className="inline-icon" /> FAQ
          </h2>
          <p className="page-sub">Frequently asked questions</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="search-box" style={{ minWidth: 220 }}>
            <Search size={14} />
            <input type="text" className="search-input" placeholder="Search FAQs…" value={search}
              onChange={e => { setSearch(e.target.value); setExpandedId(null); }}
              data-testid="faq-search-input" />
            {search && (
              <button className="search-clear-btn" onClick={() => { setSearch(''); setExpandedId(null); }} data-testid="faq-search-clear">
                <X size={14} />
              </button>
            )}
          </div>
          {isAdmin && (
            <Btn onClick={() => setEditModal({})} data-testid="add-faq-btn">
              <Plus size={16} /> Add FAQ
            </Btn>
          )}
        </div>
      </div>

      {filteredFaqs.length === 0 ? (
        <EmptyState icon={HelpCircle} title={search ? "No results found" : "No FAQs yet"}
          desc={search ? `No FAQs match "${search}". Try a different keyword.` : isAdmin ? "Add your first FAQ to help visitors." : "Check back later for helpful information."}
          action={!search && isAdmin && <Btn onClick={() => setEditModal({})} className="mt-4"><Plus size={14}/> Add FAQ</Btn>} />
      ) : (
        <div className="faq-list">
          {filteredCategories.map(cat => {
            const catFaqs = filteredFaqs.filter(f => (f.category || 'General') === cat);
            if (catFaqs.length === 0) return null;
            return (
              <div key={cat} className="faq-category">
                <h3 className="faq-category-title font-manrope">{cat}</h3>
                {catFaqs.map(faq => (
                  <div key={faq.id} className={`faq-item ${expandedId === faq.id ? 'faq-item-expanded' : ''}`}
                    data-testid={`faq-item-${faq.id}`}>
                    <button className="faq-question" onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                      data-testid={`faq-toggle-${faq.id}`}>
                      <span>{faq.question}</span>
                      <ChevronDown size={16} className={`faq-chevron ${expandedId === faq.id ? 'faq-chevron-open' : ''}`} />
                    </button>
                    {expandedId === faq.id && (
                      <div className="faq-answer" data-testid={`faq-answer-${faq.id}`}>
                        <div className="faq-answer-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }} />
                        <div className="faq-actions">
                          <div className="faq-votes">
                            <button className={`faq-vote-btn ${voted[faq.id] === 'up' ? 'faq-voted' : ''}`}
                              onClick={() => vote(faq.id, 'up')} data-testid={`faq-upvote-${faq.id}`}
                              disabled={!!voted[faq.id]}>
                              <ThumbsUp size={14} /> {faq.upvotes || 0}
                            </button>
                            <button className={`faq-vote-btn ${voted[faq.id] === 'down' ? 'faq-voted' : ''}`}
                              onClick={() => vote(faq.id, 'down')} data-testid={`faq-downvote-${faq.id}`}
                              disabled={!!voted[faq.id]}>
                              <ThumbsDown size={14} /> {faq.downvotes || 0}
                            </button>
                          </div>
                          {isAdmin && (
                            <div className="faq-admin-actions">
                              <button className="icon-btn" onClick={() => setEditModal(faq)}
                                data-testid={`faq-edit-${faq.id}`}><Edit2 size={14}/></button>
                              <button className="icon-btn icon-btn-danger" onClick={() => deleteFaq(faq.id)}
                                data-testid={`faq-delete-${faq.id}`}><Trash2 size={14}/></button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {editModal && (
        <FAQEditModal open onClose={() => setEditModal(null)}
          onSave={saveFaq} faq={editModal.id ? editModal : null} />
      )}
    </div>
  );
}

function FAQEditModal({ open, onClose, onSave, faq }) {
  const [form, setForm] = useState({
    question: faq?.question || '', answer: faq?.answer || '',
    category: faq?.category || 'General', order: faq?.order || 0,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) { setErr('Question and answer are required'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={faq ? 'Edit FAQ' : 'Add FAQ'} width="max-w-lg">
      {err && <div className="alert-error mb-4">{err}</div>}
      <div className="flex flex-col gap-4">
        <Input label="Question *" value={form.question} onChange={e => set('question', e.target.value)}
          placeholder="How do I...?" data-testid="faq-question-input" />
        <Textarea label="Answer *" value={form.answer} onChange={e => set('answer', e.target.value)}
          rows={4} placeholder="The answer..." data-testid="faq-answer-input" />
        <Input label="Category" value={form.category} onChange={e => set('category', e.target.value)}
          placeholder="General" data-testid="faq-category-input" />
        <Input label="Order" type="number" value={form.order} onChange={e => set('order', parseInt(e.target.value) || 0)}
          data-testid="faq-order-input" />
      </div>
      <div className="modal-footer">
        <Btn variant="outline" onClick={onClose}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={saving} data-testid="save-faq-btn">
          {saving ? 'Saving...' : (faq ? 'Update' : 'Create')}
        </Btn>
      </div>
    </Modal>
  );
}

// ============================================================
// SUGGESTION SUBMISSION (in settings/profile)
// ============================================================
function SuggestionBox({ token, compact }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSending(true); setMsg('');
    try {
      let attachment_url = null;
      let attachment_type = null;
      if (file) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${BACKEND_URL}/api/suggestions/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Upload failed'); }
        const data = await res.json();
        attachment_url = data.url;
        attachment_type = data.type;
        setUploading(false);
      }
      await apiFetch('/suggestions', {
        method: 'POST',
        body: JSON.stringify({ text: text.trim(), attachment_url, attachment_type }),
      }, token);
      setText(''); setFile(null);
      setMsg('Suggestion submitted! Thank you for your feedback.');
    } catch (e) { setMsg(e.message); setUploading(false); }
    finally { setSending(false); }
  };

  return (
    <div className={compact ? "suggestion-compact" : "settings-section"} data-testid="suggestion-box">
      {!compact && (
        <>
          <h3 className="settings-section-title font-manrope">
            <MessageCircle size={16} className="inline-icon" /> Submit a Suggestion
          </h3>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            Have an idea to improve Projek<span className="brand-dot">.</span>? Let the admin team know.
          </p>
        </>
      )}
      {compact && (
        <p className="text-muted" style={{ marginBottom: 12, fontSize: 13 }}>
          Have an idea to improve Projek<span className="brand-dot">.</span>?
        </p>
      )}
      {msg && <div className={msg.includes('Thank') ? 'alert-success mb-4' : 'alert-error mb-4'} data-testid="suggestion-msg">{msg}</div>}
      <Textarea
        value={text} onChange={e => setText(e.target.value)}
        placeholder="Describe your idea or suggestion..."
        rows={4} data-testid="suggestion-text"
      />
      <div className="suggestion-attach-row">
        <input type="file" ref={fileRef} accept="image/*,video/*"
          onChange={e => setFile(e.target.files?.[0] || null)} className="sr-only"
          data-testid="suggestion-file" />
        <button type="button" className="suggestion-file-btn" onClick={() => fileRef.current?.click()}
          data-testid="suggestion-file-trigger">
          <Paperclip size={14} /> Attach File
        </button>
        {file && (
          <span className="suggestion-file-name">
            {file.name} <button className="icon-btn-sm" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}><X size={12}/></button>
          </span>
        )}
      </div>
      <Btn onClick={handleSubmit} disabled={sending || !text.trim()} data-testid="submit-suggestion-btn"
        style={{ marginTop: 12 }}>
        {uploading ? 'Uploading...' : sending ? 'Sending...' : 'Submit Suggestion'}
      </Btn>
    </div>
  );
}

// ============================================================
// ADMIN DEFAULT FIELD TEMPLATE CONFIGURATOR
// ============================================================
function AdminFieldTemplate({ token }) {
  const TEMPLATE_KINDS = [
    { id: 'project', label: 'Projects', noun: 'project', base: DEFAULT_FIELDS },
    { id: 'member', label: 'Team Members', noun: 'team member', base: DEFAULT_MEMBER_FIELDS },
    { id: 'client', label: 'Clients', noun: 'client', base: DEFAULT_CLIENT_FIELDS },
  ];
  const [kind, setKind] = useState('project');
  const activeKind = TEMPLATE_KINDS.find(k => k.id === kind);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // Editor state
  const [editId, setEditId] = useState(null); // null=list, 'new'=create, id=editing
  const [editName, setEditName] = useState('');
  const [editFields, setEditFields] = useState([]);
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState('');

  useEffect(() => {
    setLoading(true);
    setEditId(null);
    apiFetch(`/admin/templates?kind=${kind}`, {}, token)
      .then(setTemplates).catch(() => setTemplates([])).finally(() => setLoading(false));
  }, [token, kind]);

  const startCreate = () => {
    setEditId('new'); setEditName(''); setEditFields([...activeKind.base]); setMsg('');
  };

  const startEdit = (tmpl) => {
    setEditId(tmpl.id);
    setEditName(tmpl.name);
    setEditFields([...tmpl.fields].sort((a, b) => a.order - b.order));
    setMsg('');
  };

  const cancelEdit = () => { setEditId(null); setMsg(''); };

  const saveTemplate = async () => {
    if (!editName.trim()) { setMsg('Template name is required.'); return; }
    setSaving(true); setMsg('');
    try {
      if (editId === 'new') {
        const created = await apiFetch('/admin/templates', {
          method: 'POST', body: JSON.stringify({ name: editName.trim(), fields: editFields, is_default: false, kind }),
        }, token);
        setTemplates(prev => [...prev, created]);
      } else {
        const updated = await apiFetch(`/admin/templates/${editId}`, {
          method: 'PUT', body: JSON.stringify({ name: editName.trim(), fields: editFields, is_default: false, kind }),
        }, token);
        setTemplates(prev => prev.map(t => t.id === editId ? updated : t));
      }
      setEditId(null);
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await apiFetch(`/admin/templates/${id}`, { method: 'DELETE' }, token);
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const setDefault = async (id) => {
    try {
      await apiFetch(`/admin/templates/${id}/set-default`, { method: 'POST' }, token);
      setTemplates(prev => prev.map(t => ({ ...t, is_default: t.id === id })));
      setMsg('Default template updated!');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg(e.message); }
  };

  // Editor field ops
  const moveEditField = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= editFields.length) return;
    const updated = [...editFields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((f, i) => (f.order = i));
    setEditFields(updated);
  };

  const toggleEditField = (idx) => {
    if (editFields[idx].id === 'name') return;
    const updated = [...editFields];
    updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
    setEditFields(updated);
  };

  const addEditField = () => {
    if (!newFieldLabel.trim()) return;
    const id = `custom_${Date.now()}`;
    const opts = newFieldType === 'multioption' ? newFieldOptions.split(',').map(o => o.trim()).filter(Boolean) : undefined;
    setEditFields(prev => [...prev, { id, label: newFieldLabel.trim(), type: newFieldType, required: false, system: false, enabled: true, order: prev.length, options: opts }]);
    setNewFieldLabel(''); setNewFieldOptions('');
  };

  const removeEditField = (idx) => {
    if (editFields[idx].id === 'name') return;
    const updated = editFields.filter((_, i) => i !== idx);
    updated.forEach((f, i) => (f.order = i));
    setEditFields(updated);
  };

  const [editRenamingId, setEditRenamingId] = useState(null);
  const [editRenameValue, setEditRenameValue] = useState('');
  const startEditRename = (fd) => { setEditRenamingId(fd.id); setEditRenameValue(fd.label); };
  const applyEditRename = (idx) => {
    if (editRenameValue.trim()) {
      const updated = [...editFields];
      updated[idx] = { ...updated[idx], label: editRenameValue.trim() };
      setEditFields(updated);
    }
    setEditRenamingId(null);
  };

  if (loading) return <div className="loading-spinner" />;

  // ── EDITOR VIEW ──────────────────────────────────────────
  if (editId !== null) {
    const regFields = editFields.filter(f => !f.isColor && !f.isMembers);
    const optFields = editFields.filter(f => f.isColor || f.isMembers);
    return (
      <div className="admin-template-editor" data-testid="admin-template-editor">
        <div className="admin-template-editor-header">
          <button className="icon-btn" onClick={cancelEdit} data-testid="cancel-edit-template"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <ChevronLeft size={15} /> Back
          </button>
          <input className="form-input admin-template-name-input"
            placeholder="Template name..." value={editName}
            onChange={e => setEditName(e.target.value)}
            data-testid="template-name-input" />
          <Btn onClick={saveTemplate} disabled={saving || !editName.trim()} data-testid="save-template-btn">
            {saving ? 'Saving...' : <><Save size={14} /> Save Template</>}
          </Btn>
        </div>

        {msg && <div className={msg.includes('!') ? 'alert-success mb-4' : 'alert-error mb-4'}>{msg}</div>}

        <div className="job-form-grid">
          {regFields.map(fd => {
            const idx = editFields.indexOf(fd);
            const rIdx = regFields.indexOf(fd);
            const isFull = fd.id === 'name' || fd.type === 'location' || fd.type === 'attachment' ||
              fd.id === 'description' || fd.id === 'site_instructions';
            return (
              <div key={fd.id}
                className={`field-edit-wrapper${isFull ? ' job-field-full' : ''} ${!fd.enabled ? 'field-edit-disabled' : ''}`}
                data-testid={`admin-field-${fd.id}`}>
                <div className="field-edit-bar">
                  <button className="field-arrow-btn" onClick={() => moveEditField(idx, -1)}
                    disabled={rIdx === 0}>
                    <ChevronLeft size={12} style={{ transform: 'rotate(90deg)' }} />
                  </button>
                  <button className="field-arrow-btn" onClick={() => moveEditField(idx, 1)}
                    disabled={rIdx === regFields.length - 1}>
                    <ChevronLeft size={12} style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                  {fd.id !== 'name' && (
                    <button className={`field-toggle-btn ${fd.enabled ? 'field-toggle-on' : ''}`}
                      onClick={() => toggleEditField(idx)}>
                      {fd.enabled ? <Eye size={12} /> : <EyeOff size={12} />}
                    </button>
                  )}
                  <button className="field-arrow-btn" onClick={() => startEditRename(fd)} title="Rename field">
                    <Edit2 size={12} />
                  </button>
                  {fd.id !== 'name' && (
                    <button className="icon-btn icon-btn-danger" style={{ padding: '2px' }}
                      onClick={() => removeEditField(idx)}>
                      <X size={12} />
                    </button>
                  )}
                </div>
                {editRenamingId === fd.id && (
                  <div className="field-rename-bar">
                    <input className="field-rename-input" autoFocus value={editRenameValue}
                      onChange={e => setEditRenameValue(e.target.value)}
                      onBlur={() => applyEditRename(idx)}
                      onKeyDown={e => { if (e.key === 'Enter') applyEditRename(idx); if (e.key === 'Escape') setEditRenamingId(null); }} />
                  </div>
                )}
                <div className="field-edit-content">
                  <FieldRenderer field={fd}
                    value={fd.type === 'multioption' && fd.system ? (fd.options?.[0]?.toLowerCase() || '') : ''}
                    onChange={() => {}} form={{}} set={() => {}} members={[]} mapsUrl={null} token={token} />
                </div>
              </div>
            );
          })}
        </div>

        {optFields.length > 0 && (
          <div className="field-options-section">
            <h4 className="field-options-title">Project Options</h4>
            <div className="field-options-grid">
              {optFields.map(fd => {
                const idx = editFields.indexOf(fd);
                return (
                  <div key={fd.id}
                    className={`field-option-card${!fd.enabled ? ' field-edit-disabled' : ''}`}
                    data-testid={`admin-field-${fd.id}`}>
                    <div className="field-option-card-header">
                      <span className="field-option-label">{fd.label}</span>
                      <button className={`field-toggle-btn ${fd.enabled ? 'field-toggle-on' : ''}`}
                        onClick={() => toggleEditField(idx)}>
                        {fd.enabled ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                    </div>
                    {fd.isMembers ? (
                      <div className="field-option-placeholder">
                        <Users size={13} /> Member picker (visible in live forms)
                      </div>
                    ) : (
                      <div className="field-edit-content">
                        <FieldRenderer field={fd} value="#10B981" onChange={() => {}}
                          form={{}} set={() => {}} members={[]} mapsUrl={null} token={token} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="field-add-section" data-testid="admin-add-field-section">
          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add Custom Field</h4>
          <div className="field-add-row">
            <input className="form-input" placeholder="Field name..." value={newFieldLabel}
              onChange={e => setNewFieldLabel(e.target.value)} />
            <select className="form-input field-type-select" value={newFieldType}
              onChange={e => setNewFieldType(e.target.value)}>
              {Object.entries(FIELD_TYPES).filter(([k]) => kind === 'project' || k !== 'color').map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <Btn size="sm" onClick={addEditField} disabled={!newFieldLabel.trim()}>
              <Plus size={14} /> Add
            </Btn>
          </div>
          {newFieldType === 'multioption' && (
            <input className="form-input mt-2" placeholder="Options (comma separated)"
              value={newFieldOptions} onChange={e => setNewFieldOptions(e.target.value)} />
          )}
        </div>
      </div>
    );
  }

  // ── TEMPLATE LIST VIEW ───────────────────────────────────
  return (
    <div className="settings-section admin-templates-section" data-testid="admin-field-template">
      <div className="admin-section-header">
        <div>
          <h3 className="settings-section-title font-manrope">Global Form Templates</h3>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            Create {activeKind.noun} form templates. Set one as the default for all new workspaces.
          </p>
        </div>
        <Btn onClick={startCreate} data-testid="create-template-btn">
          <Plus size={14} /> New Template
        </Btn>
      </div>

      <div className="admin-template-kind-tabs" data-testid="admin-template-kind-tabs">
        {TEMPLATE_KINDS.map(k => (
          <button key={k.id} type="button"
            className={`admin-template-kind-tab${kind === k.id ? ' active' : ''}`}
            onClick={() => setKind(k.id)} data-testid={`template-kind-${k.id}`}>
            {k.label}
          </button>
        ))}
      </div>

      {msg && <div className={msg.includes('!') ? 'alert-success mb-4' : 'alert-error mb-4'} style={{ marginTop: 12 }}>{msg}</div>}

      {templates.length === 0 ? (
        <EmptyState icon={Grid3X3} title="No templates yet"
          desc={`Create a template to define the default field layout for new ${activeKind.noun}s.`} />
      ) : (
        <div className="admin-template-list">
          {templates.map(t => (
            <div key={t.id} className={`admin-template-row${t.is_default ? ' is-default' : ''}`}
              data-testid={`template-row-${t.id}`}>
              <div style={{ flex: 1 }}>
                <div className="admin-template-name">{t.name}</div>
                <div className="admin-template-meta">{t.fields?.length || 0} fields</div>
              </div>
              {t.is_default && (
                <Badge variant="success" data-testid={`template-default-badge-${t.id}`}>Active Default</Badge>
              )}
              <div className="admin-template-actions">
                {!t.is_default && (
                  <Btn size="sm" variant="outline" onClick={() => setDefault(t.id)}
                    data-testid={`set-default-${t.id}`}>
                    Set as Default
                  </Btn>
                )}
                <Btn size="sm" variant="outline" onClick={() => startEdit(t)}
                  data-testid={`edit-template-${t.id}`}>
                  <Edit2 size={13} /> Edit
                </Btn>
                <button className="icon-btn icon-btn-danger" onClick={() => deleteTemplate(t.id)}
                  data-testid={`delete-template-${t.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADMIN SUGGESTIONS TAB
// ============================================================
function AdminSuggestionsTab({ token }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/admin/suggestions', {}, token)
      .then(setSuggestions).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      const updated = await apiFetch(`/admin/suggestions/${id}?status=${status}`, { method: 'PATCH' }, token);
      setSuggestions(prev => prev.map(s => s.id === id ? updated : s));
    } catch (e) { /* silent */ }
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await apiFetch(`/admin/suggestions/${id}`, { method: 'DELETE' }, token);
      setSuggestions(prev => prev.filter(s => s.id !== id));
    } catch (e) { /* silent */ }
  };

  if (loading) return <div className="loading-spinner" />;

  return (
    <div className="settings-section" data-testid="admin-suggestions">
      <h3 className="settings-section-title font-manrope">User Inquiries</h3>
      <p className="text-muted mb-4">Review feedback and ideas submitted by your users.</p>
      {suggestions.length === 0 ? (
        <EmptyState icon={MessageCircle} title="No inquiries yet" desc="User inquiries will appear here." />
      ) : (
        <div className="suggestions-list">
          {suggestions.map(s => (
            <div key={s.id} className={`suggestion-card suggestion-status-${s.status}`}
              data-testid={`suggestion-${s.id}`}>
              <div className="suggestion-card-header">
                <div className="suggestion-user-info">
                  <Avatar name={s.user_name} size={28} color="hsl(var(--primary))" />
                  <div>
                    <span className="suggestion-user-name">{s.user_name}</span>
                    <span className="suggestion-user-email">{s.user_email}</span>
                  </div>
                </div>
                <div className="suggestion-meta">
                  <Badge variant={s.status === 'new' ? 'info' : s.status === 'reviewed' ? 'success' : 'neutral'}>
                    {s.status}
                  </Badge>
                  <span className="suggestion-date">{fmtDate(s.created_at?.split('T')[0])}</span>
                </div>
              </div>
              <p className="suggestion-text">{s.text}</p>
              {s.attachment_url && (
                <div className="suggestion-attachment" data-testid={`suggestion-attachment-${s.id}`}>
                  {s.attachment_type === 'image' ? (
                    <img src={`${BACKEND_URL}${s.attachment_url}`} alt="Attachment" className="suggestion-img" />
                  ) : (
                    <video src={`${BACKEND_URL}${s.attachment_url}`} controls className="suggestion-video" />
                  )}
                </div>
              )}
              <div className="suggestion-actions">
                {s.status === 'new' && (
                  <Btn size="sm" variant="outline" onClick={() => updateStatus(s.id, 'reviewed')}
                    data-testid={`mark-reviewed-${s.id}`}>
                    <Check size={13}/> Mark Reviewed
                  </Btn>
                )}
                {s.status === 'reviewed' && (
                  <Btn size="sm" variant="outline" onClick={() => updateStatus(s.id, 'implemented')}
                    data-testid={`mark-implemented-${s.id}`}>
                    <CheckCircle size={13}/> Implemented
                  </Btn>
                )}
                <button className="icon-btn icon-btn-danger" onClick={() => deleteSuggestion(s.id)}
                  data-testid={`delete-suggestion-${s.id}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// PUBLIC JOB PAGE (?job=jobId)
// ============================================================
function PublicJobPage({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(`/public/projects/${jobId}`)
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [jobId]);

  // Auto-trigger print when ?print=1 is in the URL
  useEffect(() => {
    if (!data) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('print') === '1') {
      const timer = setTimeout(() => window.print(), 600);
      return () => clearTimeout(timer);
    }
  }, [data]);

  if (loading) return <div className="loading-screen"><div className="loading-spinner" /></div>;
  if (error) return <div className="public-page-error"><h2>Job not found</h2><p>{error}</p><a href="/">Back to Projek</a></div>;

  const { job, company, members, tasks } = data;
  const todo = tasks.filter(t => t.status === 'todo');
  const inProg = tasks.filter(t => t.status === 'in-progress');
  const done = tasks.filter(t => t.status === 'done');

  const STATUS_MAP = { new: 'New', active: 'Active', completed: 'Completed', cancelled: 'Cancelled' };
  const PRIORITY_MAP = { high: 'High', medium: 'Medium', low: 'Low' };

  return (
    <div className="public-page" data-testid="public-job-page">
      <div className="public-page-header">
        <a href="/" className="public-page-brand font-manrope">Projek<span className="brand-dot">.</span></a>
        <button className="public-print-btn" onClick={() => window.print()} data-testid="print-job-btn">
          <Printer size={16}/> Print / Save PDF
        </button>
      </div>
      <div className="public-page-content">
        <div className="public-job-header">
          <div className="public-job-color-bar" style={{ background: job.color || 'hsl(var(--primary))' }} />
          <div className="public-job-title-block">
            <div className="public-job-meta-row">
              <span className="public-company-name">{company}</span>
              {job.status && <span className={`badge badge-${job.status}`}>{STATUS_MAP[job.status] || job.status}</span>}
              {job.priority && <span className={`badge badge-priority-${job.priority}`}>{PRIORITY_MAP[job.priority] || job.priority}</span>}
            </div>
            <h1 className="font-manrope public-job-name">{job.name}</h1>
            {job.client && <p className="public-job-client">Client: <strong>{job.client}</strong></p>}
          </div>
        </div>

        <div className="public-job-details-grid">
          {job.install_start && (
            <div className="public-detail-row"><Calendar size={14}/><span><strong>Start:</strong> {fmtDate(job.install_start)}</span></div>
          )}
          {job.install_end && (
            <div className="public-detail-row"><Calendar size={14}/><span><strong>End:</strong> {fmtDate(job.install_end)}</span></div>
          )}
          {job.delivery_date && (
            <div className="public-detail-row"><Calendar size={14}/><span><strong>Delivery:</strong> {fmtDate(job.delivery_date)}</span></div>
          )}
          {job.address && (
            <div className="public-detail-row"><MapPin size={14}/><span><strong>Address:</strong> {job.address}</span></div>
          )}
          {job.site_instructions && (
            <div className="public-detail-full"><strong>Site Instructions:</strong><p>{job.site_instructions}</p></div>
          )}
          {job.description && (
            <div className="public-detail-full"><strong>Description:</strong><p>{job.description}</p></div>
          )}
        </div>

        {members.length > 0 && (
          <div className="public-section">
            <h3 className="font-manrope public-section-title">Assigned Team</h3>
            <div className="public-team-row">
              {members.map(m => (
                <div key={m.id} className="public-team-chip">
                  <Avatar name={m.name} photo={m.photo_url} color={m.color} size={32} />
                  <div>
                    <div className="public-team-name">{m.name}</div>
                    {m.role && <div className="public-team-role">{m.role}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tasks.length > 0 && (
          <div className="public-section">
            <h3 className="font-manrope public-section-title">To Do List ({tasks.length} tasks)</h3>
            <div className="public-punch-grid">
              {[['To Do', todo], ['In Progress', inProg], ['Completed', done]].map(([label, list]) => list.length > 0 && (
                <div key={label} className="public-punch-col">
                  <div className="public-punch-col-header">{label} ({list.length})</div>
                  {list.map(t => (
                    <div key={t.id} className="public-punch-item">
                      <span className="public-punch-title">{t.title}</span>
                      {t.due_date && <span className="public-punch-due">{fmtDate(t.due_date)}</span>}
                      {t.notes && <p className="public-punch-notes">{t.notes}</p>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="public-page-footer">
        <p>Powered by <a href="/" style={{color:'hsl(var(--primary))',fontWeight:700}}>Projek<span className="brand-dot">.</span></a></p>
      </div>
    </div>
  );
}

// ============================================================
// PUBLIC MEMBER PAGE (?member=memberId)
// ============================================================
function PublicMemberPage({ memberId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(`/public/members/${memberId}`)
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [memberId]);

  if (loading) return <div className="loading-screen"><div className="loading-spinner" /></div>;
  if (error) return <div className="public-page-error"><h2>Member not found</h2><p>{error}</p><a href="/">Back to Projek</a></div>;

  const { member, company, job_count } = data;
  return (
    <div className="public-page" data-testid="public-member-page">
      <div className="public-page-header">
        <a href="/" className="public-page-brand font-manrope">Projek<span className="brand-dot">.</span></a>
        <button className="public-print-btn" onClick={() => window.print()} data-testid="print-member-btn">
          <Printer size={16}/> Print / Save PDF
        </button>
      </div>
      <div className="public-page-content">
        <div className="public-member-profile">
          <Avatar name={member.name} photo={member.photo_url} color={member.color} size={80} />
          <div className="public-member-info">
            <h1 className="font-manrope public-member-name">{member.name}</h1>
            {member.role && <p className="public-member-role">{member.role}</p>}
            <p className="public-member-company">{company}</p>
            <div className="public-member-stats">
              <div className="public-stat"><span className="font-manrope" style={{fontSize:24,fontWeight:700}}>{job_count}</span>Jobs</div>
            </div>
          </div>
        </div>
        {member.phone && (
          <div className="public-detail-row"><Phone size={14}/><span>{member.phone}</span></div>
        )}
        {member.email && (
          <div className="public-detail-row"><Mail size={14}/><span>{member.email}</span></div>
        )}
      </div>
      <div className="public-page-footer">
        <p>Powered by <a href="/" style={{color:'hsl(var(--primary))',fontWeight:700}}>Projek<span className="brand-dot">.</span></a></p>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem('hm_token'));
  const [user, setUser] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [globalThemeData, setGlobalThemeData] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('hm_darkmode');
    if (saved !== null) return saved === 'true';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });
  const userRef = useRef(null);
  const googleAuthProcessed = useRef(false);

  // Keep a ref to user for beforeunload (can't use state in event handler reliably)
  useEffect(() => { userRef.current = user; }, [user]);

  // Swap browser tab/PWA icon to the workspace's custom app icon (if set)
  useEffect(() => { applyFavicon(user?.workspace_icon_url); }, [user?.workspace_icon_url]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('hm_darkmode', String(next));
      applyDarkMode(theme, next);
      return next;
    });
  }, [theme]);

  // Apply initial theme with saved dark mode preference (or system preference)
  useEffect(() => {
    const saved = localStorage.getItem('hm_darkmode');
    const savedDark = saved !== null ? saved === 'true' : (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
    applyTheme(DEFAULT_THEME);
    applyDarkMode(DEFAULT_THEME, savedDark);
  }, []);

  // Auto-delete guest on tab/window close
  useEffect(() => {
    const handler = () => {
      const u = userRef.current;
      if (u?.email?.endsWith('@projek.local')) {
        fetch(`${BACKEND_URL}/api/guests/${u.id}`, { method: 'DELETE', keepalive: true });
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Check for public URLs + Stripe return
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const companyParam = useMemo(() => urlParams.get('company'), [urlParams]);
  const jobParam = useMemo(() => urlParams.get('job'), [urlParams]);
  const memberParam = useMemo(() => urlParams.get('member'), [urlParams]);
  const clientShareParam = useMemo(() => urlParams.get('client-share'), [urlParams]);
  const stripeSessionId = useMemo(() => urlParams.get('session_id'), [urlParams]);

  // Global theme data (admin reference only, does not affect landing/user theme)
  useEffect(() => {
    apiFetch('/admin/global-theme')
      .then(gt => { if (gt) setGlobalThemeData(gt); })
      .catch(() => {});
  }, []);

  // Handle Google OAuth callback — detect session_id in URL hash
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    if (googleAuthProcessed.current) return;
    const hash = window.location.hash;
    if (!hash || !hash.includes('session_id=')) return;
    googleAuthProcessed.current = true;
    const sessionId = hash.split('session_id=')[1]?.split('&')[0];
    if (!sessionId) return;
    // Clear hash immediately
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    (async () => {
      const pendingCode = sessionStorage.getItem('hm_pending_invite_code');
      try {
        const data = await apiFetch('/auth/google', {
          method: 'POST',
          body: JSON.stringify({ session_id: sessionId, invite_code: pendingCode || undefined }),
        });
        sessionStorage.removeItem('hm_pending_invite_code');
        handleAuth(data.token, data.user);
      } catch (e) {
        sessionStorage.removeItem('hm_pending_invite_code');
        console.error('Google auth failed:', e.message);
        setAppLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  // Verify token on load
  useEffect(() => {
    // Skip if Google OAuth callback is being processed
    if (window.location.hash?.includes('session_id=')) return;
    if (!token) { setAppLoading(false); return; }
    apiFetch('/auth/me', {}, token)
      .then(u => {
        setUser(u);
        const savedDark = localStorage.getItem('hm_darkmode') === 'true';
        if (u.is_admin) {
          const savedTheme = u.theme ? THEMES.find(t => t.id === u.theme.id) || DEFAULT_THEME : DEFAULT_THEME;
          setTheme(savedTheme);
          applyTheme(savedTheme);
          applyDarkMode(savedTheme, savedDark);
          setDarkMode(savedDark);
        } else {
          setTheme(DEFAULT_THEME);
          applyTheme(DEFAULT_THEME);
          applyDarkMode(DEFAULT_THEME, savedDark);
          setDarkMode(savedDark);
        }
      })
      .catch(() => { localStorage.removeItem('hm_token'); setToken(null); })
      .finally(() => setAppLoading(false));
  }, [token]);    // eslint-disable-line

  const handleAuth = (newToken, newUser) => {
    localStorage.setItem('hm_token', newToken);
    setToken(newToken);
    setUser(newUser);
    const savedDark = localStorage.getItem('hm_darkmode') === 'true';
    if (newUser?.is_admin) {
      const savedTheme = newUser.theme ? THEMES.find(t => t.id === newUser.theme.id) || DEFAULT_THEME : DEFAULT_THEME;
      setTheme(savedTheme);
      applyTheme(savedTheme);
      applyDarkMode(savedTheme, savedDark);
      setDarkMode(savedDark);
    } else {
      setTheme(DEFAULT_THEME);
      applyTheme(DEFAULT_THEME);
      applyDarkMode(DEFAULT_THEME, savedDark);
      setDarkMode(savedDark);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hm_token');
    setToken(null); setUser(null);
    setTheme(DEFAULT_THEME);
    applyTheme(DEFAULT_THEME);
    // Keep dark mode preference - user said keep it until they switch back
    applyDarkMode(DEFAULT_THEME, darkMode);
  };

  if (appLoading) return (
    <div className="loading-screen" data-testid="app-loading">
      <div className="loading-spinner" />
    </div>
  );

  // Public company profile view (no auth needed)
  if (companyParam) return <CompanyProfilePage username={companyParam} />;
  if (jobParam) return <PublicJobPage jobId={jobParam} />;
  if (memberParam) return <PublicMemberPage memberId={memberParam} />;
  if (clientShareParam) return <PublicClientPage shareToken={clientShareParam} apiFetch={apiFetch} />;

  return (
    <AuthCtx.Provider value={{ token, user, setUser, handleAuth }}>
      <ThemeCtx.Provider value={{ theme, setTheme, darkMode, toggleDarkMode }}>
        <div className="App" data-testid="app-root">
          {!token || !user
            ? <LandingPage onAuth={handleAuth} />
            : <AppShell user={user} token={token} onLogout={handleLogout} onAuth={handleAuth} />
          }
        </div>
      </ThemeCtx.Provider>
    </AuthCtx.Provider>
  );
}
